import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"
import { showSheetPreview, hideSheetPreview } from "../lib/sheet_preview"
import { nearestVertex, nearestSegment, withVertexInserted, withVertexRemoved, backspacePoints, centroid, segmentLabels, dashFor } from "../lib/markup_geometry"
import { isFirefox } from "../lib/browser"
import { issueTagChip } from "../lib/issue_tag"

export default class extends Controller {
  static targets = ["overlay", "viewerContainer", "loading", "filename", "versionBadge", "saveStatus", "bluebeamBtn", "compareBtn", "overlayBtn", "markupPeoplePanel"]
  static values = { licenseKey: String, baseUrl: String, userName: String, scrollZoom: { type: Boolean, default: true }, scrollZoomContext: { type: String, default: "design" }, pdfTheme: { type: String, default: "light" }, leftPanel: { type: Boolean, default: true }, zoomLevel: { type: Number, default: 0 }, annotateSingular: String, annotatePlural: String }

  connect() {
    this._instance = null
    this._currentAttachmentId = null
    this._dirty = false
    this._saveTimeout = null
    this._saving = false
    this._documentReady = false
    // "Open last preview" (Larry 2026-08-06) — the Drawings/Specs toolbar
    // button lives outside this controller's element, so it reaches us by
    // event instead of a direct action.
    this._openLastPreviewHandler = () => this.openLastPreview()
    window.addEventListener("conslog:open-last-preview", this._openLastPreviewHandler)
    // Per-user zoom preference; updated live as the user zooms. Values
    // outside the sane reading range (25%–400%) are ignored — they come
    // from fit-to-page on huge sheets, not a real preference.
    const z = this.zoomLevelValue
    this._lastSavedZoom = (z >= 0.25 && z <= 4) ? z : null

    // Hook Apryse's OWN per-pane close (the ✕ in each split pane's header,
    // aria-label "Close Document") so clicking it brings the OTHER pane to the
    // full page — close the left pane → refresh into the right sheet; close the
    // right pane → back to the original. Capture phase + composedPath so we
    // catch the click inside Apryse's shadow DOM and pre-empt its native close.
    this._onNativePaneClose = this._onNativePaneClose.bind(this)
    document.addEventListener("click", this._onNativePaneClose, true)
  }

  _onNativePaneClose(e) {
    if (!this._splitActive || !this.hasViewerContainerTarget) return
    const path = e.composedPath ? e.composedPath() : []
    // Only act on OUR viewer's panes.
    if (!path.includes(this.viewerContainerTarget)) return
    const btn = path.find(el => el && el.getAttribute && el.getAttribute("aria-label") === "Close Document")
    if (!btn) return
    e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation()
    const r = btn.getBoundingClientRect()
    const box = this.viewerContainerTarget.getBoundingClientRect()
    const isLeftPane = r.left < (box.left + box.width / 2)
    this._collapseSplit(isLeftPane ? "right" : "left")
  }

  async open({ attachmentId, punchlistId, filename, dataUrl, saveUrl, readOnly = false, commentOnly = false, version = null, versionCurrent = true, versionTag = null, versionTagTitle = "", commentEl = null, onDraftUpdate = null, compareEnabled = false, compareFileId = null, initialPage = null, searchTerm = null, bluebeamEnabled = null, related = null, splitListUrl = null, referencesUrl = null, neighbors = null, replaceTab = false }) {
    this._installScaleCrashTrap()
    this._ribbonWatchdog()
    // Prev/next sheets' dataUrls (host-provided) — prefetched after this
    // sheet loads so arrowing left/right swaps near-instantly (Phelipe
    // 2026-08-01). Only the PDF bytes are cached; annotations (xfdf) are
    // always re-fetched fresh on open.
    this._neighborUrls = Array.isArray(neighbors) ? neighbors.filter(Boolean) : []
    // In-viewer compare entry point (Drawings). When enabled, a "Compare"
    // button shows in the header; clicking it asks the host page to open the
    // sheet-picker (compare to previous / pick another sheet).
    this._compareFileId = compareFileId
    this._overlayMode = false
    if (this.hasCompareBtnTarget) {
      // Use an inline style rather than the `hidden` class: the button also
      // carries `inline-flex`, and that utility wins over `hidden` in the
      // stylesheet, so toggling the class alone wouldn't actually hide it.
      this.compareBtnTarget.style.display = compareEnabled ? "inline-flex" : "none"
    }
    if (this.hasOverlayBtnTarget) {
      // Overlay only ever compares versions of the SAME sheet — same
      // eligibility as split compare (an older version must exist).
      this.overlayBtnTarget.style.display = compareEnabled ? "inline-flex" : "none"
    }

    const wasClosed = this.overlayTarget.classList.contains("hidden")
    this.overlayTarget.classList.remove("hidden")
    // Browser Back closes the viewer instead of leaving the page (Phelipe
    // 2026-08-27). Only on a real open — swapping sheets must not stack
    // an entry per sheet.
    if (wasClosed) this._pushViewerHistory()
    // Sheet-to-sheet navigation (prev/next arrows): the viewer chrome and the
    // old sheet stay visible — only a small "Loading…" chip shows — instead
    // of white-flashing the whole work area. First open keeps the full loader.
    this._softSwap = !!this._instance && !this.overlayTarget.classList.contains("hidden") && this._documentReady
    if (this._softSwap) this._showSoftLoader(filename)
    else this.loadingTarget.classList.remove("hidden")
    this.filenameTarget.textContent = filename || "document.pdf"
    // Opening another document always starts unsplit (no reload — we're
    // about to load the new document anyway).
    if (this._splitActive) await this._unsplit(false)
    // "Split" button next to the filename. When a splitListUrl is given
    // (Specs / Drawings viewers), clicking it opens a picker to choose any
    // project document (standard drawing, spec section, or drawing sheet)
    // to view side-by-side. No-op when absent.
    this._splitListUrl = splitListUrl
    this._renderSplitButton(splitListUrl)
    // References strip: documents this one cites (Section N, SD-N, sheet
    // numbers), each a chip that splits to it. Fetched on demand.
    this._referencesUrl = referencesUrl
    // Fetch the document's cross-references; the in-PDF clickable
    // highlights (built after load) consume this.
    this._referencesPromise = referencesUrl ? this._fetchReferences(referencesUrl) : Promise.resolve([])
    this._refHotspotIds = new Set()
    this._setVersionBadge(version, versionCurrent, versionTag, versionTagTitle)
    this._setSaveStatus("")
    this._customSaveUrl = saveUrl || null
    this._readOnly = readOnly
    this._commentOnly = commentOnly
    this._related = related
    this._existingAnnotIds = null
    this._documentReady = false
    // Search-inside-documents: jump to this page and pre-run this term once
    // the document loads (set when opening from a list search result).
    this._initialPage = initialPage || null
    this._searchTerm = (searchTerm || "").trim() || null

    // Live draft-comment integration: as the user annotates, build a phrase
    // ("Annotated pages 1, 2, and 4 in file.pdf") into the host page's comment
    // box. `commentEl` is the host's comment textarea; `onDraftUpdate` lets the
    // host react (autosize, send-button state). We strip the draft we wrote on a
    // previous open so reopening doesn't accumulate stale text on top of it.
    this._commentEl = commentEl || null
    this._onDraftUpdate = typeof onDraftUpdate === "function" ? onDraftUpdate : null
    const prevDraft = this._lastAutoDraft || ""
    let base = this._commentEl ? this._commentEl.value : ""
    if (prevDraft && base.endsWith(prevDraft)) {
      base = base.slice(0, base.length - prevDraft.length).replace(/\n+$/, "")
    }
    this._draftBaseText = base
    this._lastAutoDraft = ""

    // Hide "Markup in Bluebeam" button — active sessions now open Bluebeam directly
    if (this.hasBluebeamBtnTarget) {
      this.bluebeamBtnTarget.classList.add("hidden")
      // Drawings & Specs opt in explicitly per document; null (Design and
      // other hosts that don't pass the flag) keeps the legacy behavior
      // above untouched. Inline style because `inline-flex` beats `hidden`
      // in the stylesheet (same trick as compareBtn).
      if (bluebeamEnabled !== null) {
        this.bluebeamBtnTarget.style.display = bluebeamEnabled ? "inline-flex" : "none"
      }
    }

    try {
      const url = dataUrl || (this.baseUrlValue.replace(/\/0\//, `/${punchlistId}/`) + `?attachment_id=${attachmentId}`)
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()

      this._currentAttachmentId = data.attachment_id
      this._currentDocData = data
      this._punchlistId = punchlistId
      this.filenameTarget.textContent = filename || data.name || "document.pdf"

      // Sheet tabs (Larry 2026-07-31): every sheet opened in this viewer
      // session becomes a tab — bounce between them, ✕ to drop one, ⇆ to
      // compare a tab side-by-side with the current sheet. Drawings/Specs
      // hosts only (they pass splitListUrl/referencesUrl).
      if (splitListUrl || referencesUrl) {
        this._trackSheetTab({ attachmentId: data.attachment_id, filename: filename || data.name || "document.pdf",
                              args: arguments[0], replaceTab })
      }

      // Prefetched/primed neighbor? Reuse its PDF bytes (the slow download)
      // and, when it was parse-ahead'd, the ready-to-show document. `data`
      // above was still fetched fresh, so annotations stay current.
      const pre = await this._takePreloaded(url)

      if (!this._instance) {
        await this._initWebViewer(data)
      } else {
        await this._loadDocument(data, pre.blob, pre.doc)
      }

      this._schedulePrefetchNeighbors()

      // Apply the user's saved PDF-viewer theme (independent of the app theme).
      // Initialize once from the saved value; keep any in-session toggle after that.
      if (this._pdfTheme == null) {
        this._pdfTheme = this.pdfThemeValue === "dark" ? "dark" : "light"
      }
      this._instance.UI.setTheme(this._pdfTheme)
      this._applyOverlayTheme()
    } catch (error) {
      console.error("[action-item-pdf-viewer] Failed to load PDF:", error)
      this.filenameTarget.textContent = "Error loading PDF"
      this.loadingTarget.classList.add("hidden")
      this._hideSoftLoader()
    }
  }

  // ── Neighbor prefetch (Phelipe 2026-08-01) ────────────────────────────
  // After a sheet loads, quietly download the prev/next sheets' PDF bytes so
  // arrow navigation swaps without the big S3 download. Server-friendly:
  // one light JSON call per neighbor (the bytes come from S3), sequential,
  // delayed past the current load, capped LRU cache with a short TTL.

  PREFETCH_CACHE_MAX = 6
  PREFETCH_TTL_MS = 10 * 60 * 1000

  // Preloaded material for a sheet about to open: cached bytes (prefetch or
  // primeBlob) and, when the neighbor was parse-ahead'd, the ready Document.
  // Awaits an in-flight primeBlob download instead of starting a second one.
  // The parsed doc is single-use: hand it to the viewer and drop it.
  async _takePreloaded(dataUrl) {
    const out = { blob: null, doc: null }
    const cache = this._pdfBlobCache
    const entry = cache && cache.get(dataUrl)
    if (entry) {
      if (Date.now() - entry.ts > this.PREFETCH_TTL_MS) {
        cache.delete(dataUrl)
      } else if (entry.blob) {
        out.blob = entry.blob
      } else if (entry.promise) {
        out.blob = await entry.promise.catch(() => null)
      }
    }
    const parsed = this._parsedDocCache && this._parsedDocCache.get(dataUrl)
    if (parsed && Date.now() - parsed.ts <= this.PREFETCH_TTL_MS) out.doc = parsed.doc
    if (this._parsedDocCache) this._parsedDocCache.delete(dataUrl)
    return out
  }

  // Start downloading a sheet's PDF the moment the host knows its direct
  // URL — in parallel with the metadata request open() makes. No extra
  // requests: the same download, just earlier.
  primeBlob(dataUrl, directPdfUrl) {
    if (!dataUrl || !directPdfUrl) return
    this._pdfBlobCache = this._pdfBlobCache || new Map()
    const existing = this._pdfBlobCache.get(dataUrl)
    if (existing && Date.now() - existing.ts <= this.PREFETCH_TTL_MS) return
    const entry = { ts: Date.now() }
    entry.promise = fetch(directPdfUrl)
      .then(r => { if (!r.ok) throw new Error(`prime HTTP ${r.status}`); return r.blob() })
      .then(blob => { entry.blob = blob; return blob })
      .catch(() => { this._pdfBlobCache.delete(dataUrl); return null })
    this._pdfBlobCache.set(dataUrl, entry)
  }

  _schedulePrefetchNeighbors() {
    clearTimeout(this._prefetchTimer)
    const urls = this._neighborUrls || []
    // Parse-ahead docs are only ever the CURRENT neighbors — drop the rest
    // so at most two parsed documents sit in memory.
    if (this._parsedDocCache) {
      Array.from(this._parsedDocCache.keys()).forEach(k => { if (!urls.includes(k)) this._parsedDocCache.delete(k) })
    }
    if (!urls.length) return
    // Wait a beat so the prefetch never competes with the sheet the user is
    // actually looking at.
    this._prefetchTimer = setTimeout(() => { this._prefetchNeighbors(urls) }, 1200)
  }

  async _prefetchNeighbors(urls) {
    this._pdfBlobCache = this._pdfBlobCache || new Map()
    this._parsedDocCache = this._parsedDocCache || new Map()
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    for (const url of urls) {
      try {
        let entry = this._pdfBlobCache.get(url)
        if (entry && Date.now() - entry.ts > this.PREFETCH_TTL_MS) { this._pdfBlobCache.delete(url); entry = null }
        let blob = entry && (entry.blob || (entry.promise ? await entry.promise.catch(() => null) : null))
        if (!blob) {
          const resp = await fetch(url, {
            headers: { "Accept": "application/json", "X-CSRF-Token": token },
            credentials: "same-origin"
          })
          if (!resp.ok) continue
          const data = await resp.json()
          if (!data.pdf_url) continue
          const pdfResp = await fetch(data.pdf_url)
          if (!pdfResp.ok) continue
          blob = await pdfResp.blob()
          this._pdfBlobCache.set(url, { blob, ts: Date.now() })
          // LRU-ish cap: drop the oldest entries beyond the limit.
          while (this._pdfBlobCache.size > this.PREFETCH_CACHE_MAX) {
            const oldest = this._pdfBlobCache.keys().next().value
            this._pdfBlobCache.delete(oldest)
          }
        }
        // Parse-ahead: build the ready-to-show document in the background so
        // arrowing skips the parse too. Best-effort — worst case the blob
        // alone still saves the download.
        if (blob && !this._parsedDocCache.get(url)) {
          try {
            const doc = await this._instance.Core.createDocument(blob, { extension: "pdf" })
            this._parsedDocCache.set(url, { doc, ts: Date.now() })
          } catch (_) { /* parse-ahead is bonus */ }
        }
      } catch (_) { /* prefetch is pure bonus — never surface errors */ }
    }
  }

  // ── Sheet tabs (Larry 2026-07-31, item 22) ────────────────────────────

  // Tabs persist per project per day (Larry 2026-08-06: keep them across
  // a refresh or a detour elsewhere, but "close it down the next day" so
  // they never stack up forever). localStorage, defensively — args must
  // survive JSON; anything that can't just isn't restored.
  _tabStorageKey() {
    // The design phase lives at /design/:project_id/step — it used to fall
    // through to the shared "global" bucket, colliding across projects.
    const m = window.location.pathname.match(/\/projects\/\d+/) ||
              window.location.pathname.match(/\/design\/\d+/)
    return `conslog_sheet_tabs::${m ? m[0] : "global"}`
  }

  _persistSheetTabs() {
    try {
      const tabs = (this._sheetTabs || []).filter(t => {
        try { JSON.stringify(t.args); return true } catch { return false }
      })
      window.localStorage.setItem(this._tabStorageKey(), JSON.stringify({
        day: new Date().toDateString(),
        activeKey: this._activeSheetKey,
        tabs: tabs.map(t => ({ key: t.key, filename: t.filename, args: t.args }))
      }))
    } catch { /* storage full/blocked — tabs just don't persist */ }
  }

  _readSavedSheetTabs() {
    try {
      const raw = window.localStorage.getItem(this._tabStorageKey())
      if (!raw) return null
      const saved = JSON.parse(raw)
      if (saved.day !== new Date().toDateString()) {
        window.localStorage.removeItem(this._tabStorageKey())
        return null
      }
      return saved.tabs?.length ? saved : null
    } catch {
      return null
    }
  }

  // The toolbar's "Open last preview": bring back today's tabs and land on
  // the one that was active.
  openLastPreview() {
    const saved = this._readSavedSheetTabs()
    if (!saved) {
      const flash = document.createElement("div")
      flash.className = "flash-toast-alert"
      flash.setAttribute("data-controller", "flash")
      flash.setAttribute("data-flash-dismiss-after-value", "4000")
      flash.textContent = "No sheets opened today yet."
      document.body.prepend(flash)
      return
    }
    this._sheetTabs = saved.tabs.slice()
    const active = saved.tabs.find(t => t.key === saved.activeKey) || saved.tabs[saved.tabs.length - 1]
    if (active?.args) this.open(active.args)
  }

  _trackSheetTab({ attachmentId, filename, args, replaceTab = false }) {
    if (!this._sheetTabs) {
      // First sheet of this page-load: today's earlier tabs come back
      // alongside it instead of being lost to the refresh.
      const saved = this._readSavedSheetTabs()
      this._sheetTabs = saved ? saved.tabs.slice() : []
    }
    const key = String(attachmentId || filename)
    // Never store the replaceTab flag: tab pills replay these args on click,
    // and a replayed open must land as a plain activate, not another replace.
    const cleanArgs = { ...(args || {}) }
    delete cleanArgs.replaceTab
    const existing = this._sheetTabs.find(t => t.key === key)
    if (existing) {
      existing.args = cleanArgs
      existing.filename = filename
      // Arrowing onto a sheet that already has its own tab: the walked-from
      // tab closes so the advance doesn't leave a duplicate trail behind.
      if (replaceTab && this._activeSheetKey && this._activeSheetKey !== key) {
        this._sheetTabs = this._sheetTabs.filter(t => t.key !== this._activeSheetKey)
      }
    } else if (replaceTab && this._sheetTabs.some(t => t.key === this._activeSheetKey)) {
      // Prev/next advance (Larry 2026-08-20): the current tab BECOMES the new
      // sheet instead of stacking a new pill per arrow press.
      const cur = this._sheetTabs.find(t => t.key === this._activeSheetKey)
      cur.key = key
      cur.filename = filename
      cur.args = cleanArgs
    } else {
      this._sheetTabs.push({ key, filename, args: cleanArgs })
      if (this._sheetTabs.length > 10) {
        const idx = this._sheetTabs.findIndex(t => t.key !== key)
        if (idx >= 0) this._sheetTabs.splice(idx, 1)
      }
    }
    this._activeSheetKey = key
    this._renderSheetTabs()
    this._persistSheetTabs()
  }

  // Colors for OUR chrome around the Apryse viewer (header, sheet tabs),
  // light or dark to match the viewer theme (Phelipe 2026-09-16: "if I'm on
  // dark mode make this top section dark as well").
  _chromeColors() {
    const dark = this._pdfTheme === "dark"
    return dark
      ? { stripBg: "#1f2226", border: "#3a3f46", bg: "#2a2e33", text: "#9ca3af", textStrong: "#d1d5db",
          activeBg: "#1e3a5f", activeBorder: "#3b82f6", activeText: "#93c5fd" }
      : { stripBg: "#f9fafb", border: "#e5e7eb", bg: "#ffffff", text: "#6b7280", textStrong: "#4b5563",
          activeBg: "#eff6ff", activeBorder: "#93c5fd", activeText: "#1d4ed8" }
  }

  _applyOverlayTheme() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.toggle("pdf-dark", this._pdfTheme === "dark")
    this._renderSheetTabs()
    this._styleTakeoffAiBar()
  }

  _renderSheetTabs() {
    // Dedicated thin tab row between the header and the Apryse chrome
    // (Phelipe 2026-08-01: "let's try putting tabs here — a thin extra
    // line"), instead of pills squeezed next to the filename.
    if (!this._sheetTabStrip || !this._sheetTabStrip.isConnected) {
      const strip = document.createElement("div")
      strip.style.cssText = "display:none;align-items:center;gap:6px;padding:3px 12px;min-height:30px;" +
        "overflow-x:auto;scrollbar-width:thin;background:#f9fafb;border-bottom:1px solid #e5e7eb;flex-shrink:0;"
      const anchor = this.hasViewerContainerTarget ? this.viewerContainerTarget : null
      if (anchor && anchor.parentElement) {
        anchor.parentElement.insertBefore(strip, anchor)
      } else if (this.hasFilenameTarget) {
        this.filenameTarget.insertAdjacentElement("afterend", strip)
      } else {
        return
      }
      this._sheetTabStrip = strip
    }
    const strip = this._sheetTabStrip
    const c = this._chromeColors()
    strip.style.background = c.stripBg
    strip.style.borderBottom = `1px solid ${c.border}`
    // Drag-and-drop lands on the STRIP (wired once — it survives re-renders).
    // Chrome-tabs feel (Phelipe 2026-08-26): the dragged pill itself slides
    // through the strip on dragover, so the OTHER tabs shift aside and you
    // watch the final order form live. Native DnD survives because the node
    // is only moved within its parent, never removed; the order you see is
    // committed on dragend.
    if (!strip._dndWired) {
      strip._dndWired = true
      strip.addEventListener("dragover", (e) => {
        if (!this._dragTabKey) return
        e.preventDefault()
        e.dataTransfer.dropEffect = "move"
        const dragged = strip.querySelector(`[data-tab-key="${CSS.escape(this._dragTabKey)}"]`)
        if (!dragged) return
        const others = [...strip.querySelectorAll("[data-tab-key]")].filter(p => p !== dragged)
        const next = others.find(p => {
          const r = p.getBoundingClientRect()
          return e.clientX < r.left + r.width / 2
        }) || null
        if (dragged.nextElementSibling !== next) strip.insertBefore(dragged, next)
        // Nudge the strip when dragging near its edges — overflow-x means a
        // long tab row otherwise can't be crossed in one drag.
        const sr = strip.getBoundingClientRect()
        if (e.clientX < sr.left + 40) strip.scrollLeft -= 12
        else if (e.clientX > sr.right - 40) strip.scrollLeft += 12
      })
      // Commit happens in dragend (it always fires); drop only suppresses
      // the browser's snap-back animation.
      strip.addEventListener("drop", (e) => e.preventDefault())
    }
    strip.innerHTML = ""
    const tabs = this._sheetTabs || []
    strip.style.display = tabs.length ? "flex" : "none"
    if (!tabs.length) return

    // "Close all" lives on the far LEFT of the strip (Larry 2026-08-18) —
    // one click instead of X-ing tabs one by one.
    const closeAll = document.createElement("button")
    closeAll.type = "button"
    closeAll.textContent = "Close all"
    closeAll.title = "Close all sheet tabs"
    closeAll.style.cssText = `border:1px solid ${c.border};background:${c.bg};color:${c.text};cursor:pointer;` +
      "font-size:11px;padding:1px 7px;border-radius:8px;flex-shrink:0;white-space:nowrap;"
    closeAll.addEventListener("click", () => this._closeAllSheetTabs())
    strip.appendChild(closeAll)

    tabs.forEach(tab => {
      const active = tab.key === this._activeSheetKey
      const pill = document.createElement("span")
      pill.style.cssText = "display:inline-flex;align-items:center;gap:5px;padding:2px 9px;border-radius:8px;" +
        `font-size:12px;white-space:nowrap;cursor:pointer;flex-shrink:0;border:1px solid ${active ? c.activeBorder : c.border};` +
        `background:${active ? c.activeBg : c.bg};color:${active ? c.activeText : c.textStrong};`
      // Tabs drag to reorder, browser-tab style (Larry 2026-08-25): the
      // dragged pill ghosts out and a blue bar previews exactly where it
      // will land. Strip-level handlers do the actual placement.
      pill.dataset.tabKey = tab.key
      pill.draggable = true
      pill.addEventListener("dragstart", (e) => {
        this._dragTabKey = tab.key
        e.dataTransfer.effectAllowed = "move"
        try { e.dataTransfer.setData("text/plain", tab.key) } catch (_) {}
        // Deferred so the browser's drag image is the normal pill, not the ghost.
        setTimeout(() => { pill.style.opacity = "0.4"; pill.style.borderStyle = "dashed" }, 0)
      })
      pill.addEventListener("dragend", () => this._endTabDrag())
      const name = document.createElement("span")
      name.textContent = (tab.filename || "sheet").split(" · ")[0]
      name.style.cssText = "max-width:180px;overflow:hidden;text-overflow:ellipsis;"
      name.title = tab.filename || ""
      if (!active) name.addEventListener("click", () => this.open(tab.args))
      pill.appendChild(name)
      if (!active) {
        const cmp = document.createElement("button")
        cmp.type = "button"
        cmp.textContent = "⇆"
        cmp.title = `Compare ${name.textContent} with the current sheet`
        cmp.style.cssText = `border:none;background:none;color:${c.text};cursor:pointer;font-size:12px;padding:0;`
        cmp.addEventListener("click", (e) => { e.stopPropagation(); this._compareSheetTab(tab) })
        pill.appendChild(cmp)
      }
      {
        const x = document.createElement("button")
        x.type = "button"
        x.textContent = "✕"
        x.title = "Close tab"
        x.style.cssText = "border:none;background:none;color:#9ca3af;cursor:pointer;font-size:11px;padding:0;"
        x.addEventListener("click", (e) => { e.stopPropagation(); this._closeSheetTab(tab) })
        pill.appendChild(x)
      }
      strip.appendChild(pill)
    })
  }

  // Commit whatever order the strip is showing when the drag ends — what
  // you previewed is what you get.
  _endTabDrag() {
    this._dragTabKey = null
    const strip = this._sheetTabStrip
    if (strip) {
      const order = [...strip.querySelectorAll("[data-tab-key]")].map(p => p.dataset.tabKey)
      const byKey = new Map((this._sheetTabs || []).map(t => [t.key, t]))
      const reordered = order.map(k => byKey.get(k)).filter(Boolean)
      if (reordered.length === (this._sheetTabs || []).length) this._sheetTabs = reordered
    }
    this._persistSheetTabs()
    this._renderSheetTabs()
  }

  _closeSheetTab(tab) {
    this._sheetTabs = (this._sheetTabs || []).filter(t => t.key !== tab.key)
    this._persistSheetTabs()
    if (this._activeSheetKey === tab.key && this._sheetTabs.length) {
      const next = this._sheetTabs[this._sheetTabs.length - 1]
      this._renderSheetTabs()
      this.open(next.args)
      return
    }
    this._renderSheetTabs()
    // Closing the ONLY tab used to be impossible (no X — Larry 2026-08-18);
    // now it closes the preview itself, like closing the last browser tab.
    if (!this._sheetTabs.length) this.close()
  }

  _closeAllSheetTabs() {
    this._sheetTabs = []
    this._activeSheetKey = null
    this._persistSheetTabs()
    this._renderSheetTabs()
    this.close()
  }

  // Side-by-side: reuse the split machinery with the tab's document on the
  // right pane (its dataUrl serves the same pdf_viewer_data JSON).
  _compareSheetTab(tab) {
    const dataUrl = tab.args && tab.args.dataUrl
    if (!dataUrl) return
    this._related = { dataUrl, label: (tab.filename || "sheet").split(" · ")[0], term: null }
    this._toggleSplit()
    const m = window.location.pathname.match(/\/projects\/(\d+)/)
    track("Takeoff Sheets Compared", { project_id: m ? parseInt(m[1], 10) : null })
  }

  _showSoftLoader(filename) {
    if (!this._softChip) {
      const chip = document.createElement("div")
      chip.style.cssText = "position:absolute;top:55px;left:16px;z-index:70;" +
        "display:flex;align-items:center;gap:8px;background:#f3f4f6;border:1px solid #e5e7eb;border-radius:999px;" +
        "padding:6px 14px;font-size:13px;color:#374151;box-shadow:0 4px 14px rgba(0,0,0,.15);max-width:520px;"
      chip.innerHTML = '<svg class="animate-spin" style="width:14px;height:14px;color:#2563eb;" fill="none" viewBox="0 0 24 24">' +
        '<circle style="opacity:.25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>' +
        '<path style="opacity:.75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span></span>'
      const host = this.hasViewerContainerTarget ? this.viewerContainerTarget : this.element
      if (getComputedStyle(host).position === "static") host.style.position = "relative"
      host.appendChild(chip)
      this._softChip = chip
    }
    const short = (filename || "sheet").split(" · ")[0]
    this._softChip.lastElementChild.textContent = `Loading ${short}…`
    this._softChip.style.display = "flex"
  }

  _hideSoftLoader() {
    if (this._softChip) this._softChip.style.display = "none"
  }

  // opts.keepHistory — leave the viewer's Back-button entry in place. Only
  // openInBluebeam passes it: that flow either sends the browser to
  // Bluebeam's sign-in or shows the launch popover on this very page, and
  // the history.back() a normal close performs (~1s later, after the
  // annotation flush) cancelled the sign-in navigation outright, or — when
  // a session was created — made Turbo restore the page, so the popover
  // opened on a detached copy nobody could see (Phelipe 2026-09-14, the
  // Kearney drawings). A Stimulus click action passes an Event here, which
  // has no keepHistory, so every other close keeps today's behavior.
  async close(opts) {
    const keepHistory = !!(opts && opts.keepHistory === true)
    clearTimeout(this._saveTimeout)

    // Force-complete any in-progress annotation (e.g. freehand pen stroke
    // whose mouseup landed outside the canvas when clicking Close)
    if (this._instance && (!this._readOnly || this._commentOnly)) {
      try {
        const dv = this._instance.Core.documentViewer
        dv.setToolMode(dv.getTool("AnnotationEdit"))
      } catch (_) { /* best-effort */ }
      // Brief delay for annotation manager to process the finalized annotation
      await new Promise(r => setTimeout(r, 200))
    }

    // Wait for any in-flight save to finish
    if (this._saving) {
      await new Promise(resolve => {
        const check = () => this._saving ? setTimeout(check, 50) : resolve()
        check()
      })
    }

    // Final save on close — only if user actually made changes
    if (this._dirty && (!this._readOnly || this._commentOnly) && this._instance && this._currentAttachmentId) {
      await this._saveAnnotations()
    }

    if (this._splitActive) await this._unsplit(false)
    this.overlayTarget.classList.add("hidden")
    this._currentAttachmentId = null
    this._dirty = false
    // Drop the viewer's history entry so the NEXT Back goes where the user
    // expects (the page behind it), not back into a closed viewer. A close
    // that came FROM Back has already consumed it — see _pushViewerHistory.
    // With keepHistory the entry stays (and _viewerHistoryPushed stays true,
    // so the next open doesn't push a second one); the next ordinary close
    // or Back press consumes it.
    if (!keepHistory) this._consumeViewerHistory()
    this.element.dispatchEvent(new CustomEvent("pdf-viewer:closed", { bubbles: true }))
  }

  // ── Back button (Phelipe 2026-08-27) ────────────────────────────────
  // The viewer is an overlay on the host page, not its own URL, so the
  // browser's Back button used to sail straight past it and land on
  // whatever came before — for an action item's PDF that meant the
  // dashboard, losing the user's place entirely. Opening now pushes one
  // history entry; Back pops it and simply closes the viewer.
  _pushViewerHistory() {
    if (this._viewerHistoryPushed) return

    try {
      history.pushState({ conslogPdfViewer: true }, "")
      this._viewerHistoryPushed = true
    } catch (_) {
      return // private mode / blocked history — Back just behaves as before
    }

    if (!this._boundViewerPopState) {
      this._boundViewerPopState = () => {
        // The entry is already gone; close WITHOUT calling history.back()
        // again, which would take the user off the page after all.
        this._viewerHistoryPushed = false
        if (!this.overlayTarget.classList.contains("hidden")) this.close()
      }
      window.addEventListener("popstate", this._boundViewerPopState)
    }
  }

  _consumeViewerHistory() {
    if (!this._viewerHistoryPushed) return

    this._viewerHistoryPushed = false
    try { history.back() } catch (_) { /* best-effort */ }
  }

  openInBluebeam() {
    const pdfId = this._punchlistId
    // keepHistory: see close(). The host either navigates to Bluebeam's
    // sign-in or shows its popover here — a history.back() in between would
    // cancel the first and hide the second.
    this.close({ keepHistory: true })
    this.element.dispatchEvent(new CustomEvent("pdf-viewer:open-bluebeam", { bubbles: true, detail: { pdfId } }))
  }

  // Ask AI from inside the fullscreen viewer (Larry 2026-08-26): the
  // overlay buries the header's green chat button, so the viewer's own
  // button pops the ChatLog panel (it z-indexes above the viewer).
  askAi() {
    window.dispatchEvent(new CustomEvent("toolbox-chat:toggle"))
  }

  // ── Hide/unhide markups by person (Larry 2026-08-26) ───────────────
  // "Larry's marked it up a bunch — I just want to hide his markups."
  // A checkbox per author (yourself included); Hide all / Show all on
  // top. Hidden authors stay hidden across sheet swaps and fresh adds.
  toggleMarkupPeople() {
    if (!this._instance || !this.hasMarkupPeoplePanelTarget) return
    const panel = this.markupPeoplePanelTarget
    if (panel.classList.contains("hidden")) {
      this._ensureMarkupVisibilityHooks()
      this._renderMarkupPeople()
      panel.classList.remove("hidden")
    } else {
      panel.classList.add("hidden")
    }
  }

  _markupAuthors() {
    const { annotationManager } = this._instance.Core
    const skip = new Set(["AutoCAD SHX Text"])
    const authors = new Map()
    annotationManager.getAnnotationsList().forEach((a) => {
      const author = (a.Author || "").trim()
      if (!author || skip.has(author) || a.Subject === "refTarget") return
      authors.set(author, (authors.get(author) || 0) + 1)
    })
    return authors
  }

  _renderMarkupPeople() {
    const panel = this.markupPeoplePanelTarget
    this._hiddenMarkupAuthors = this._hiddenMarkupAuthors || new Set()
    const authors = this._markupAuthors()
    panel.innerHTML = ""

    const head = document.createElement("div")
    head.className = "flex items-center justify-between gap-2 px-2 py-1.5 border-b border-gray-100 mb-1"
    const title = document.createElement("span")
    title.className = "text-[13px] font-semibold text-gray-700"
    title.textContent = "Markups by person"
    head.appendChild(title)
    const links = document.createElement("span")
    links.className = "flex items-center gap-2"
    ;[["Hide all", true], ["Show all", false]].forEach(([label, hide]) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "text-[11px] text-brand hover:underline cursor-pointer"
      btn.textContent = label
      btn.addEventListener("click", () => {
        this._hiddenMarkupAuthors = hide ? new Set(this._markupAuthors().keys()) : new Set()
        this._applyMarkupVisibility()
        this._renderMarkupPeople()
      })
      links.appendChild(btn)
    })
    head.appendChild(links)
    panel.appendChild(head)

    if (!authors.size) {
      const empty = document.createElement("p")
      empty.className = "px-2 py-1.5 text-[13px] text-gray-400"
      empty.textContent = "No markups on this sheet yet."
      panel.appendChild(empty)
      return
    }

    ;[...authors.entries()].sort((a, b) => a[0].localeCompare(b[0])).forEach(([author, count]) => {
      const row = document.createElement("label")
      row.className = "flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
      const box = document.createElement("input")
      box.type = "checkbox"
      box.checked = !this._hiddenMarkupAuthors.has(author)
      box.className = "rounded border-gray-300 cursor-pointer"
      box.addEventListener("change", () => {
        if (box.checked) this._hiddenMarkupAuthors.delete(author)
        else this._hiddenMarkupAuthors.add(author)
        this._applyMarkupVisibility()
      })
      const name = document.createElement("span")
      name.className = "flex-1 min-w-0 truncate text-[13px] text-gray-700"
      name.textContent = author
      const n = document.createElement("span")
      n.className = "text-[11px] text-gray-400 tabular-nums"
      n.textContent = String(count)
      row.appendChild(box)
      row.appendChild(name)
      row.appendChild(n)
      panel.appendChild(row)
    })
  }

  _applyMarkupVisibility() {
    if (!this._instance || !this._hiddenMarkupAuthors) return
    const { annotationManager } = this._instance.Core
    const hide = []
    const show = []
    annotationManager.getAnnotationsList().forEach((a) => {
      const author = (a.Author || "").trim()
      if (!author) return
      ;(this._hiddenMarkupAuthors.has(author) ? hide : show).push(a)
    })
    if (show.length) annotationManager.showAnnotations(show)
    if (hide.length) annotationManager.hideAnnotations(hide)
  }

  // Sheet swaps reload annotations visible — re-apply the hidden set; a
  // fresh markup from a hidden author hides too.
  _ensureMarkupVisibilityHooks() {
    if (this._markupVisHooked || !this._instance) return
    this._markupVisHooked = true
    const { documentViewer, annotationManager } = this._instance.Core
    documentViewer.addEventListener("documentLoaded", () => {
      setTimeout(() => this._applyMarkupVisibility(), 500)
      if (this.hasMarkupPeoplePanelTarget && !this.markupPeoplePanelTarget.classList.contains("hidden")) {
        this._renderMarkupPeople()
      }
    })
    annotationManager.addEventListener("annotationChanged", (_annots, action) => {
      if (action === "add" && this._hiddenMarkupAuthors && this._hiddenMarkupAuthors.size) {
        this._applyMarkupVisibility()
      }
    })
  }

  async download() {
    if (!this._instance) return
    const { UI } = this._instance
    const filename = this.filenameTarget.textContent || "document"
    UI.downloadPdf({ filename })
  }

  // Ask the host page (Drawings) to open the compare sheet-picker for the
  // currently-open sheet. The host owns the list of sheets, so the picker
  // lives there; the viewer just hands off the current file id.
  requestCompare() {
    if (!this._compareFileId) return
    this.element.dispatchEvent(new CustomEvent("pdf-viewer:request-compare", {
      bubbles: true,
      detail: { fileId: this._compareFileId }
    }))
  }

  // "Compare (Overlay)" — same picker, overlay mode: the host resolves the
  // two versions and calls openOverlayCompare below.
  requestOverlay() {
    if (!this._compareFileId) return
    this.element.dispatchEvent(new CustomEvent("pdf-viewer:request-compare", {
      bubbles: true,
      detail: { fileId: this._compareFileId, mode: "overlay" }
    }))
  }

  // ── Overlay compare (Bluebeam-style, Phelipe 2026-08-01) ──────────────
  // Both versions of the SAME sheet blended into one image: the old
  // revision tinted red, the new one blue — shared linework goes dark,
  // every change pops as color. Built with plain page rendering + canvas
  // blending (our license doesn't include the engine's Document Comparison
  // package, and this needs no extra cost). All in the browser.
  async openOverlayCompare({ oldUrl, newUrl, title }) {
    if (!this._instance || !oldUrl || !newUrl) return
    this._showSoftLoader(title || "overlay")
    try {
      const [oldBlob, newBlob] = await Promise.all([
        this._fetchDocBlobFor(oldUrl),
        this._fetchDocBlobFor(newUrl)
      ])
      if (!oldBlob || !newBlob) throw new Error("Could not download both versions.")

      const { Core } = this._instance
      const [docOld, docNew] = await Promise.all([
        Core.createDocument(oldBlob, { extension: "pdf" }),
        Core.createDocument(newBlob, { extension: "pdf" })
      ])
      const merged = await this._buildOverlayCanvas(docOld, docNew)
      this._showOverlayLightbox(merged, title || "Overlay compare")
    } catch (e) {
      console.error("[action-item-pdf-viewer] overlay compare failed:", e)
      this._setSaveStatus("Couldn't build the overlay for these versions.")
    } finally {
      this._hideSoftLoader()
    }
  }

  // Viewer-data URL → PDF bytes (reuses the prefetch cache when warm).
  async _fetchDocBlobFor(dataUrl) {
    const pre = await this._takePreloaded(dataUrl)
    if (pre.blob) return pre.blob
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const resp = await fetch(dataUrl, {
      headers: { "Accept": "application/json", "X-CSRF-Token": token },
      credentials: "same-origin"
    })
    if (!resp.ok) return null
    const data = await resp.json()
    if (!data.pdf_url) return null
    const pdfResp = await fetch(data.pdf_url)
    return pdfResp.ok ? await pdfResp.blob() : null
  }

  // Render page 1 of a document to a canvas at the given pixel width.
  _renderPageCanvas(doc, targetWidth) {
    return new Promise((resolve, reject) => {
      try {
        const info = doc.getPageInfo(1)
        const zoom = targetWidth / (info?.width || targetWidth)
        doc.loadCanvas({
          pageNumber: 1,
          zoom,
          // Fixed 1x multiplier: on Retina the default doubles every
          // dimension and three ~5600px canvases would eat ~500MB.
          multiplier: 1,
          drawComplete: (canvas) => resolve(canvas)
        })
        setTimeout(() => reject(new Error("page render timed out")), 45000)
      } catch (e) { reject(e) }
    })
  }

  // Classic overlay recipe: "screen" a color over each page (black linework
  // takes the tint, white paper stays white), then "multiply" the two tinted
  // pages together — old-only marks read red, new-only blue, identical
  // linework stacks to dark, paper stays white.
  async _buildOverlayCanvas(docOld, docNew) {
    const TARGET_W = 2800
    const [oldCanvas, newCanvas] = await Promise.all([
      this._renderPageCanvas(docOld, TARGET_W),
      this._renderPageCanvas(docNew, TARGET_W)
    ])
    const W = Math.max(oldCanvas.width, newCanvas.width)
    const H = Math.max(oldCanvas.height, newCanvas.height)

    const tint = (src, color) => {
      const c = document.createElement("canvas")
      c.width = W
      c.height = H
      const x = c.getContext("2d")
      x.fillStyle = "#ffffff"
      x.fillRect(0, 0, W, H)
      x.drawImage(src, 0, 0)
      x.globalCompositeOperation = "screen"
      x.fillStyle = color
      x.fillRect(0, 0, W, H)
      return c
    }

    const merged = document.createElement("canvas")
    merged.width = W
    merged.height = H
    const ctx = merged.getContext("2d")
    ctx.drawImage(tint(oldCanvas, "rgb(235,64,64)"), 0, 0)
    ctx.globalCompositeOperation = "multiply"
    ctx.drawImage(tint(newCanvas, "rgb(64,108,235)"), 0, 0)
    return merged
  }

  // Fullscreen lightbox for the overlay result: legend, wheel/button zoom,
  // drag-free panning via native scroll, Esc or ✕ to close. The live sheet
  // stays loaded underneath, so closing returns instantly.
  _showOverlayLightbox(canvas, title) {
    this._closeOverlayLightbox()
    const box = document.createElement("div")
    box.style.cssText = "position:fixed;inset:0;z-index:2147483040;background:#1f2937;display:flex;flex-direction:column;"
    const bar = document.createElement("div")
    bar.style.cssText = "display:flex;align-items:center;gap:12px;padding:8px 14px;background:#111827;color:#e5e7eb;font-size:13px;flex-shrink:0;"
    const name = document.createElement("span")
    name.textContent = title
    name.style.cssText = "font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"
    const legend = document.createElement("span")
    legend.innerHTML = '<span style="color:#f87171;">■ old</span>&nbsp; <span style="color:#93c5fd;">■ new</span>&nbsp; <span style="color:#9ca3af;">■ unchanged</span>'
    legend.style.cssText = "white-space:nowrap;"
    const spacer = document.createElement("span")
    spacer.style.flex = "1"
    const mkBtn = (label, title2) => {
      const b = document.createElement("button")
      b.type = "button"
      b.textContent = label
      b.title = title2 || ""
      b.style.cssText = "background:#374151;color:#e5e7eb;border:none;border-radius:8px;padding:4px 12px;font-size:13px;cursor:pointer;"
      return b
    }
    const zoomOut = mkBtn("−", "Zoom out")
    const zoomIn = mkBtn("+", "Zoom in")
    // Live zoom readout, like the sheet viewer's (Larry 2026-08-21) — the
    // overlay gave no sign it was zooming at all.
    const zoomPct = document.createElement("span")
    zoomPct.style.cssText = "min-width:46px;text-align:center;color:#9ca3af;font-variant-numeric:tabular-nums;"
    const fit = mkBtn("Fit", "Fit the sheet to the window")
    const close = mkBtn("✕ Close", "Back to the sheet (Esc)")
    bar.append(name, legend, spacer, zoomOut, zoomPct, zoomIn, fit, close)

    const scroller = document.createElement("div")
    scroller.style.cssText = "flex:1;overflow:auto;display:block;padding:16px;"
    canvas.style.cssText = "display:block;margin:0 auto;background:#fff;box-shadow:0 8px 30px rgba(0,0,0,.45);"
    scroller.appendChild(canvas)

    const onKey = (e) => { if (e.key === "Escape") this._closeOverlayLightbox() }
    document.addEventListener("keydown", onKey)
    this._overlayLightboxKey = onKey
    close.addEventListener("click", () => this._closeOverlayLightbox())

    box.append(bar, scroller)
    document.body.appendChild(box)
    this._overlayLightbox = box

    // Size AFTER mounting, from the real container width (window.innerWidth
    // is unreliable in embedded browser contexts).
    let width = Math.round((scroller.clientWidth || 0) * 0.94)
    if (!width || width < 320) width = Math.min(canvas.width, 1400)
    const fitWidth = width
    const apply = () => {
      canvas.style.width = `${width}px`
      canvas.style.height = "auto"
      zoomPct.textContent = `${Math.round((width / fitWidth) * 100)}%`
    }
    apply()

    // Zoom about the pointer so the spot under the cursor stays put —
    // scrolling to the same place after every step is what made this feel
    // broken.
    const zoomBy = (f, e) => {
      const before = width
      width = Math.max(320, Math.min(canvas.width * 4, Math.round(width * f)))
      apply()
      if (width === before) return
      const r = scroller.getBoundingClientRect()
      const px = (e ? e.clientX - r.left : r.width / 2) + scroller.scrollLeft
      const py = (e ? e.clientY - r.top : r.height / 2) + scroller.scrollTop
      const ratio = width / before
      scroller.scrollLeft += px * (ratio - 1)
      scroller.scrollTop += py * (ratio - 1)
    }
    zoomIn.addEventListener("click", () => zoomBy(1.25))
    zoomOut.addEventListener("click", () => zoomBy(0.8))
    fit.addEventListener("click", () => { width = fitWidth; apply(); scroller.scrollTo(0, 0) })

    // Wheel behaves like every other viewer in the app (Larry 2026-08-21):
    // a plain wheel SCROLLS the sheet, ctrl/cmd-wheel and trackpad pinch
    // zoom. It used to swallow every scroll and turn it into a zoom, so
    // there was no way to pan.
    scroller.addEventListener("wheel", (e) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      zoomBy(e.deltaY < 0 ? 1.15 : 0.87, e)
    }, { passive: false })
    const m = window.location.pathname.match(/\/projects\/(\d+)/)
    track("Drawing Overlay Compared", { project_id: m ? parseInt(m[1], 10) : null })
  }

  _closeOverlayLightbox() {
    if (this._overlayLightboxKey) document.removeEventListener("keydown", this._overlayLightboxKey)
    this._overlayLightboxKey = null
    if (this._overlayLightbox) this._overlayLightbox.remove()
    this._overlayLightbox = null
  }

  // ── Private ─────────────────────────────────────────────────────

  async _ensureWebViewerLoaded() {
    if (typeof window.WebViewer === "function") return
    return new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = "/webviewer/webviewer.min.js"
      script.onload = resolve
      script.onerror = () => reject(new Error("Failed to load WebViewer script"))
      document.head.appendChild(script)
    })
  }

  async _initWebViewer(data) {
    await this._ensureWebViewerLoaded()
    const instance = await window.WebViewer({
      path: "/webviewer",
      licenseKey: this.licenseKeyValue,
      fullAPI: true,
      // Firefox only (S2-17, Starr 2026-10-23): Apryse's virtual display
      // mode — pages drawn as you scroll — breaks on Firefox 146+ with this
      // WebViewer (11.7.1): a PDF of about 20+ pages throws "too much
      // recursion" and freezes the tab once scrolled; Apryse fixed it in
      // 11.9.1. Their own workaround until we upgrade is to switch the mode
      // off on Firefox. As a constructor option it reaches every pane
      // (split and compare too). Chrome, Safari and Edge are untouched.
      disableVirtualDisplayMode: isFirefox()
    }, this.viewerContainerTarget)

    this._instance = instance

    // Enable Apryse's native multi-viewer so the View ▸ Page Layout ▸
    // "Compare Pages" item appears — lets the user drag/browse a file from
    // outside the tool to compare against the open sheet.
    try {
      instance.UI.enableFeatures([instance.UI.Feature.MultiViewerMode])
    } catch (e) {
      console.warn("[action-item-pdf-viewer] Could not enable MultiViewerMode:", e)
    }

    // "Takeoff" ribbon — contractor measurement tools, placed after Forms.
    this._setupTakeoffRibbon()

    // Larry (Kloepfer) 2026-09-15, Bluebeam habits: a bar along the bottom
    // with the arrow / hand / zoom / sheet-layout controls, Backspace while
    // placing points, shift-click to add or drop a node, a popup on the
    // selected markup (hatch, side lengths, label, text size/color,
    // eyedropper, cutout) and a hover readout of any measurement.
    this._buildBottomToolbar()
    this._installDrawingKeys()
    this._installMeasureHover()
    if (!this._readOnly) this._installNodeEditing()
    // The markup popup buttons (hatch, sides, label, text size/color,
    // eyedropper, cutout) were pulled by Phelipe 2026-09-16 — the style
    // panel already covers color, line style, fill and opacity. The flows
    // stay wired (_installMarkupPopup + the _tk* methods) for a later home.

    // Keyboard ← / → = the header's prev/next sheet buttons (Phelipe
    // 2026-07-28). Listens on the app window AND the Apryse iframe (where
    // focus usually lives). Skips typing fields and pages without the nav.
    this._installArrowForwarding()
    this._buildFloatingNav()

    // Set annotation author to the logged-in user's name
    if (this.userNameValue) {
      instance.Core.annotationManager.setCurrentUser(this.userNameValue)
    }
    // Everyone on the project can edit/delete ANY markup, not just their
    // own (Larry 2026-08-12: an old polygon had no trash option — Apryse's
    // default author check had locked it because its stored author string
    // no longer matched). Deliberately-locked annotations (reference
    // hotspots set Locked=true) stay protected.
    instance.Core.annotationManager.promoteUserToAdmin()

    await this._loadDocument(data)
    this._setupScrollZoom()
    this._addScrollZoomToggle()
    this._addThemeToggle()
    this._setupLeftPanel()
    this._setupZoomPersistence()
    if (!this._readOnly || this._commentOnly) this._setupAnnotationListeners()
  }

  // "Takeoff" tab: the measurement/takeoff tools a contractor needs —
  // calibrate/scale (each tool's Scale control), distance, arc, perimeter,
  // area, ellipse area, rectangular area, and count. WebViewer 11's Modular
  // UI ships these as the stock Measure ribbon, revealed by the Measurement
  // feature; we relabel it "Takeoff" and move it to sit right after "Forms"
  // (View · Annotate · Shapes · Insert · Edit · Forms · Fill and Sign order
  // comes from the default modular header). Every step is defensive: if the
  // modular-header API differs, the stock ribbon still appears — just in its
  // default spot — rather than breaking the viewer.
  _setupTakeoffRibbon() {
    const { UI } = this._instance
    try {
      UI.enableFeatures([UI.Feature.Measurement])
    } catch (e) {
      console.warn("[action-item-pdf-viewer] Could not enable Measurement:", e)
      return
    }

    // The ribbon's visible label comes from i18n — override the translation
    // so every render (including re-layouts) says "Takeoff".
    try {
      UI.setTranslations("en", { "option.toolbarGroup.toolbarGroup-Measure": "Takeoff" })
    } catch (_) { /* falls back to the stock "Measure" label */ }

    // Reorder the ribbon group so Takeoff lands right after Forms.
    try {
      const header = typeof UI.getModularHeader === "function" ? UI.getModularHeader("default-top-header") : null
      const items = header && typeof header.getItems === "function" ? header.getItems() : []
      const group = (items || []).find(it => it && it.dataElement === "default-ribbon-group")
      const ribbons = group && Array.isArray(group.items) ? group.items.slice() : []
      const mIdx = ribbons.findIndex(it => it && it.dataElement === "toolbarGroup-Measure")
      if (group && mIdx >= 0 && typeof group.setItems === "function") {
        const [measure] = ribbons.splice(mIdx, 1)
        if (measure.label) measure.label = "Takeoff"
        if (measure.title) measure.title = "Takeoff"
        const fIdx = ribbons.findIndex(it => it && it.dataElement === "toolbarGroup-Forms")
        ribbons.splice(fIdx >= 0 ? fIdx + 1 : ribbons.length, 0, measure)
        group.setItems(ribbons)
      }
    } catch (e) {
      console.warn("[action-item-pdf-viewer] Takeoff ribbon reorder failed:", e)
    }

    // Mixpanel: count Takeoff usage. The viewer is host-agnostic, so the
    // project id comes from the page URL.
    try {
      const evt = UI.Events && UI.Events.TOOLBAR_GROUP_CHANGED
      if (evt) {
        UI.addEventListener(evt, (e) => {
          const groupName = (e && e.detail) || e
          if (groupName !== "toolbarGroup-Measure") return
          const m = window.location.pathname.match(/\/projects\/(\d+)/)
          track("Takeoff Tab Opened", { project_id: m ? parseInt(m[1], 10) : null })
        })
      }
    } catch (_) { /* tracking is best-effort */ }

    this._clearPersistedToolFill()
    this._enableMeasurementSnapping()
    this._installTakeoffToolGuard()
    // Rotation workaround RETIRED (Phelipe 2026-09-01, item 33): its vertex
    // re-conversion was itself the "line jumps to the far right" bug when
    // snap handed the tool an already-converted point. Factory measurement
    // behavior from here on; snapping stays on. The method remains below
    // for a fast restore if rotated sheets misbehave again.
    // this._fixMeasurementRotationBug()
    this._registerTakeoffDrawHandlers()
    this._setupTakeoffCleanupHooks()
    this._setupTakeoffAiBar()
  }

  // The modular UI sometimes drops a freshly-picked measurement tool back to
  // the default tool a couple of seconds after the FIRST activation that
  // follows a ribbon switch — with no user input involved (verified live
  // 2026-08-26: the revert is the tool button's own toggle handler firing
  // without any click; Phelipe's "first takeoff click does nothing"). Guard:
  // remember when the user last actually touched something; if the active
  // tool flips from a measurement tool to a default one with NO trusted
  // input in the preceding 400ms, put the user's tool back. A deliberate
  // toggle-off, Escape, or any real click marks input and passes through.
  _installTakeoffToolGuard() {
    const dv = this._instance.Core.documentViewer
    const DEFAULT_TOOLS = new Set(["AnnotationEdit", "Pan", "TextSelect", "AnnotationCreateTextHighlight"])
    this._tkLastInputAt = 0
    const mark = (e) => { if (e.isTrusted) this._tkLastInputAt = Date.now() }
    ;[window, this._snapUiRoot()].forEach((t) => {
      try {
        t.addEventListener("mousedown", mark, true)
        t.addEventListener("keydown", mark, true)
      } catch (_) { /* root variant without listeners */ }
    })
    dv.addEventListener("toolModeUpdated", (tool) => {
      try {
        const name = tool && tool.name
        if (name && this.MEASUREMENT_TOOL_NAMES.includes(name)) {
          this._tkMeasurePick = { name, at: Date.now(), rescues: this._tkMeasurePick?.name === name ? (this._tkMeasurePick.rescues || 0) : 0 }
          return
        }
        const pick = this._tkMeasurePick
        this._tkMeasurePick = null
        if (!pick || !DEFAULT_TOOLS.has(name)) return
        const phantom = Date.now() - this._tkLastInputAt > 400
        const recent = Date.now() - pick.at < 5000
        if (phantom && recent && (pick.rescues || 0) < 2) {
          setTimeout(() => {
            try {
              this._instance.UI.setToolMode(pick.name)
              this._tkMeasurePick = { name: pick.name, at: Date.now(), rescues: (pick.rescues || 0) + 1 }
            } catch (_) { /* tool gone — leave it */ }
          }, 60)
        }
      } catch (_) { /* guard is best-effort, never break tool switching */ }
    })
  }

  // Snap-to-geometry ON by default for every measurement tool (Larry
  // 2026-08-04). It shipped off and buried behind the style panel's palette
  // icon — Larry couldn't find it and neither could Phelipe, live on the
  // call. Applied at setup and re-applied when the Takeoff ribbon is picked,
  // since a tool created later starts from its own defaults. Entirely
  // best-effort: setSnapMode needs the full API, and any build without it
  // just keeps the old free-hand behavior.
  // Apryse 11.7.1 bug (verified live 2026-08-18): on ROTATED pages the
  // measurement create tools store raw viewer-space points — every other
  // tool converts through getPDFCoordinates. Real plan sheets are usually
  // rotated (C-1 here is /Rotate 270), so takeoff clicks landed off-page and
  // drew nothing (Larry's "area measurement does nothing"). Convert each
  // vertex as it lands, and re-correct the finished shape as a safety net.
  // Remove when the vendored WebViewer is upgraded past this fix.
  MEASUREMENT_SUBJECTS = ["Area Measurement", "Perimeter Measurement", "Distance Measurement",
                          "Arc Measurement", "Ellipse Measurement", "Rectangular Area Measurement",
                          "Cloudy Rectangular Area Measurement"]

  _fixMeasurementRotationBug() {
    const { Core } = this._instance
    const dv = Core.documentViewer
    const pageRotated = (page) => {
      try { return (dv.getDocument()?.getPageRotation(page) || 0) % 360 !== 0 } catch (_) { return false }
    }
    const toPdf = (page, x, y) => {
      try { return dv.getDocument().getPDFCoordinates(page, x, y) } catch (_) { return null }
    }
    // A converted vertex must land ON the page. A point that was already
    // in PDF space (snap can hand the tool a fresh, untagged point)
    // converts AGAIN to roughly a page-dimension away — the takeoff line
    // that "jumped way to the right" (Larry 2026-08-26). Out-of-box
    // results are dropped and the original coordinate kept.
    const sane = (page, c) => {
      try {
        const info = dv.getDocument().getPageInfo(page)
        if (!info || !info.width || !info.height) return true
        const bound = Math.max(info.width, info.height)
        const margin = bound * 0.08
        return c.x >= -margin && c.y >= -margin && c.x <= bound + margin && c.y <= bound + margin
      } catch (_) { return true }
    }

    // Click-built tools (polygon/polyline): fix the vertex the click just
    // appended so the preview and the final shape are both right. Points are
    // tagged so a vertex is never converted twice.
    ;["AnnotationCreateAreaMeasurement", "AnnotationCreatePerimeterMeasurement"].forEach((name) => {
      const tool = dv.getTool(name)
      if (!tool || tool.__rotFix || typeof tool.mouseLeftDown !== "function") return
      tool.__rotFix = true
      const down = tool.mouseLeftDown
      tool.mouseLeftDown = function(...args) {
        const r = down.apply(this, args)
        try {
          const a = this.annotation
          if (a && pageRotated(a.PageNumber)) {
            const path = typeof a.getPath === "function" ? a.getPath() : null
            if (path) path.forEach((pt) => {
              if (pt.__pdfFixed) return
              const c = toPdf(a.PageNumber, pt.x, pt.y)
              if (c && sane(a.PageNumber, c)) { pt.x = c.x; pt.y = c.y }
              pt.__pdfFixed = true
            })
          }
        } catch (_) { /* leave the raw point — completion pass still corrects */ }
        return r
      }
    })

    // Drag-built tools (distance/arc/ellipse/rect areas) redraw their whole
    // geometry every mousemove, so per-event fixes fight the tool. Correct
    // the committed shape once instead, the moment it's added.
    if (this._measRotFixListener) return
    this._measRotFixListener = (annotations, action, info) => {
      // Only freshly DRAWN shapes: imported XFDF is already in PDF space —
      // converting it again would walk saved takeoffs around the sheet.
      if (action !== "add" || (info && info.imported)) return
      annotations.forEach((a) => {
        try {
          if (!this.MEASUREMENT_SUBJECTS.includes(a.Subject) || a.__rotFixed) return
          if (!pageRotated(a.PageNumber)) return
          a.__rotFixed = true
          const path = typeof a.getPath === "function" ? a.getPath() : null
          let touched = false
          if (path && path.length) {
            path.forEach((pt) => {
              if (pt.__pdfFixed) return
              const c = toPdf(a.PageNumber, pt.x, pt.y)
              if (c && sane(a.PageNumber, c)) { pt.x = c.x; pt.y = c.y; touched = true }
              pt.__pdfFixed = true
            })
          } else if (typeof a.getStartPoint === "function" && typeof a.getEndPoint === "function") {
            const sp = a.getStartPoint(), ep = a.getEndPoint()
            const cs = toPdf(a.PageNumber, sp.x, sp.y), ce = toPdf(a.PageNumber, ep.x, ep.y)
            if (cs && ce && sane(a.PageNumber, cs) && sane(a.PageNumber, ce)) {
              a.setStartPoint(cs.x, cs.y); a.setEndPoint(ce.x, ce.y); touched = true
            }
          }
          if (touched) {
            if (typeof a.adjustRect === "function") a.adjustRect()
            Core.annotationManager.redrawAnnotation(a)
          }
        } catch (_) { /* never break annotation add for other listeners */ }
      })
    }
    Core.annotationManager.addEventListener("annotationChanged", this._measRotFixListener)
  }

  _enableMeasurementSnapping() {
    const apply = () => {
      try {
        const { Core } = this._instance
        // NEVER arm snapping without a loaded document. setSnapMode boots the
        // snap engine, whose init reads getDocument() — against null it
        // crashes inside a promise ("Cannot read ... 'ug'"), the boot promise
        // wedges forever, and the FIRST measurement-tool activation dies with
        // it (Phelipe 2026-08-26: the area pentagon's first click did
        // nothing, and snapping never engaged). Setup-time calls therefore
        // no-op here; the documentLoaded hook below does the real arming.
        if (!Core.documentViewer.getDocument()) return
        const modes = Core.Tools?.SnapModes || Core.documentViewer?.SnapMode
        const mode = modes && (modes.DEFAULT ?? modes.ALL)
        if (mode == null) return
        ;[...this.MEASUREMENT_TOOL_NAMES, ...this.SNAP_SHAPE_TOOL_NAMES].forEach((name) => {
          try {
            const tool = Core.documentViewer.getTool(name)
            if (!tool || typeof tool.setSnapMode !== "function") return
            // Idempotence matters: setSnapMode fires TOOL_UPDATED when the
            // engine finishes booting (~2s), and a burst of those makes the
            // UI drop the user's just-picked tool back to AnnotationEdit —
            // the "first takeoff click does nothing" Phelipe hit 2026-08-26.
            // Re-applies (ribbon entry etc.) must therefore no-op once armed.
            try { if (tool.getSnapMode() === mode) return } catch (_) { /* fall through to set */ }
            tool.setSnapMode(mode)
          } catch (_) { /* tool absent in this build */ }
        })
        // Keep the style panel's "Enable snapping" checkbox honest — it reads
        // its own state, so without this it would show unchecked while
        // snapping is actually on.
        this._syncSnapCheckbox()
      } catch (_) { /* viewer not ready — the document hook retries */ }
    }
    apply()
    try {
      // Every sheet tab loads a fresh document into the same viewer — re-arm
      // on each load. Hooked once.
      if (!this._snapDocHooked) {
        this._snapDocHooked = true
        this._instance.Core.documentViewer.addEventListener("documentLoaded", () => setTimeout(apply, 0))
      }
    } catch (_) { /* no viewer yet — ribbon hook still covers us */ }
    try {
      const evt = this._instance.UI.Events && this._instance.UI.Events.TOOLBAR_GROUP_CHANGED
      if (evt && !this._snapRibbonHooked) {
        this._snapRibbonHooked = true
        this._instance.UI.addEventListener(evt, (e) => {
          const groupName = (e && e.detail) || e
          if (groupName === "toolbarGroup-Measure") setTimeout(apply, 0)
        })
      }
    } catch (_) { /* no ribbon events in this build */ }
  }

  // WebViewer 11 renders into a SHADOW ROOT, not an iframe (Larry
  // 2026-08-21: snapping WAS on, but this looked in the wrong document, so
  // the box always rendered unchecked — and ticking it turned snapping
  // OFF). Same resolution _positionFloatingNav uses.
  _snapUiRoot() {
    const host = this.hasViewerContainerTarget ? this.viewerContainerTarget : this.element
    return host?.querySelector("apryse-webviewer")?.shadowRoot ||
           this._instance?.UI?.iframeWindow?.document ||
           document
  }

  _syncSnapCheckbox() {
    const box = this._snapUiRoot()
      ?.querySelector('[data-element="measurement-snapping"] input[type="checkbox"]')
    if (box && !box.checked) box.click()
  }

  // One-time-per-browser reset of persisted FILL styles on the area tools —
  // Apryse paints a tool's saved fill onto its ribbon icon (the purple
  // pentagon / hatched circle+square Phelipe flagged 2026-08-01, saved from
  // earlier demo styling). Retries because Apryse restores saved styles
  // slightly after init. New shapes still get their 35% fill per-annotation
  // (_applyDefaultAreaFill), so clearing the tools loses no behavior.
  // A saved ink markup can carry an <apref> — a pointer into an "appearance
  // document" (Bluebeam-style embedded appearance) that isn't part of this
  // file. Importing one wedges page paint in an endless "No matching
  // annotation in appearance document" retry and the sheet stays blank white
  // (Phelipe 2026-09-01, C-1 on project 35). Drop the pointer before import;
  // Apryse redraws the stroke from its own inklist/properties instead.
  _stripBrokenAppearanceRefs(xfdf) {
    try {
      return xfdf
        .replace(/<apref\b[^>]*\/>/g, "")
        .replace(/<apref\b[^>]*>[\s\S]*?<\/apref>/g, "")
        // A measurement saved with a factor-less <numberformat> (seen on
        // Keith's 2026-07-29 line on C-3) yields a half-built Scale object
        // that crashes the Takeoff scale panel. A factor of 1 restores the
        // shape without changing what the caption already displays.
        .replace(/<numberformat(?![^>]*\bfactor=)/g, '<numberformat factor="1"')
    } catch (_) { return xfdf }
  }

  // Belt-and-suspenders for the crash class we cannot enumerate keys for
  // (Phelipe 2026-09-01: his Chrome carried persisted scale state under a
  // name our cleaner didn't cover; ScaleSelector's getScalePrecision read
  // .value of undefined and React tore the whole viewer UI down — empty
  // toolbar, white body, on every open). The moment that exact crash
  // signature fires, delete every stored key that smells like Apryse
  // scale/tool state — matched by NAME or by VALUE fingerprint, so the key's
  // name no longer matters — and reload once (session-flagged; a second
  // crash after reset just logs).
  _installScaleCrashTrap() {
    if (window.__tkScaleCrashTrap) return
    window.__tkScaleCrashTrap = true
    window.addEventListener("error", (e) => {
      try {
        const sig = `${e.message || ""} ${(e.error && e.error.stack) || ""} ${e.filename || ""}`
        if (!/reading 'value'/.test(sig)) return
        if (!/ScaleSelector|getScalePrecision|webviewer/i.test(sig)) return
        if (sessionStorage.getItem("tk_scale_trap_v1")) {
          console.error("[pdf-viewer] scale crash persists after settings reset — leaving state alone")
          return
        }
        sessionStorage.setItem("tk_scale_trap_v1", "1")
        const doomed = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (!key) continue
          const raw = localStorage.getItem(key) || ""
          const nameHit = /^(toolData-|lastPicked|toolbarGroup|activeGroup|webviewer|scale|measurement|customScales)/i.test(key)
          const valueHit = /"(Scale|pageScale|worldScale|PrecisionValue|MeasurementUnits)"/.test(raw)
          if (nameHit || valueHit || key === "tk_tool_fill_reset_v2") doomed.push(key)
        }
        doomed.forEach((k) => localStorage.removeItem(k))
        console.warn("[pdf-viewer] scale crash trapped — reset", doomed.length, "persisted viewer keys, reloading")
        window.location.reload()
      } catch (_) { /* storage unavailable */ }
    })
  }

  // A malformed persisted measurement SCALE white-screens the whole viewer
  // (Phelipe 2026-09-01: ScaleSelector's getScalePrecision reads .value of
  // undefined and the UI render loop dies — his session had a bad scale in
  // localStorage, a fresh browser didn't). Factory-reset any persisted
  // Apryse tool entry whose Scale doesn't parse into the shape the UI
  // expects; healthy entries are left alone.
  _clearBrokenPersistedScales() {
    try {
      const bad = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (!key) continue
        const raw = localStorage.getItem(key)
        if (!raw || !raw.includes('"Scale"')) continue
        try {
          if (this._hasBrokenScale(JSON.parse(raw))) bad.push(key)
        } catch (_) { if (/^toolData-/.test(key)) bad.push(key) }
      }
      bad.forEach((k) => localStorage.removeItem(k))
      if (bad.length) console.warn("[pdf-viewer] cleared broken persisted scale on:", bad.join(", "))
    } catch (_) { /* localStorage unavailable */ }
  }

  // Walk a parsed persisted blob (any Apryse key, not just toolData-*) and
  // report whether ANY nested `Scale` property fails the shape the UI needs:
  // [[num, unit], [num, unit]] or {pageScale:{value:num}, worldScale:{value:num}}.
  _hasBrokenScale(node, depth = 0) {
    if (!node || typeof node !== "object" || depth > 4) return false
    const pairOk = (p) => Array.isArray(p) && p.length === 2 && typeof p[0] === "number" && isFinite(p[0]) && typeof p[1] === "string"
    for (const [k, sc] of Object.entries(node)) {
      if (k === "Scale" && sc != null) {
        const arrayForm = Array.isArray(sc) && sc.length === 2 && pairOk(sc[0]) && pairOk(sc[1])
        const objForm = sc && typeof sc === "object" && !Array.isArray(sc) &&
          sc.pageScale && sc.worldScale &&
          typeof sc.pageScale.value === "number" && isFinite(sc.pageScale.value) &&
          typeof sc.worldScale.value === "number" && isFinite(sc.worldScale.value)
        if (!arrayForm && !objForm) return true
      } else if (sc && typeof sc === "object" && this._hasBrokenScale(sc, depth + 1)) {
        return true
      }
    }
    return false
  }

  // Last-resort self-heal (Phelipe 2026-09-01, round 3): corrupt persisted
  // viewer state his Chrome kept for this site crashed Apryse's React UI so
  // hard that NO toolbar/ribbon ever mounted — an all-white body under a
  // healthy page header, surviving refresh after refresh. If the modular
  // header hasn't appeared shortly after boot, factory-reset every Apryse
  // localStorage key and reload the page ONCE (session-flagged so a truly
  // broken build can't reload-loop).
  _ribbonWatchdog() {
    setTimeout(() => {
      try {
        const el = (this.element || document).querySelector("apryse-webviewer")
        const root = (el && el.shadowRoot) || null
        const healthy = !!(root && root.querySelector('[class*="ModularHeader"], [data-element="default-top-header"], [class*="Ribbon"]'))
        if (healthy) { sessionStorage.removeItem("tk_ui_selfheal_v1"); return }
        if (sessionStorage.getItem("tk_ui_selfheal_v1")) {
          console.error("[pdf-viewer] viewer UI still failed to mount after a settings reset — not retrying")
          return
        }
        sessionStorage.setItem("tk_ui_selfheal_v1", "1")
        const doomed = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (!key) continue
          const raw = localStorage.getItem(key) || ""
          if (/^toolData-/.test(key) || /^(lastPickedTool|toolbarGroup|activeGroup|webviewer)/i.test(key) ||
              raw.includes('"Scale"') || key === "tk_tool_fill_reset_v2") doomed.push(key)
        }
        doomed.forEach((k) => localStorage.removeItem(k))
        console.warn("[pdf-viewer] viewer UI never mounted — reset", doomed.length, "persisted viewer keys, reloading")
        window.location.reload()
      } catch (_) { /* storage unavailable — nothing to heal */ }
    }, 8000)
  }

  _clearPersistedToolFill() {
    this._clearBrokenPersistedScales()
    const attempt = () => {
      try {
        const { Core } = this._instance
        this.AREA_TOOL_NAMES.forEach((name) => {
          try {
            const tool = Core.documentViewer.getTool(name)
            const f = tool && tool.defaults && tool.defaults.FillColor
            const hasFill = f && typeof f.A === "number" && f.A > 0.01
            if (hasFill) tool.setStyles({ FillColor: new Core.Annotations.Color(255, 255, 255, 0) })
          } catch (_) { /* tool absent in this build */ }
        })
      } catch (_) { /* viewer not ready — a later retry covers it */ }
    }
    try {
      if (localStorage.getItem("tk_tool_fill_reset_v2")) return
      localStorage.setItem("tk_tool_fill_reset_v2", "1")
    } catch (_) { /* localStorage unavailable — run once per page instead */ }
    attempt()
    setTimeout(attempt, 1500)
    setTimeout(attempt, 4000)
  }

  // ── Takeoff round 2 (Larry 2026-07-31, items 16-20) ─────────────────────

  AREA_TOOL_NAMES = ["AnnotationCreateAreaMeasurement", "AnnotationCreateRectangularAreaMeasurement",
                     "AnnotationCreateCloudyRectangularAreaMeasurement", "AnnotationCreateEllipseMeasurement"]

  // Every tool on the Takeoff ribbon that can snap to the drawing's geometry.
  MEASUREMENT_TOOL_NAMES = ["AnnotationCreateDistanceMeasurement", "AnnotationCreatePerimeterMeasurement",
                            "AnnotationCreateArcMeasurement", "AnnotationCreateAreaMeasurement",
                            "AnnotationCreateRectangularAreaMeasurement",
                            "AnnotationCreateCloudyRectangularAreaMeasurement",
                            "AnnotationCreateEllipseMeasurement"]

  // Plain drawing tools (Shapes ribbon) that snap to the sheet's geometry
  // too — Larry 2026-09-15: "their snap is really, really good" is about
  // drawing markups, not only takeoffs.
  SNAP_SHAPE_TOOL_NAMES = ["AnnotationCreatePolygon", "AnnotationCreatePolygonCloud", "AnnotationCreatePolyline",
                           "AnnotationCreateLine", "AnnotationCreateArrow", "AnnotationCreateRectangle",
                           "AnnotationCreateEllipse"]

  // Tools that place one point per click — where Backspace drops the last
  // point (Larry 2026-09-15 / sheet row 22).
  POINT_TOOL_NAMES = ["AnnotationCreatePolygon", "AnnotationCreatePolygonCloud", "AnnotationCreatePolyline",
                      "AnnotationCreatePerimeterMeasurement", "AnnotationCreateAreaMeasurement"]

  // Item 16: area takeoffs come out filled (stroke color at ~35% opacity)
  // instead of outline-only. Applied to each freshly DRAWN annotation, not
  // to the tool defaults — styling the tool recolors its ribbon icon
  // (Phelipe 2026-08-01: "revert this icon"), per-annotation styling doesn't.
  _applyDefaultAreaFill(a) {
    try {
      const { Annotations } = this._instance.Core
      const fill = a.FillColor
      const hasFill = fill && typeof fill.A === "number" && fill.A > 0.01
      if (hasFill) return
      const s = a.StrokeColor
      a.FillColor = s ? new Annotations.Color(s.R, s.G, s.B, 0.35)
                      : new Annotations.Color(230, 126, 34, 0.35)
      this._instance.Core.documentViewer.getAnnotationManager().redrawAnnotation(a)
    } catch (e) { /* fill is cosmetic — never break the draw */ }
  }

  // Items 17 + 18 + 20 rendering: custom draw handlers. Hatch fill (45°
  // lines clipped to the shape) when tk_hatch=1; cutout holes render as
  // paper-white patches with a dashed border; count pins honor a chosen
  // symbol. All persisted via annotation customData (rides along in XFDF).
  _registerTakeoffDrawHandlers() {
    const { Core } = this._instance
    const A = Core.Annotations
    // WebViewer 11 names the hook setCustomDrawHandler (singular); older
    // builds had the plural. The plural-only guard here meant NOTHING below
    // ever registered on 11 — hatch and cutout shapes painted plain
    // (found 2026-09-16 while adding Larry's label options).
    const register = A && (A.setCustomDrawHandler || A.setCustomDrawHandlers)
    if (typeof register !== "function") return
    const self = this

    const hatchIn = (ctx, buildPath, stroke) => {
      ctx.save()
      buildPath()
      ctx.clip()
      ctx.strokeStyle = `rgba(${stroke.R || 0}, ${stroke.G || 0}, ${stroke.B || 0}, 0.55)`
      ctx.lineWidth = 0.75
      const box = ctx._tkBox
      const span = box.w + box.h
      ctx.beginPath()
      for (let off = -box.h; off < span; off += 7) {
        ctx.moveTo(box.x + off, box.y)
        ctx.lineTo(box.x + off + box.h, box.y + box.h)
      }
      ctx.stroke()
      ctx.restore()
    }

    const polyDraw = function (ctx, pageMatrix, rotation, options) {
      // A label option (hidden label, side lengths, text size/color) means
      // we paint the shape AND its text ourselves — Apryse's painter has no
      // hooks for the caption. Cloudy outlines stay with Apryse.
      if (self._tkOwnsPainting(this)) {
        try { self._tkPaintPath(ctx, this, true, hatchIn); return } catch (_) { /* fall back to Apryse */ }
      }
      options.originalDraw(ctx, pageMatrix, rotation)
      try {
        if (this.getCustomData("tk_cutout_parent")) {
          const path = this.getPath() || []
          if (path.length < 3) return
          ctx.save()
          ctx.beginPath()
          ctx.moveTo(path[0].x, path[0].y)
          for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].y)
          ctx.closePath()
          ctx.fillStyle = "rgba(255,255,255,0.92)"
          ctx.fill()
          ctx.setLineDash([4, 3])
          const s = this.StrokeColor || {}
          ctx.strokeStyle = `rgba(${s.R || 0}, ${s.G || 0}, ${s.B || 0}, 0.9)`
          ctx.lineWidth = 1
          ctx.stroke()
          ctx.restore()
          return
        }
        if (this.getCustomData("tk_hatch") === "1") {
          const path = this.getPath() || []
          if (path.length < 3) return
          const xs = path.map(p => p.x), ys = path.map(p => p.y)
          ctx._tkBox = { x: Math.min(...xs), y: Math.min(...ys),
                         w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) }
          hatchIn(ctx, () => {
            ctx.beginPath()
            ctx.moveTo(path[0].x, path[0].y)
            for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].y)
            ctx.closePath()
          }, this.StrokeColor || {})
        }
      } catch (_) { /* never break annotation painting */ }
    }

    const rectDraw = function (ctx, pageMatrix, rotation, options) {
      options.originalDraw(ctx, pageMatrix, rotation)
      try {
        if (this.getCustomData("tk_hatch") !== "1") return
        ctx._tkBox = { x: this.X, y: this.Y, w: this.Width, h: this.Height }
        hatchIn(ctx, () => { ctx.beginPath(); ctx.rect(this.X, this.Y, this.Width, this.Height) },
          this.StrokeColor || {})
      } catch (_) { /* keep painting */ }
    }

    const ellipseDraw = function (ctx, pageMatrix, rotation, options) {
      options.originalDraw(ctx, pageMatrix, rotation)
      try {
        if (this.getCustomData("tk_hatch") !== "1") return
        const cx = this.X + this.Width / 2, cy = this.Y + this.Height / 2
        ctx._tkBox = { x: this.X, y: this.Y, w: this.Width, h: this.Height }
        hatchIn(ctx, () => { ctx.beginPath(); ctx.ellipse(cx, cy, this.Width / 2, this.Height / 2, 0, 0, Math.PI * 2) },
          this.StrokeColor || {})
      } catch (_) { /* keep painting */ }
    }

    // Item 20: count marks drawn with the user's chosen symbol (when one is
    // set on the annotation) instead of the stock check glyph.
    const countDraw = function (ctx, pageMatrix, rotation, options) {
      const sym = (this.getCustomData && this.getCustomData("tk_symbol")) || ""
      if (!sym) return options.originalDraw(ctx, pageMatrix, rotation)
      try {
        const s = this.StrokeColor || {}
        const cx = this.X + (this.Width || 12) / 2
        const cy = this.Y + (this.Height || 12) / 2
        const size = Math.max(10, Math.max(this.Width || 12, this.Height || 12))
        ctx.save()
        ctx.fillStyle = `rgba(${s.R ?? 220}, ${s.G ?? 38}, ${s.B ?? 38}, 1)`
        ctx.font = `${size}px sans-serif`
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(sym, cx, cy)
        ctx.restore()
      } catch (_) { options.originalDraw(ctx, pageMatrix, rotation) }
    }

    const polylineDraw = function (ctx, pageMatrix, rotation, options) {
      if (self._tkOwnsPainting(this)) {
        try { self._tkPaintPath(ctx, this, false, hatchIn); return } catch (_) { /* fall back to Apryse */ }
      }
      options.originalDraw(ctx, pageMatrix, rotation)
    }

    try { register.call(A, A.PolygonAnnotation, polyDraw) } catch (_) { /* class may differ */ }
    try { if (A.PolylineAnnotation) register.call(A, A.PolylineAnnotation, polylineDraw) } catch (_) { /* class may differ */ }
    try { register.call(A, A.RectangleAnnotation, rectDraw) } catch (_) { /* class may differ */ }
    try { register.call(A, A.EllipseAnnotation, ellipseDraw) } catch (_) { /* class may differ */ }
    try { if (A.CountMeasurementAnnotation) register.call(A, A.CountMeasurementAnnotation, countDraw) } catch (_) { /* class may differ */ }
  }

  // Deleting a takeoff parent cleans up its cutouts + segment labels;
  // moving/resizing re-applies the cutout subtraction on the caption.
  _setupTakeoffCleanupHooks() {
    const { Core } = this._instance
    const am = Core.documentViewer.getAnnotationManager()
    am.addEventListener("annotationChanged", (annots, action, info) => {
      try {
        if (action === "add") {
          // Only freshly DRAWN annotations — imported ones (loading a saved
          // document's XFDF) keep whatever style they were saved with.
          const imported = info && info.imported
          annots.forEach(a => {
            // Armed cutout: the polygon just drawn becomes the hole.
            if (!imported && this._tkCutoutParentId) {
              const A = this._instance.Core.Annotations
              if (A.PolygonAnnotation && a instanceof A.PolygonAnnotation) {
                this._tkFinishCutout(a)
                return
              }
            }
            // Item 16: new area takeoffs come out filled (per-annotation so
            // the ribbon icon keeps its stock look).
            if (!imported && this._isTakeoffArea(a)) this._applyDefaultAreaFill(a)
            // Chosen count symbol carries onto every new count mark.
            const CountA = this._instance.Core.Annotations.CountMeasurementAnnotation
            if (!imported && this._tkCountSymbol && CountA && a instanceof CountA && !a.getCustomData("tk_symbol")) {
              a.setCustomData("tk_symbol", this._tkCountSymbol)
              am.redrawAnnotation(a)
            }
          })
        } else if (action === "delete") {
          const ids = annots.map(a => a.Id)
          const orphans = am.getAnnotationsList().filter(a =>
            ids.includes(a.getCustomData("tk_seg_parent")) || ids.includes(a.getCustomData("tk_cutout_parent")))
          if (orphans.length) am.deleteAnnotations(orphans, { imported: false, force: true })
        } else if (action === "modify") {
          annots.forEach(a => {
            if (this._isTakeoffArea(a)) this._applyCutoutSubtraction(a)
            const parentId = a.getCustomData && a.getCustomData("tk_cutout_parent")
            if (parentId) {
              const parent = am.getAnnotationById(parentId)
              if (parent) this._applyCutoutSubtraction(parent)
            }
          })
        }
      } catch (_) { /* hooks must never break editing */ }
    })
  }

  _isTakeoffArea(a) {
    const A = this._instance.Core.Annotations
    if (a.getCustomData && a.getCustomData("tk_cutout_parent")) return false
    return (a instanceof A.PolygonAnnotation || a instanceof A.RectangleAnnotation ||
            a instanceof A.EllipseAnnotation) && !!a.Measure
  }

  // Shoelace area of a point path (page units — points²).
  _tkShoelace(path) {
    let sum = 0
    for (let i = 0; i < path.length; i++) {
      const p = path[i], q = path[(i + 1) % path.length]
      sum += (p.x * q.y) - (q.x * p.y)
    }
    return Math.abs(sum) / 2
  }

  _tkAreaOf(a) {
    const path = (typeof a.getPath === "function" && a.getPath()) || []
    if (path.length >= 3) return this._tkShoelace(path)
    if (a.Width && a.Height) {
      const A = this._instance.Core.Annotations
      if (a instanceof A.EllipseAnnotation) return Math.PI * (a.Width / 2) * (a.Height / 2)
      return a.Width * a.Height
    }
    return 0
  }

  // points² → scaled unit² factor + label, from the annotation's Measure
  // dictionary (written by Apryse when the tool is calibrated).
  _tkAreaFactor(a) {
    try {
      const m = a.Measure
      const axis = m && m.axis && m.axis[0] ? Number(m.axis[0].factor) : null
      const area = m && m.area && m.area[0] ? Number(m.area[0].factor) : null
      const unit = (m && ((m.area && m.area[0] && m.area[0].unit) || (m.axis && m.axis[0] && m.axis[0].unit))) || ""
      if (area && isFinite(area)) return { factor: area, unit }
      if (axis && isFinite(axis)) return { factor: axis * axis, unit: unit ? `${unit}²` : "" }
    } catch (_) { /* fall through */ }
    return null
  }

  _tkAxisFactor(a) {
    try {
      const m = a.Measure
      const axis = m && m.axis && m.axis[0] ? Number(m.axis[0].factor) : null
      // Apryse leaves the axis unit blank on area tools — the world unit of
      // the Scale ("ft" in 1 in = 8 ft) is what a side length is in.
      const unit = (m && m.axis && m.axis[0] && m.axis[0].unit) || (a.Scale && a.Scale[1] && a.Scale[1][1]) || ""
      if (axis && isFinite(axis)) return { factor: axis, unit }
    } catch (_) { /* fall through */ }
    return null
  }

  // Item 18: net caption = own area minus every linked cutout hole.
  _applyCutoutSubtraction(parent) {
    try {
      const am = this._instance.Core.documentViewer.getAnnotationManager()
      const holes = am.getAnnotationsList().filter(h =>
        h.getCustomData && h.getCustomData("tk_cutout_parent") === parent.Id)
      if (!holes.length) return
      const conv = this._tkAreaFactor(parent)
      if (!conv) return
      const net = Math.max(0, this._tkAreaOf(parent) - holes.reduce((s, h) => s + this._tkAreaOf(h), 0))
      const val = net * conv.factor
      const label = `${val.toFixed(2)} ${conv.unit}`.trim()
      if (parent.getContents() !== label) {
        parent.setContents(label)
        am.redrawAnnotation(parent)
      }
    } catch (_) { /* caption update is best-effort */ }
  }

  // ── AI takeoff (Phelipe 2026-07-28): a small purple bar appears while the
  // Takeoff ribbon is active. "AI scale" reads the sheet's scale statement
  // and calibrates every measure tool; "AI count" takes one boxed example
  // symbol and pins every match. Review-first: pins are normal annotations
  // the user deletes/adds, nothing saves without them. ────────────────────
  _setupTakeoffAiBar() {
    const { UI } = this._instance
    if (!this._takeoffProjectId()) return
    try {
      const evt = UI.Events && UI.Events.TOOLBAR_GROUP_CHANGED
      if (evt) {
        UI.addEventListener(evt, (e) => {
          const groupName = (e && e.detail) || e
          this._toggleTakeoffAiBar(String(groupName).includes("Measure"))
        })
      }
    } catch (_) { /* fall through to polling */ }
    // Belt-and-braces: WebViewer 11's modular UI doesn't always emit the
    // legacy toolbar-group event, so poll the active ribbon too.
    if (!this._aiTkPoll) this._aiTkPoll = setInterval(() => this._syncTakeoffAiBar(), 1000)
  }

  _installArrowForwarding() {
    if (this._arrowFwd) {
      // Re-attach to the fresh iframe after a document/instance reload.
      try { this._instance.UI.iframeWindow.addEventListener("keydown", this._arrowFwd) } catch (_) { /* same-window build */ }
      return
    }
    this._arrowFwd = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      // Another handler (the specs page's own host-side keydown) already
      // navigated on this keystroke — don't page twice.
      if (e.defaultPrevented) return
      const t = e.target
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return
      if (this.hasOverlayTarget && this.overlayTarget.classList.contains("hidden")) return
      // A side-by-side compare open on top of the viewer (K7-20) owns the
      // keys — don't page the sheet hidden underneath it.
      if (document.querySelector('[data-controller~="pdf-compare"]:not(.hidden)')) return
      const name = e.key === "ArrowLeft" ? "viewerPrevBtn" : "viewerNextBtn"
      // Clicking the host page's own Prev/Next button reuses its exact
      // in-place swap — keyboard behaves identically to the arrow buttons.
      // Both list pages expose the same target name: Drawings & Specs AND
      // Specifications (standard drawings included — that page's buttons
      // were missed before, so arrows only worked on the drawings tab).
      const btn = document.querySelector(
        `[data-drawings-specs-target~="${name}"], [data-specifications-target~="${name}"], [data-design-step-target~="${name}"]`
      )
      if (!btn || btn.disabled) return
      e.preventDefault()
      btn.click()
    }
    window.addEventListener("keydown", this._arrowFwd)
    try { this._instance.UI.iframeWindow.addEventListener("keydown", this._arrowFwd) } catch (_) { /* same-window build */ }
  }

  _syncTakeoffAiBar() {
    try {
      if (!this._instance) return
      const { UI } = this._instance
      let group = null
      if (typeof UI.getToolbarGroup === "function") group = UI.getToolbarGroup()
      if (!group && typeof UI.getActiveRibbonItem === "function") {
        const item = UI.getActiveRibbonItem()
        group = (item && (item.dataElement || item.toolbarGroup)) || item
      }
      this._toggleTakeoffAiBar(String(group || "").includes("Measure"))
    } catch (_) { /* keep last state */ }
  }

  _takeoffProjectId() {
    const m = window.location.pathname.match(/\/projects\/(\d+)/)
    return m ? m[1] : null
  }

  _toggleTakeoffAiBar(show) {
    // Never hide mid-flow (armed or scanning) — tool switches can briefly
    // flip the active ribbon and the bar must not vanish under the user.
    if (!show && (this._aiTkArmed || this._aiTkBusy)) return
    if (!show) {
      if (this._aiTkBar) this._aiTkBar.style.display = "none"
      return
    }
    if (!this._aiTkBar) this._buildTakeoffAiBar()
    this._aiTkBar.style.display = "flex"
  }

  // Obvious floating Prev / Next / Close, bottom-right of the sheet (Larry
  // 2026-08-12: his engineer never found the small header arrows and kept
  // using the browser back button). The header buttons STAY — this is an
  // extra, louder pair that clicks them, so all three host pages share one
  // code path and their disabled/hidden state carries over.
  _buildFloatingNav() {
    if (this._floatNav) return
    const host = this.hasViewerContainerTarget ? this.viewerContainerTarget : this.element
    if (getComputedStyle(host).position === "static") host.style.position = "relative"

    const hostBtn = (name) => document.querySelector(
      `[data-drawings-specs-target~="${name}"], [data-specifications-target~="${name}"], [data-design-step-target~="${name}"]`
    )
    // Only pages that HAVE prev/next get the floating pair (the punchlist
    // modal viewer doesn't).
    if (!hostBtn("viewerPrevBtn")) return

    // Sits OVER the sheet (semi-transparent so linework shows through), and
    // shifts left of the viewer's comments panel when that is open — the
    // pair must never cover the panel (Phelipe 2026-08-13).
    const wrap = document.createElement("div")
    wrap.style.cssText = "position:absolute;bottom:56px;right:24px;z-index:60;display:flex;gap:8px;transition:right .2s;"
    const mk = (label, title, onClick) => {
      const b = document.createElement("button")
      b.type = "button"
      b.title = title
      b.innerHTML = label
      b.style.cssText = "width:42px;height:42px;border-radius:9999px;background:#0f172a;color:#fff;" +
        "border:none;cursor:pointer;font-size:17px;line-height:1;display:flex;align-items:center;" +
        "justify-content:center;box-shadow:0 6px 18px rgba(0,0,0,.28);opacity:.3;transition:opacity .15s;"
      b.addEventListener("mouseenter", () => { if (!b.disabled) b.style.opacity = "1" })
      b.addEventListener("mouseleave", () => { if (!b.disabled) b.style.opacity = ".3" })
      b.addEventListener("click", onClick)
      return b
    }
    const clickHost = (name) => () => {
      const btn = hostBtn(name)
      if (btn && !btn.disabled) btn.click()
    }
    this._floatPrev = mk("&#8592;", "Previous sheet", clickHost("viewerPrevBtn"))
    this._floatNext = mk("&#8594;", "Next sheet", clickHost("viewerNextBtn"))
    wrap.append(this._floatPrev, this._floatNext)

    // The green "Send to ChatLog" button is parked for now (Phelipe
    // 2026-08-13) — _sendViewToChat and the server side stay, only the
    // launcher is gone.
    host.appendChild(wrap)
    this._floatNav = wrap

    // Never re-park the pair while the pointer is on it (Larry 2026-08-21:
    // "the arrow keeps moving on me" while clicking through the combined
    // set). Paging changes the sheet width, which moves the anchor.
    wrap.addEventListener("mouseenter", () => { this._floatNavHover = true })
    wrap.addEventListener("mouseleave", () => { this._floatNavHover = false; this._positionFloatingNav() })

    this._positionFloatingNav()

    // Mirror the header buttons' disabled state so the floating pair never
    // promises a page that isn't there, and keep the pair clear of the
    // comments panel as it opens and closes.
    this._floatNavSync = setInterval(() => {
      const sync = (float, name) => {
        const src = hostBtn(name)
        if (!src || !float) return
        const off = src.disabled
        float.disabled = off
        // .3 resting (Phelipe 2026-08-13 — the linework reads through it);
        // hover takes it to full, disabled fades further.
        float.style.opacity = off ? ".15" : ".3"
        float.style.cursor = off ? "default" : "pointer"
      }
      sync(this._floatPrev, "viewerPrevBtn")
      sync(this._floatNext, "viewerNextBtn")
      this._positionFloatingNav()
    }, 800)
  }

  // Park the pair at the bottom-right OF THE SHEET, not of the whole viewer
  // (Phelipe 2026-08-13): the comments panel lives inside the same Apryse
  // frame, so a plain right:24px sits on top of it. Measure the panel and
  // step left by its width whenever it's open.
  _positionFloatingNav() {
    if (!this._floatNav || this._floatNavHover) return

    const host = this.hasViewerContainerTarget ? this.viewerContainerTarget : this.element
    let offset = 24
    try {
      // WebViewer 11 renders into a SHADOW ROOT on <apryse-webviewer> — not
      // an iframe (older builds did, so both are handled). Shadow rects are
      // already viewport-relative; iframe rects need the frame's own left.
      const shadow = host.querySelector("apryse-webviewer")?.shadowRoot
      const frameWin = shadow ? null : this._instance?.UI?.iframeWindow
      const root = shadow || frameWin?.document
      const frameLeft = shadow
        ? 0
        : ((frameWin?.frameElement || host.querySelector("iframe"))?.getBoundingClientRect().left || 0)

      // Anchor to where the SHEET view ends rather than to the panel: the
      // document container shrinks when the comments panel opens, so this
      // one measurement covers both states (and any other side panel).
      const sheet = root?.querySelector('[data-element="documentContainer"]') ||
                    root?.querySelector(".DocumentContainer")
      if (sheet) {
        const gap = Math.round(host.getBoundingClientRect().right -
                               (frameLeft + sheet.getBoundingClientRect().right))
        if (gap > 8) offset = gap + 24
      }
      // Fallback: measure the notes panel itself if the container lookup
      // came up empty (markup drift between WebViewer versions).
      if (offset === 24) {
        const panel = root?.querySelector('[data-element="notesPanel"]') ||
                      root?.querySelector(".NotesPanel")
        const width = panel?.getBoundingClientRect().width || 0
        if (width > 40) offset = Math.round(width) + 24
      }
    } catch (_) { /* viewer not ready — the default corner still works */ }

    // Hysteresis (Larry 2026-08-21): a scrollbar coming and going as pages
    // of different widths render shifts the measurement a few px, which used
    // to slide the buttons out from under the cursor mid-click. Only a real
    // layout change — a panel opening — clears this threshold.
    if (this._floatNavOffset === undefined || Math.abs(this._floatNavOffset - offset) > 16) {
      this._floatNavOffset = offset
      this._floatNav.style.right = `${offset}px`
    }
  }

  // Render the current page (annotations included) to a PNG and post it as
  // an image message in the project room. The chat side then auto-files it
  // into Photos, same as any chat photo.
  async _sendViewToChat(btn) {
    if (this._sendingToChat) return
    const chatEl = document.querySelector('[data-controller~="toolbox-chat"]')
    const projectId = chatEl?.dataset?.toolboxChatProjectIdValue
    if (!projectId || !this._instance) return
    this._sendingToChat = true
    const prevLabel = btn.innerHTML
    btn.innerHTML = "…"

    try {
      const { documentViewer, annotationManager } = this._instance.Core
      const doc = documentViewer.getDocument()
      const pageNumber = documentViewer.getCurrentPage()
      const blob = await new Promise((resolve, reject) => {
        doc.loadCanvas({
          pageNumber,
          zoom: 1.5,
          drawComplete: async (canvas) => {
            try {
              await annotationManager.drawAnnotations({ pageNumber, overrideCanvas: canvas, majorRedraw: true })
              canvas.toBlob(b => (b ? resolve(b) : reject(new Error("empty canvas"))), "image/png")
            } catch (err) { reject(err) }
          }
        })
      })

      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const headers = { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf }
      const filename = `sheet-${pageNumber}-${Date.now()}.png`
      const presignResp = await fetch(`/projects/${projectId}/chat/presign_uploads`, {
        method: "POST", credentials: "same-origin", headers,
        body: JSON.stringify({ files: [{ filename, content_type: "image/png", byte_size: blob.size }] })
      })
      const presign = await presignResp.json().catch(() => ({}))
      if (!presignResp.ok || !presign.uploads?.[0]) throw new Error(presign.error || "upload refused")
      const up = presign.uploads[0]
      const putResp = await fetch(up.upload_url, { method: "PUT", headers: { "Content-Type": up.content_type }, body: blob })
      if (!putResp.ok) throw new Error("upload failed")

      const msgResp = await fetch(`/projects/${projectId}/chat/create_message`, {
        method: "POST", credentials: "same-origin", headers,
        body: JSON.stringify({ room: "project", kind: "image",
                               cache_name: up.cache_name, filename: up.filename })
      })
      if (!msgResp.ok) throw new Error((await msgResp.json().catch(() => ({}))).error || "send failed")
      this._toastMini("Sent to ChatLog — it's in Photos too.")
    } catch (err) {
      this._toastMini(err?.message || "Couldn't send that to ChatLog.", true)
    } finally {
      this._sendingToChat = false
      btn.innerHTML = prevLabel
    }
  }

  _toastMini(message, error = false) {
    const flash = document.createElement("div")
    flash.className = error ? "flash-toast-alert" : "flash-toast-notice"
    flash.setAttribute("data-controller", "flash")
    flash.setAttribute("data-flash-dismiss-after-value", "4000")
    flash.textContent = message
    document.body.prepend(flash)
  }

  _buildTakeoffAiBar() {
    // Lives inside the viewer container (the Apryse iframe's host) so it
    // renders above the full-screen overlay; top clears the ribbon + tool
    // rows drawn inside the iframe.
    const host = this.hasViewerContainerTarget ? this.viewerContainerTarget : this.element
    if (getComputedStyle(host).position === "static") host.style.position = "relative"
    const bar = document.createElement("div")
    bar.style.cssText = "position:absolute;top:52px;right:14px;z-index:60;" +
      "display:flex;align-items:center;gap:8px;background:#faf5ff;border:1px solid #e9d5ff;" +
      "border-radius:10px;padding:4px 8px;box-shadow:0 4px 14px rgba(0,0,0,.12);font-size:13px;"
    const mkBtn = (label) => {
      const b = document.createElement("button")
      b.type = "button"
      b.textContent = label
      b.style.cssText = "background:#9333ea;color:#fff;border:none;border-radius:8px;" +
        "padding:4px 10px;font-size:13px;font-weight:500;cursor:pointer;white-space:nowrap;"
      return b
    }
    this._aiTkScaleBtn = mkBtn("✨ AI scale")
    this._aiTkCountBtn = mkBtn("✨ AI count")
    this._aiTkStatus = document.createElement("span")
    this._aiTkStatus.style.cssText = "color:#6b21a8;max-width:300px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"
    this._aiTkStatus.textContent = "AI can read this sheet's scale for you."
    this._aiTkScaleBtn.addEventListener("click", () => this._aiReadScale())
    this._aiTkCountBtn.addEventListener("click", () => this._aiCountStart())
    this._aiTkUndoBtn = document.createElement("button")
    this._aiTkUndoBtn.type = "button"
    this._aiTkUndoBtn.style.cssText = "display:none;background:#fff;color:#9333ea;border:1px solid #d8b4fe;" +
      "border-radius:8px;padding:4px 10px;font-size:13px;font-weight:500;cursor:pointer;white-space:nowrap;"
    this._aiTkUndoBtn.addEventListener("click", () => this._aiUndoPins())

    // Hatch (pulled again, Phelipe 2026-08-04) / Cutout / Segments /
    // Count-symbol stay off the bar — their flows (_tkToggleHatch,
    // _tkStartCutout, _tkToggleSegments, _tkApplySymbol) remain wired for
    // when they return. Already-hatched shapes keep rendering via the draw
    // handlers.
    // AI count is parked (Phelipe 2026-07-28) — button not appended; the
    // whole flow stays in place for when it comes back.
    bar.append(this._aiTkScaleBtn, this._aiTkStatus)
    host.appendChild(bar)
    this._aiTkBar = bar
    this._styleTakeoffAiBar()
  }

  // The AI scale bar follows the viewer theme (Phelipe 2026-09-16: "when
  // dark mode is enabled make this dark mode as well").
  _styleTakeoffAiBar() {
    const bar = this._aiTkBar
    if (!bar) return
    const dark = this._pdfTheme === "dark"
    bar.style.background = dark ? "#2a2433" : "#faf5ff"
    bar.style.borderColor = dark ? "#5b3f80" : "#e9d5ff"
    if (this._aiTkStatus) {
      // Re-map whatever tone the status is showing to the theme's palette.
      this._aiTkStatus.style.color = this._tkTone(this._aiTkStatus.dataset.tone || this._aiTkStatus.style.color, dark)
    }
    if (this._aiTkUndoBtn) {
      this._aiTkUndoBtn.style.background = dark ? "#1f1a26" : "#fff"
      this._aiTkUndoBtn.style.color = dark ? "#d8b4fe" : "#9333ea"
      this._aiTkUndoBtn.style.borderColor = dark ? "#5b3f80" : "#d8b4fe"
    }
  }

  // Status tones: purple (info) / amber (warning) / red (error), in the
  // light or dark palette. Accepts either palette's value.
  _tkTone(color, dark) {
    const pairs = [["#6b21a8", "#d8b4fe"], ["#b45309", "#fbbf24"], ["#b91c1c", "#f87171"]]
    const hex = (color || "").trim().toLowerCase()
    const pair = pairs.find(([l, d]) => l === hex || d === hex) || pairs[0]
    return dark ? pair[1] : pair[0]
  }

  _tkSelectedAnnotation() {
    try {
      const am = this._instance.Core.documentViewer.getAnnotationManager()
      return (am.getSelectedAnnotations() || [])[0] || null
    } catch (_) { return null }
  }

  // Item 17 — hatch-pattern fill toggle on the selected area shape.
  _tkToggleHatch() {
    const a = this._tkSelectedAnnotation()
    if (!a || !this._isHatchable(a)) {
      return this._tkSay("Select a polygon, rectangle or ellipse first, then click Hatch.", "#b45309")
    }
    try {
      const am = this._instance.Core.documentViewer.getAnnotationManager()
      const on = a.getCustomData("tk_hatch") === "1"
      a.setCustomData("tk_hatch", on ? "" : "1")
      am.redrawAnnotation(a)
      am.trigger("annotationChanged", [[a], "modify", {}])
      this._tkSay(on ? "Hatch fill removed." : "Hatch fill on — adjust color/opacity in the style panel.")
      track("Takeoff Hatch Toggled", { project_id: this._takeoffProjectId(), on: !on })
    } catch (e) { this._tkSay("Could not toggle the hatch on that shape.", "#b91c1c") }
  }

  // Item 18 — polygon cutout: pick the parent area, then draw the hole.
  _tkStartCutout() {
    if (this._tkCutoutParentId) return this._tkCutoutCancel()
    const a = this._tkSelectedAnnotation()
    if (!a || !this._isTakeoffArea(a)) {
      return this._tkSay("Select the area to cut from first, then click Cutout.", "#b45309")
    }
    const { UI } = this._instance
    this._tkCutoutParentId = a.Id
    this._tkCutoutPrevTool = (() => { try { return UI.getToolMode()?.name } catch (_) { return null } })()
    this._tkBanner("Draw the cutout inside the area — double-click to close it (Esc cancels)")
    if (this._tkCutoutBtn) this._tkCutoutBtn.textContent = "Cancel cutout"
    try { UI.setToolMode("AnnotationCreatePolygon") } catch (e) {
      this._tkCutoutParentId = null
      if (this._tkCutoutBtn) this._tkCutoutBtn.textContent = "Cutout"
      this._tkBanner(null)
      return this._tkSay("The polygon tool isn't available in this viewer.", "#b91c1c")
    }
    if (!this._tkCutoutEsc) {
      this._tkCutoutEsc = (e) => { if (e.key === "Escape") this._tkCutoutCancel() }
      window.addEventListener("keydown", this._tkCutoutEsc)
      try { UI.iframeWindow.addEventListener("keydown", this._tkCutoutEsc) } catch (_) { /* same-window build */ }
    }
  }

  _tkCutoutCancel() {
    this._tkCutoutParentId = null
    this._tkBanner(null)
    if (this._tkCutoutBtn) this._tkCutoutBtn.textContent = "Cutout"
    try { this._instance.UI.setToolMode(this._tkCutoutPrevTool || "Pan") } catch (_) { /* keep tool */ }
  }

  _tkFinishCutout(hole) {
    const am = this._instance.Core.documentViewer.getAnnotationManager()
    const parent = am.getAnnotationById(this._tkCutoutParentId)
    this._tkCutoutParentId = null
    this._tkBanner(null)
    if (this._tkCutoutBtn) this._tkCutoutBtn.textContent = "Cutout"
    try { this._instance.UI.setToolMode(this._tkCutoutPrevTool || "Pan") } catch (_) { /* keep tool */ }
    if (!parent) return
    try {
      hole.setCustomData("tk_cutout_parent", parent.Id)
      const { Annotations } = this._instance.Core
      try { hole.FillColor = new Annotations.Color(255, 255, 255, 0.92) } catch (_) { /* draw handler paints it */ }
      am.redrawAnnotation(hole)
      this._applyCutoutSubtraction(parent)
      am.trigger("annotationChanged", [[hole], "modify", {}])
      this._tkSay("Cutout applied — the area total now excludes it.")
      track("Takeoff Cutout Added", { project_id: this._takeoffProjectId() })
    } catch (e) { this._tkSay("Could not apply that cutout.", "#b91c1c") }
  }

  // Item 19 — per-segment length labels on the selected perimeter/area.
  // Side lengths (Larry 2026-09-15: "it shows the distance between each
  // segment"). A flag on the shape; the painter labels every side from the
  // shape's own scale, so the labels move with the nodes and never become
  // stray annotations. Older labels made as separate text boxes are swept.
  _tkToggleSegments() {
    const a = this._tkSelectedAnnotation()
    if (!a || !this._isPathShape(a)) {
      return this._tkSay("Select a polygon or perimeter first, then click Sides.", "#b45309")
    }
    const am = this._instance.Core.documentViewer.getAnnotationManager()
    const legacy = am.getAnnotationsList().filter(x => x.getCustomData && x.getCustomData("tk_seg_parent") === a.Id)
    if (legacy.length) am.deleteAnnotations(legacy, { imported: false, force: true })
    const on = a.getCustomData("tk_segments") === "1"
    if (!on && !this._tkAxisFactor(a)) {
      return this._tkSay("Set the scale first (AI scale or the tool's calibrate), then the sides can be measured.", "#b45309")
    }
    a.setCustomData("tk_segments", on ? "" : "1")
    this._tkRepaint(a)
    this._tkSay(on ? "Side lengths hidden." : "Side lengths shown on every segment.")
    track("Takeoff Segments Shown", { project_id: this._takeoffProjectId(), on: !on })
  }

  // Item 20 — symbol for count marks: applies to selected count pins now
  // and to every count placed afterwards.
  _tkApplySymbol() {
    const sym = this._tkSymbolSel.value
    this._tkCountSymbol = sym
    try {
      const { Core } = this._instance
      const am = Core.documentViewer.getAnnotationManager()
      const CountA = Core.Annotations.CountMeasurementAnnotation
      if (!CountA) return this._tkSay("Counting isn't available in this viewer build.", "#b45309")
      const selected = (am.getSelectedAnnotations() || []).filter(x => x instanceof CountA)
      selected.forEach(x => { x.setCustomData("tk_symbol", sym); am.redrawAnnotation(x) })
      if (selected.length) {
        am.trigger("annotationChanged", [selected, "modify", {}])
        this._tkSay(`Symbol updated on ${selected.length} count ${selected.length === 1 ? "mark" : "marks"}.`)
      } else {
        this._tkSay(sym ? `New count marks will use ${sym}.` : "New count marks use the standard check.")
      }
      track("Takeoff Count Symbol Changed", { project_id: this._takeoffProjectId(), symbol: sym || "check" })
    } catch (e) { this._tkSay("Could not change the count symbol.", "#b91c1c") }
  }

  // Center-screen step banner for the guided count flow.
  _tkBanner(text) {
    const host = this.hasViewerContainerTarget ? this.viewerContainerTarget : this.element
    if (!this._aiTkBannerEl) {
      const el = document.createElement("div")
      el.style.cssText = "position:absolute;top:120px;left:50%;transform:translateX(-50%);z-index:61;" +
        "background:#9333ea;color:#fff;border-radius:10px;padding:10px 16px;font-size:14px;font-weight:500;" +
        "box-shadow:0 6px 18px rgba(0,0,0,.25);max-width:80%;text-align:center;"
      host.appendChild(el)
      this._aiTkBannerEl = el
    }
    if (text) {
      this._aiTkBannerEl.textContent = text
      this._aiTkBannerEl.style.display = "block"
    } else {
      this._aiTkBannerEl.style.display = "none"
    }
  }

  _tkSetBusy(busy) {
    this._aiTkBusy = busy
    if (this._aiTkCountBtn) {
      this._aiTkCountBtn.textContent = busy ? "Scanning…" : "✨ AI count"
      this._aiTkCountBtn.style.opacity = busy ? "0.7" : "1"
    }
    if (this._aiTkScaleBtn) this._aiTkScaleBtn.style.opacity = busy ? "0.7" : "1"
  }

  _aiUndoPins() {
    try {
      const am = this._instance.Core.documentViewer.getAnnotationManager()
      if (this._aiTkLastPins && this._aiTkLastPins.length) am.deleteAnnotations(this._aiTkLastPins)
    } catch (_) { /* pins may already be gone */ }
    this._aiTkLastPins = []
    this._aiTkUndoBtn.style.display = "none"
    this._tkSay("Pins removed.")
  }

  _aiCountCancel() {
    if (!this._aiTkArmed) return
    this._aiTkArmed = false
    this._tkBanner(null)
    try { this._instance.UI.setToolMode("Pan") } catch (_) { /* keep tool */ }
    this._tkSay("Count canceled.")
  }

  _tkSay(text, color = "#6b21a8") {
    if (this._aiTkStatus && this._aiTkBar && this._aiTkBar.style.display !== "none") {
      this._aiTkStatus.textContent = text
      this._aiTkStatus.title = text
      this._aiTkStatus.dataset.tone = color
      this._aiTkStatus.style.color = this._tkTone(color, this._pdfTheme === "dark")
    } else {
      // Outside the Takeoff ribbon there is no status line — say it as a toast.
      this._toastMini(text, color === "#b91c1c")
    }
  }


  // ── Larry 2026-09-15: Bluebeam-style editing on every sheet ──────────

  _isHatchable(a) {
    const A = this._instance.Core.Annotations
    if (!a || (a.getCustomData && a.getCustomData("tk_cutout_parent"))) return false
    return a instanceof A.PolygonAnnotation || a instanceof A.RectangleAnnotation || a instanceof A.EllipseAnnotation
  }

  // Polygons and polylines (areas, perimeters, plain shapes) — the ones
  // with nodes to edit and sides to label. Cutout holes are left alone.
  _isPathShape(a) {
    const A = this._instance.Core.Annotations
    if (!a || typeof a.getPath !== "function") return false
    if (a.getCustomData && a.getCustomData("tk_cutout_parent")) return false
    return a instanceof A.PolygonAnnotation || (A.PolylineAnnotation && a instanceof A.PolylineAnnotation)
  }

  _tkOwnsPainting(a) {
    try {
      if (!a.getCustomData || a.getCustomData("tk_cutout_parent")) return false
      if (a.Style === "cloudy") return false
      return a.getCustomData("tk_no_caption") === "1" || a.getCustomData("tk_segments") === "1" ||
             !!a.getCustomData("tk_font") || !!a.getCustomData("tk_font_color")
    } catch (_) { return false }
  }

  // Paints a polygon/polyline the way Apryse would (fill, stroke, dash,
  // opacity, hatch) and then the text we control: the measurement label
  // (unless hidden) and one length per side, both in the chosen size and
  // color with a white halo so they read over line work.
  _tkPaintPath(ctx, a, closed, hatchIn) {
    const path = (a.getPath && a.getPath()) || []
    if (path.length < 2) return
    const pts = path.map(p => ({ x: p.x, y: p.y }))
    const s = a.StrokeColor || {}
    const rgba = (c, alpha) => `rgba(${c.R ?? 0}, ${c.G ?? 0}, ${c.B ?? 0}, ${alpha})`
    const trace = () => {
      ctx.beginPath()
      ctx.moveTo(pts[0].x, pts[0].y)
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y)
      if (closed) ctx.closePath()
    }
    ctx.save()
    ctx.globalAlpha = typeof a.Opacity === "number" ? a.Opacity : 1
    trace()
    const f = a.FillColor
    if (closed && f && (f.A ?? 0) > 0) { ctx.fillStyle = rgba(f, f.A); ctx.fill() }
    ctx.strokeStyle = rgba(s, s.A ?? 1)
    ctx.lineWidth = a.StrokeThickness || 1
    ctx.lineJoin = "round"
    const dash = dashFor(a.Style)
    if (dash) ctx.setLineDash(dash)
    ctx.stroke()
    ctx.setLineDash([])
    ctx.restore()

    if (closed && a.getCustomData("tk_hatch") === "1" && hatchIn) {
      const xs = pts.map(p => p.x), ys = pts.map(p => p.y)
      ctx._tkBox = { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) }
      hatchIn(ctx, trace, s)
    }

    const size = Number(a.getCustomData("tk_font")) || 11
    const color = a.getCustomData("tk_font_color") || rgba(s, 1)
    if (a.Measure && a.getCustomData("tk_no_caption") !== "1") {
      const text = a.getContents && a.getContents()
      if (text) { const c = centroid(pts); this._tkText(ctx, text, c.x, c.y, size, color) }
    }
    if (a.getCustomData("tk_segments") === "1") {
      const conv = this._tkAxisFactor(a)
      if (conv) segmentLabels(pts, closed, conv.factor, conv.unit).forEach(l => this._tkText(ctx, l.text, l.x, l.y, Math.max(7, size * 0.85), color))
    }
  }

  _tkText(ctx, text, x, y, size, color) {
    ctx.save()
    ctx.font = `500 ${size}px sans-serif`
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.lineJoin = "round"
    ctx.lineWidth = Math.max(2, size / 4)
    ctx.strokeStyle = "rgba(255,255,255,0.85)"
    ctx.strokeText(text, x, y)
    ctx.fillStyle = color
    ctx.fillText(text, x, y)
    ctx.restore()
  }

  // Redraw + tell the manager the shape changed, so it saves and the
  // measurement text refreshes.
  _tkRepaint(a) {
    const am = this._instance.Core.documentViewer.getAnnotationManager()
    try { am.redrawAnnotation(a) } catch (_) { /* keep going */ }
    try { am.trigger("annotationChanged", [[a], "modify", {}]) } catch (_) { /* keep going */ }
  }

  // "I can turn the units off" — hide the measurement label; hovering the
  // shape still reads it out.
  _tkToggleCaption() {
    const a = this._tkSelectedAnnotation()
    if (!a || !this._isPathShape(a) || !a.Measure) return this._tkSay("Select an area or perimeter first.", "#b45309")
    const off = a.getCustomData("tk_no_caption") === "1"
    a.setCustomData("tk_no_caption", off ? "" : "1")
    this._tkRepaint(a)
    this._tkSay(off ? "Label shown." : "Label hidden — hover the shape to read it.")
  }

  TK_FONT_SIZES = [8, 11, 14, 18, 24]

  // "Let's make it a 28 point" — cycle through label sizes.
  _tkCycleFont() {
    const a = this._tkSelectedAnnotation()
    if (!a || !this._isPathShape(a)) return this._tkSay("Select a polygon or perimeter first.", "#b45309")
    const cur = Number(a.getCustomData("tk_font")) || 11
    const i = this.TK_FONT_SIZES.indexOf(cur)
    const next = this.TK_FONT_SIZES[(i + 1) % this.TK_FONT_SIZES.length]
    a.setCustomData("tk_font", String(next))
    this._tkRepaint(a)
    this._tkSay(`Label text ${next}pt.`)
  }

  TK_TEXT_COLORS = [["", "the line color"], ["#ffffff", "white"], ["#111827", "black"], ["#dc2626", "red"], ["#2563eb", "blue"]]

  // "My caption is red also, so it doesn't see it. So I'm going to turn it
  // white" — cycle the label color independently of the line.
  _tkCycleTextColor() {
    const a = this._tkSelectedAnnotation()
    if (!a || !this._isPathShape(a)) return this._tkSay("Select a polygon or perimeter first.", "#b45309")
    const cur = a.getCustomData("tk_font_color") || ""
    const i = this.TK_TEXT_COLORS.findIndex(([v]) => v === cur)
    const [next, name] = this.TK_TEXT_COLORS[(i + 1) % this.TK_TEXT_COLORS.length]
    a.setCustomData("tk_font_color", next)
    this._tkRepaint(a)
    this._tkSay(`Label text is now ${name}.`)
  }

  // Bluebeam's eyedropper: pick any color off the sheet and paint the
  // selected markup with it (line, and the fill when it has one). Uses the
  // browser's own picker — Chrome and Edge have it, Safari does not.
  async _tkEyedropper() {
    const { Core } = this._instance
    const am = Core.documentViewer.getAnnotationManager()
    const A = Core.Annotations
    const sel = am.getSelectedAnnotations() || []
    if (!sel.length) return this._tkSay("Select a markup first, then pick a color.", "#b45309")
    if (typeof window.EyeDropper !== "function") return this._tkSay("This browser has no eyedropper — Chrome and Edge do.", "#b45309")
    let hex
    try { ({ sRGBHex: hex } = await new window.EyeDropper().open()) } catch (_) { return }
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex || "")
    if (!m) return
    const [r, g, b] = [1, 2, 3].map(i => parseInt(m[i], 16))
    sel.forEach(a => {
      try {
        a.StrokeColor = new A.Color(r, g, b, a.StrokeColor?.A ?? 1)
        if (a.FillColor && (a.FillColor.A ?? 0) > 0) a.FillColor = new A.Color(r, g, b, a.FillColor.A)
        if (a.TextColor) a.TextColor = new A.Color(r, g, b, 1)
        am.redrawAnnotation(a)
      } catch (_) { /* leave that one */ }
    })
    try { am.trigger("annotationChanged", [sel, "modify", {}]) } catch (_) { /* keep going */ }
    this._tkSay(`Color set to ${hex}.`)
    track("Markup Eyedropper Used", { project_id: this._takeoffProjectId() })
  }

  // Bluebeam-style bar along the bottom: arrow, hand, zoom, fit and the
  // sheet layout (one sheet vs continuous scroll). The same controls sit
  // top-left in Apryse's header, where Larry never found them.
  _buildBottomToolbar() {
    const { UI, Core } = this._instance
    if (this._bottomBarInstance === this._instance) return
    const C = UI.Components
    if (!C || !C.ModularHeader || !C.ToolButton || !C.CustomButton || !C.Zoom) return
    try {
      const layoutBtn = (de, label, title, mode) => new C.CustomButton({
        dataElement: de, label, title, onClick: () => { try { UI.setLayoutMode(mode) } catch (_) { /* unsupported */ } }
      })
      const items = [
        new C.ToolButton({ dataElement: "clSelectTool", toolName: "AnnotationEdit", title: "Select (arrow)" }),
        new C.ToolButton({ dataElement: "clPanTool", toolName: "Pan", title: "Pan (hand) — drag to move around the sheet" }),
        new C.Divider({ dataElement: "clBottomDivider1" }),
        new C.Zoom({ dataElement: "clBottomZoom" }),
        new C.CustomButton({ dataElement: "clFitPage", label: "Fit", title: "Fit the whole sheet on screen",
                             onClick: () => { try { UI.setFitMode(UI.FitMode.FitPage) } catch (_) { /* unsupported */ } } }),
        new C.Divider({ dataElement: "clBottomDivider2" }),
        layoutBtn("clLayoutSingle", "Single sheet", "One sheet at a time", UI.LayoutMode.Single),
        layoutBtn("clLayoutScroll", "Scroll", "Scroll through all the pages", UI.LayoutMode.Continuous)
      ]
      const header = new C.ModularHeader({ dataElement: "clBottomToolbar", placement: "bottom", justifyContent: "center", gap: 8, items })
      UI.addModularHeaders([header])
      this._bottomBarInstance = this._instance
    } catch (e) {
      console.warn("[action-item-pdf-viewer] bottom toolbar failed:", e)
    }
  }

  // Backspace / Delete / ⌘Z while placing the points of a polygon,
  // polyline, area or perimeter drops the LAST point only — Escape still
  // wipes the whole shape. Apryse keeps the in-progress shape on the tool
  // as [fixed…, floating]; the floating point follows the mouse.
  _installDrawingKeys() {
    if (!this._drawKeys) {
      this._trackMouse = (e) => { this._lastMouse = { x: e.clientX, y: e.clientY } }
      window.addEventListener("mousemove", this._trackMouse, true)
      this._drawKeysSeen = new WeakSet()
      this._drawKeys = (e) => {
        // The same keystroke can reach us via window, document and the
        // viewer's own root — act once.
        if (this._drawKeysSeen.has(e)) return
        this._drawKeysSeen.add(e)
        const undo = (e.metaKey || e.ctrlKey) && !e.shiftKey && String(e.key).toLowerCase() === "z"
        if (e.key !== "Backspace" && e.key !== "Delete" && !undo) return
        // One press = one node (Phelipe 2026-09-16: "sometimes it removed
        // the last two or three lines"). Holding the key auto-repeats, and
        // a press can arrive more than once — ignore repeats and anything
        // within a quarter second of the last one we acted on.
        if (e.repeat) { e.preventDefault(); return }
        const now = Date.now()
        if (now - (this._drawKeysAt || 0) < 250) { e.preventDefault(); return }
        const t = e.composedPath ? e.composedPath()[0] : e.target
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return
        const dv = this._instance?.Core?.documentViewer
        if (!dv) return
        let tool = null
        try { tool = dv.getToolMode() } catch (_) { return }
        // Any create tool with a point path in progress (polygon, polyline,
        // area, perimeter) — not tied to the tool's name.
        const a = tool && tool.annotation
        const path = a && typeof a.getPath === "function" && typeof a.popPath === "function" ? a.getPath() : null
        if (!path || path.length < 2) return
        e.preventDefault()
        e.stopPropagation()
        this._drawKeysAt = now
        const pts = path.map(p => ({ x: p.x, y: p.y }))
        const next = backspacePoints(pts, this._parkTolerance(dv))
        if (!next) {
          // Nothing left but the first node — start the shape over.
          try { this._instance.UI.setToolMode(tool.name) } catch (_) { /* keep tool */ }
          return
        }
        while (a.getPath().length) a.popPath()
        next.forEach(p => a.addPathPoint(p.x, p.y))
        this._parkRepaint(dv, a)
      }
      window.addEventListener("keydown", this._drawKeys, true)
      document.addEventListener("keydown", this._drawKeys, true)
    }
    // The viewer's own root (shadow root, or an iframe window on older
    // builds) — re-hooked on every init because the instance is new.
    try { this._snapUiRoot()?.addEventListener("keydown", this._drawKeys, true) } catch (_) { /* no root */ }
    try {
      const w = this._instance?.UI?.iframeWindow
      if (w && w !== window) w.addEventListener("keydown", this._drawKeys, true)
    } catch (_) { /* same-window build */ }
  }

  // "Parked" = the floating point sits on the last node (within a few
  // screen pixels, whatever the zoom).
  _parkTolerance(dv) {
    let zoom = 1
    try { zoom = dv.getZoomLevel() || 1 } catch (_) { zoom = 1 }
    return 6 / zoom
  }

  // Repaint the pending shape with its floating point parked on a node.
  // No synthetic mouse events: on rotated sheets and near snap targets a
  // replayed mouse move lands off the node (seen live 2026-09-16). Pinning
  // the point and redrawing the annotation from the list is exact, and the
  // real mouse picks the point up again on its next move.
  _parkRepaint(dv, a) {
    try {
      const p = a.getPath()
      const node = p[p.length - 1]
      if (!node) return
      a.setPathPoint(p.length - 1, node.x, node.y)
      dv.getAnnotationManager().drawAnnotationsFromList([a])
    } catch (_) { /* next real mouse move repaints */ }
  }







  // Shift-click on a selected polygon/polyline: on a node removes it, on a
  // side adds a node there (Bluebeam's Shift+click). Dragging nodes is
  // Apryse's own. Measurements recalculate; cutouts re-net.
  _installNodeEditing() {
    const { Core } = this._instance
    const dv = Core.documentViewer
    const am = dv.getAnnotationManager()
    const A = Core.Annotations
    am.addEventListener("annotationSelected", (list, action) => {
      if (action === "selected" && list && list.length === 1) this._lastSelectedShape = list[0]
      else if (action === "deselected" && !(am.getSelectedAnnotations() || []).length) this._lastSelectedShapeAt = Date.now()
    })
    this._nodeEdit = (e) => {
      try {
        if (!e || !e.shiftKey) return
        const tool = dv.getToolMode()
        if (!tool || tool.name !== "AnnotationEdit") return
        const a = this._lastSelectedShape
        if (!a || !this._isPathShape(a)) return
        if (typeof dv.getRotation === "function" && dv.getRotation() !== 0) return
        const pt = this._clientToPage(e.clientX, e.clientY, a.PageNumber)
        if (!pt) return
        const zoom = dv.getZoomLevel() || 1
        const tol = 10 / zoom
        const closed = a instanceof A.PolygonAnnotation
        const pts = a.getPath().map(p => ({ x: p.x, y: p.y }))
        let next = null
        const vi = nearestVertex(pts, pt, tol)
        if (vi >= 0) {
          if (pts.length > (closed ? 3 : 2)) next = withVertexRemoved(pts, vi)
        } else {
          const seg = nearestSegment(pts, pt, tol, closed)
          if (seg) next = withVertexInserted(pts, seg.index, seg.point)
        }
        if (!next) return
        while (a.getPath().length) a.popPath()
        next.forEach(p => a.addPathPoint(p.x, p.y))
        try { a.adjustRect() } catch (_) { /* older builds */ }
        this._tkRepaint(a)
        if (this._isTakeoffArea(a)) this._applyCutoutSubtraction(a)
        // Shift-click also toggles Apryse's selection — keep the shape selected.
        setTimeout(() => { try { am.selectAnnotation(a) } catch (_) { /* gone */ } }, 0)
        track("Markup Node Edited", { project_id: this._takeoffProjectId(), action: vi >= 0 ? "remove" : "add" })
      } catch (_) { /* never break the click */ }
    }
    dv.addEventListener("mouseLeftDown", this._nodeEdit)
  }

  // Client pixel → page point, from the page container's box (no rotation).
  _clientToPage(cx, cy, pageNumber) {
    const dv = this._instance.Core.documentViewer
    const el = this._snapUiRoot()?.querySelector(`#pageContainer${pageNumber}`)
    if (!el) return null
    const r = el.getBoundingClientRect()
    if (!r.width || !r.height) return null
    return { x: (cx - r.left) / r.width * dv.getPageWidth(pageNumber), y: (cy - r.top) / r.height * dv.getPageHeight(pageNumber) }
  }

  // The popup on a selected markup gains the Bluebeam-style options. Each
  // shows only for the shapes it applies to.
  _installMarkupPopup() {
    const { UI, Core } = this._instance
    if (this._popupInstance === this._instance) return
    const am = Core.documentViewer.getAnnotationManager()
    if (!UI.annotationPopup || typeof UI.annotationPopup.add !== "function") return
    const measured = (a) => this._isPathShape(a) && !!a.Measure
    // Icons, not words — text labels wrapped and ran into each other in the
    // popup (Phelipe 2026-09-16). Each has a tooltip.
    const svg = (body) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`
    const ICON = {
      hatch: svg('<rect x="4" y="4" width="16" height="16" rx="1.5"/><path d="M4 12l8-8M4 20L20 4M12 20l8-8"/>'),
      sides: svg('<path d="M4 18L20 6"/><path d="M6 15l2 2M9 12l2 2M12 9l2 2M15 6l2 2"/><path d="M3 21h6"/>'),
      label: svg('<path d="M4 7h16M4 12h10M4 17h7"/><path d="M17 15l2 2 3-4"/>'),
      textSize: svg('<path d="M3 18l4-11 4 11M4.5 14h5"/><path d="M13 18l3-8 3 8M14.2 15h3.6"/>'),
      textColor: svg('<path d="M6 16l4.5-11 4.5 11M7.8 12h5.4"/><path d="M4 20h16" stroke-width="3"/>'),
      eyedropper: svg('<path d="M4 20l1-4 9-9 3 3-9 9-4 1z"/><path d="M13 6l2-2a2.1 2.1 0 013 3l-2 2"/><path d="M12 8l4 4"/>'),
      cutout: svg('<path d="M4 4h16v16H4z"/><path d="M10 10h5v5h-5z" stroke-dasharray="2 2"/>')
    }
    const defs = [
      { de: "clPopHatch", img: ICON.hatch, title: "Hatch fill on / off", fn: () => this._tkToggleHatch(), when: (a) => this._isHatchable(a) },
      { de: "clPopSides", img: ICON.sides, title: "Show the length of every side", fn: () => this._tkToggleSegments(), when: measured },
      { de: "clPopLabel", img: ICON.label, title: "Show or hide the measurement label", fn: () => this._tkToggleCaption(), when: measured },
      { de: "clPopTextSize", img: ICON.textSize, title: "Label text size", fn: () => this._tkCycleFont(), when: measured },
      { de: "clPopTextColor", img: ICON.textColor, title: "Label text color", fn: () => this._tkCycleTextColor(), when: measured },
      { de: "clPopEyedropper", img: ICON.eyedropper, title: "Eyedropper — pick this markup's color from the sheet", fn: () => this._tkEyedropper(), when: () => typeof window.EyeDropper === "function" },
      { de: "clPopCutout", img: ICON.cutout, title: "Cutout — cut a hole out of this area", fn: () => this._tkStartCutout(), when: (a) => this._isTakeoffArea(a) }
    ]
    // add() puts each new button at the FRONT, so add in reverse to read
    // Hatch → Sides → Label → Text size → Text color → Eyedropper → Cutout.
    defs.slice().reverse().forEach(d => {
      try { UI.annotationPopup.add({ type: "actionButton", dataElement: d.de, img: d.img, title: d.title, onClick: d.fn }) } catch (_) { /* popup API differs */ }
    })
    const sync = () => {
      const sel = am.getSelectedAnnotations() || []
      const a = sel.length === 1 ? sel[0] : null
      defs.forEach(d => {
        let show = false
        try { show = !!a && d.when(a) } catch (_) { show = false }
        try { show ? UI.enableElements([d.de]) : UI.disableElements([d.de]) } catch (_) { /* fine */ }
      })
    }
    am.addEventListener("annotationSelected", () => setTimeout(sync, 0))
    sync()
    this._popupInstance = this._instance
  }

  // Hover any measurement to read it — the way Bluebeam shows a hidden
  // caption's value on hover.
  _installMeasureHover() {
    const { Core } = this._instance
    const dv = Core.documentViewer
    const am = dv.getAnnotationManager()
    const host = this.hasViewerContainerTarget ? this.viewerContainerTarget : this.element
    if (getComputedStyle(host).position === "static") host.style.position = "relative"
    this._measureTip?.remove()
    const tip = document.createElement("div")
    tip.style.cssText = "position:absolute;z-index:62;display:none;pointer-events:none;background:#111827;color:#fff;" +
      "font-size:12px;font-weight:500;padding:3px 8px;border-radius:6px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.25);"
    host.appendChild(tip)
    this._measureTip = tip
    let raf = null
    const hide = () => { tip.style.display = "none" }
    this._hoverMeasure = (e) => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = null
        let a = null
        try { a = am.getAnnotationByMouseEvent(e) } catch (_) { a = null }
        if (!a || !a.Measure) return hide()
        const text = a.getContents && a.getContents()
        if (!text) return hide()
        const r = host.getBoundingClientRect()
        tip.textContent = text
        tip.style.left = `${e.clientX - r.left + 14}px`
        tip.style.top = `${e.clientY - r.top + 14}px`
        tip.style.display = "block"
      })
    }
    dv.addEventListener("mouseMove", this._hoverMeasure)
    dv.addEventListener("mouseLeave", hide)
  }

  // Renders the current page to an offscreen canvas (about maxWidth px wide).
  // Returns { canvas, factor, pageWidth, pageHeight, pageNumber } where
  // factor converts page points → canvas pixels.
  _tkPageCanvas(maxWidth) {
    const { Core } = this._instance
    const dv = Core.documentViewer
    const doc = dv.getDocument()
    const pageNumber = dv.getCurrentPage()
    const info = doc.getPageInfo(pageNumber)
    const zoom = Math.min(maxWidth / info.width, 4)
    return new Promise((resolve, reject) => {
      try {
        doc.loadCanvas({
          pageNumber,
          zoom,
          drawComplete: (canvas) => resolve({
            canvas, pageNumber,
            factor: canvas.width / info.width,
            pageWidth: info.width, pageHeight: info.height
          })
        })
      } catch (e) { reject(e) }
    })
  }

  _tkHeaders() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  async _aiReadScale() {
    if (this._aiTkBusy) return
    this._aiTkBusy = true
    this._tkSay("Reading the sheet's scale…")
    try {
      const page = await this._tkPageCanvas(2200)
      const resp = await fetch(`/projects/${this._takeoffProjectId()}/ai_takeoff/read_scale`, {
        method: "POST", headers: this._tkHeaders(),
        body: JSON.stringify({ image: page.canvas.toDataURL("image/jpeg", 0.9) })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read the scale")
      if (!data.found) {
        this._tkSay("No scale statement found on this sheet — set it manually (Takeoff → any tool → Scale).", "#b45309")
        return
      }
      this._applyMeasureScale(data.display, data.actual)
      this._tkSay(`✓ Scale set to ${data.label} — measurements now use it.`, "#15803d")
      track("Takeoff AI Scale Set", { project_id: this._takeoffProjectId(), label: data.label })
    } catch (err) {
      this._tkSay(err.message, "#b91c1c")
    } finally {
      this._aiTkBusy = false
    }
  }

  _applyMeasureScale(display, actual) {
    const { Core } = this._instance
    const scale = [[display[0], display[1]], [actual[0], actual[1]]]
    this._aiTkScale = scale
    const tools = ["AnnotationCreateDistanceMeasurement", "AnnotationCreatePerimeterMeasurement",
                   "AnnotationCreateAreaMeasurement", "AnnotationCreateRectangularAreaMeasurement",
                   "AnnotationCreateCloudyRectangularAreaMeasurement", "AnnotationCreateArcMeasurement",
                   "AnnotationCreateEllipseMeasurement"]
    tools.forEach((name) => {
      try {
        const tool = Core.documentViewer.getTool(name)
        if (tool && typeof tool.setStyles === "function") tool.setStyles({ Scale: scale })
      } catch (_) { /* tool not present in this build */ }
    })
    // setStyles can reset a tool's snap mode in some WebViewer builds — the
    // observed sequence was exactly "AI scale, then no more snap" (Larry
    // 2026-08-25). Re-arm snapping after every scale push.
    this._enableMeasurementSnapping()
  }

  // Step 1 of AI count: the user boxes ONE example symbol. The box is drawn
  // with the Takeoff group's own rectangular-area tool so the ribbon STAYS
  // on Takeoff (the plain rectangle tool lives in Annotate and yanked the
  // whole UI over there). The temp annotation is deleted the moment we have
  // its rectangle. Esc cancels.
  _aiCountStart() {
    if (this._aiTkBusy || this._aiTkArmed) return
    const { Core, UI } = this._instance
    this._aiTkArmed = true
    this._tkBanner("Step 1 of 2 — drag a box around ONE example symbol (press Esc to cancel)")
    this._tkSay("Waiting for your example box…")
    // Give the capture tool a scale so Apryse doesn't interrupt with its
    // calibrate dialog; any value works — the annotation is discarded.
    try {
      const tool = Core.documentViewer.getTool("AnnotationCreateRectangularAreaMeasurement")
      if (tool && typeof tool.setStyles === "function") {
        tool.setStyles({ Scale: this._aiTkScale || [[1, "in"], [1, "in"]] })
      }
      UI.setToolMode("AnnotationCreateRectangularAreaMeasurement")
    } catch (_) {
      try { UI.setToolMode("AnnotationCreateRectangle") } catch (_2) { /* keep current tool */ }
    }
    if (!this._aiTkEsc) {
      this._aiTkEsc = (e) => { if (e.key === "Escape") this._aiCountCancel() }
      window.addEventListener("keydown", this._aiTkEsc)
      try { this._instance.UI.iframeWindow.addEventListener("keydown", this._aiTkEsc) } catch (_) { /* same-window build */ }
    }
    if (this._aiTkCapture) return // listener already installed
    this._aiTkCapture = (annotations, action) => {
      if (!this._aiTkArmed || action !== "add") return
      const annot = annotations && annotations[0]
      if (!annot || annot.Subject === "AI count" || annot.Subject === "Count Measurement") return
      this._aiTkArmed = false
      const r = typeof annot.getRect === "function" ? annot.getRect() : null
      const rect = r
        ? { x: Math.min(r.x1, r.x2), y: Math.min(r.y1, r.y2), w: Math.abs(r.x2 - r.x1), h: Math.abs(r.y2 - r.y1), page: annot.PageNumber }
        : { x: annot.X, y: annot.Y, w: annot.Width, h: annot.Height, page: annot.PageNumber }
      const am = Core.documentViewer.getAnnotationManager()
      am.deleteAnnotation(annot)
      try { UI.setToolMode("Pan") } catch (_) { /* stay on tool */ }
      if (!rect.w || !rect.h) { this._tkBanner(null); this._tkSay("That box was empty — try again.", "#b45309"); return }
      this._aiCountRun(rect)
    }
    Core.documentViewer.getAnnotationManager().addEventListener("annotationChanged", this._aiTkCapture)
  }

  async _aiCountRun(rect) {
    this._tkSetBusy(true)
    this._tkBanner("Step 2 of 2 — ✨ scanning the sheet for matches…")
    this._tkSay("Scanning…")
    try {
      // Render BIG, then scan in overlapping high-res patches — one shrunken
      // photo of a 36" sheet hides the small symbols from the AI.
      const page = await this._tkPageCanvas(4400)
      if (rect.page !== page.pageNumber) throw new Error("Draw the example box on the page you're viewing.")
      const f = page.factor
      const pad = Math.min(rect.w, rect.h) * 0.15
      const crop = document.createElement("canvas")
      const cropW = Math.max(1, Math.round((rect.w + pad * 2) * f))
      const cropH = Math.max(1, Math.round((rect.h + pad * 2) * f))
      const cropScale = Math.min(1, 800 / Math.max(cropW, cropH))
      crop.width = Math.max(1, Math.round(cropW * cropScale))
      crop.height = Math.max(1, Math.round(cropH * cropScale))
      crop.getContext("2d").drawImage(page.canvas,
        Math.max(0, (rect.x - pad) * f), Math.max(0, (rect.y - pad) * f),
        cropW, cropH, 0, 0, crop.width, crop.height)

      const cols = Math.min(4, Math.max(1, Math.round(page.canvas.width / 1500)))
      const rows = Math.min(3, Math.max(1, Math.round(page.canvas.height / 1500)))
      const tiles = []
      const tw = page.canvas.width / cols
      const th = page.canvas.height / rows
      const ovX = tw * 0.06
      const ovY = th * 0.06
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const sx = Math.max(0, c * tw - ovX)
          const sy = Math.max(0, r * th - ovY)
          const sw = Math.min(page.canvas.width - sx, tw + ovX * 2)
          const sh = Math.min(page.canvas.height - sy, th + ovY * 2)
          const t = document.createElement("canvas")
          t.width = Math.round(sw)
          t.height = Math.round(sh)
          t.getContext("2d").drawImage(page.canvas, sx, sy, sw, sh, 0, 0, t.width, t.height)
          tiles.push({ image: t.toDataURL("image/jpeg", 0.88),
                       x: sx / page.canvas.width, y: sy / page.canvas.height,
                       w: sw / page.canvas.width, h: sh / page.canvas.height })
        }
      }
      this._tkBanner(`Step 2 of 2 — ✨ scanning the sheet in ${tiles.length} patches…`)

      const resp = await fetch(`/projects/${this._takeoffProjectId()}/ai_takeoff/count_symbols`, {
        method: "POST", headers: this._tkHeaders(),
        body: JSON.stringify({ tiles: tiles, example: crop.toDataURL("image/png") })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "The count failed — try again")
      const n = this._placeCountPins(data.points || [], rect, page, data.label)
      if (n > 0 && this._aiTkUndoBtn) {
        this._aiTkUndoBtn.textContent = `Undo ${n} pins`
        this._aiTkUndoBtn.style.display = "inline-block"
      }
      this._tkSay(n > 0 ? `✓ AI pinned ${n} "${data.label}" — delete any wrong pins, add missed ones with the count tool.`
                        : "AI found no matches — try a tighter box around the symbol.", n > 0 ? "#15803d" : "#b45309")
      track("Takeoff AI Count Run", { project_id: this._takeoffProjectId(), found: n, label: data.label })
    } catch (err) {
      this._tkSay(err.message, "#b91c1c")
    } finally {
      this._tkSetBusy(false)
      this._tkBanner(null)
    }
  }

  _placeCountPins(points, rect, page, label) {
    const { Core } = this._instance
    const am = Core.documentViewer.getAnnotationManager()
    const CountA = Core.Annotations.CountMeasurementAnnotation
    const r = Math.max(6, Math.min(rect.w, rect.h) / 2)
    const annots = points.map((p) => {
      const cx = (p.x / 1000) * page.pageWidth
      const cy = (p.y / 1000) * page.pageHeight
      let a
      if (CountA) {
        a = new CountA()
        a.Subject = "Count Measurement"
      } else {
        a = new Core.Annotations.EllipseAnnotation()
        a.Subject = "AI count"
        a.StrokeColor = new Core.Annotations.Color(147, 51, 234, 1)
        a.StrokeThickness = 2
      }
      a.PageNumber = page.pageNumber
      a.X = cx - r
      a.Y = cy - r
      a.Width = r * 2
      a.Height = r * 2
      a.Author = am.getCurrentUser()
      a.setContents(label || "AI count")
      return a
    })
    if (annots.length) {
      am.addAnnotations(annots)
      am.drawAnnotationsFromList(annots)
    }
    this._aiTkLastPins = annots
    return annots.length
  }

  // Zoom level: persists per user (debounced) and re-applies on every
  // document open — Specs & Drawings, Action Items, Design alike — so the
  // viewer comes back at the zoom they left it.
  _setupZoomPersistence() {
    const { Core } = this._instance
    Core.documentViewer.addEventListener("zoomUpdated", (zoom) => {
      // Ignore the automatic fit/apply zooms during document load — only
      // user-driven changes should persist. The suppress counter swallows
      // the programmatic setFitMode/setZoomLevel events even when they
      // land late (big sheets keep firing fit events well after load).
      if (this._suppressZoomEvents > 0) {
        this._suppressZoomEvents--
        return
      }
      if (!this._documentReady || this._applyingZoom || this._splitActive) return
      clearTimeout(this._zoomSaveTimer)
      this._zoomSaveTimer = setTimeout(() => {
        const z = Math.round(zoom * 100) / 100
        // Outside 25%–400% it's a fit artifact or an accidental fling —
        // never persist it as the user's preference.
        if (z < 0.25 || z > 4) return
        if (this._lastSavedZoom && Math.abs(this._lastSavedZoom - z) < 0.005) return
        this._lastSavedZoom = z
        const token = document.querySelector('meta[name="csrf-token"]')?.content
        fetch("/save_pdf_zoom", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRF-Token": token },
          credentials: "same-origin",
          body: `zoom=${z}`
        }).catch(() => {})
      }, 800)
    })
  }

  // Left (thumbnails) panel: open by default, per the user's saved
  // preference. Closing/opening it persists, so the viewer comes back
  // the way they left it (mirrors the theme preference).
  _setupLeftPanel() {
    const { UI } = this._instance
    this._leftPanelOpen = this.leftPanelValue
    if (this._leftPanelOpen) {
      try { UI.openElements(["leftPanel"]) } catch (_) {}
    }

    try {
      UI.addEventListener(UI.Events.VISIBILITY_CHANGED, (e) => {
        if (e?.detail?.element !== "leftPanel") return
        const open = !!e.detail.isVisible
        if (open === this._leftPanelOpen) return
        this._leftPanelOpen = open

        const token = document.querySelector('meta[name="csrf-token"]')?.content
        fetch("/save_pdf_left_panel", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRF-Token": token },
          credentials: "same-origin",
          body: `open=${open}`
        }).catch(() => {})
      })
    } catch (_) { /* viewer without visibility events — preference just won't persist */ }
  }

  // Comments (notes) panel preference — localStorage like the sheet-tab
  // strip; a viewer-chrome choice doesn't need a server round trip.
  _notesPanelPref() {
    try { return localStorage.getItem("conslog-notes-panel") } catch (_) { return null }
  }

  _watchNotesPanel() {
    if (this._notesPanelWatched) return
    this._notesPanelWatched = true
    const { UI } = this._instance
    try {
      UI.addEventListener(UI.Events.VISIBILITY_CHANGED, (e) => {
        if (e?.detail?.element !== "notesPanel") return
        // Ignore the programmatic opens/closes around document load — only
        // a click the user makes should update the remembered choice.
        if (Date.now() - (this._notesPanelSuppress || 0) < 1500) return
        try { localStorage.setItem("conslog-notes-panel", e.detail.isVisible ? "open" : "closed") } catch (_) {}
      })
    } catch (_) { /* viewer without visibility events — preference just won't persist */ }
  }

  _setupScrollZoom() {
    this._scrollZoomEnabled = this.scrollZoomValue
    // Attach wheel-to-zoom to every current pane (1 in single view, 2 in split)
    // so scroll-to-zoom works in the split view too. Re-synced on enter/leave
    // split via _syncScrollZoomTargets().
    this._syncScrollZoomTargets()
  }

  // (Re)attach wheel-zoom to all current document viewers. Detaches prior
  // handlers first so entering/leaving split never double-binds.
  _syncScrollZoomTargets() {
    if (!this._instance) return
    this._detachWheelZoom()
    try {
      this._instance.Core.getDocumentViewers().forEach(dv => this._attachWheelZoom(dv))
    } catch (_) { /* viewers not ready */ }
  }

  _detachWheelZoom() {
    const refs = this._wheelZoomRefs || []
    refs.forEach(({ target, handler }) => {
      try { target.removeEventListener('wheel', handler, { passive: false }) } catch (_) { /* gone */ }
    })
    this._wheelZoomRefs = []
  }

  // Wheel-to-zoom for ONE pane. Zooms that pane only (dv.zoomTo), keeping the
  // point under the cursor fixed — so each split pane zooms independently.
  _attachWheelZoom(dv) {
    let scrollViewElement, viewerElement
    try { scrollViewElement = dv.getScrollViewElement(); viewerElement = dv.getViewerElement() } catch (_) { return }
    // Attach to the SCROLL AREA, not the inner viewer element: wheel events
    // over the gray gutter around the page never bubble through the inner
    // element, so those spins scrolled instead of zooming ("it doesn't zoom
    // every time").
    const target = scrollViewElement || viewerElement
    if (!target) return

    // Bluebeam-style wheel zoom — validated against the vendored build with a
    // measurement harness: anchor error is 0px at page corners, center, and
    // deep zoom; spins over the gray gutter pin the nearest page edge.
    // - The anchor LOCKS on the first notch of a gesture (300ms idle or a
    //   moved mouse re-anchors) and every step corrects toward it, so points
    //   near a page edge converge exactly once the view can physically scroll
    //   there. Re-anchoring on every notch is what made corners drift.
    // - Notches landing within one frame collapse into a single engine zoom,
    //   which cuts the tile-repaint flicker around the page while spinning.
    // - Multiplicative, delta-proportional steps: wheel notches move ~±15%,
    //   trackpad ticks glide. Geometry updates synchronously after zoomTo
    //   (measured), so the scroll correction lands in the same frame.
    let anchor = null, pending = 0, scheduled = false
    const flush = () => {
      scheduled = false
      const dY = pending; pending = 0
      if (!dY || !anchor) return
      const cur = dv.getZoomLevel()
      const factor = Math.min(1.6, Math.max(0.62, Math.exp(-dY * 0.0015)))
      const z = Math.max(0.25, Math.min(16.0, cur * factor))
      if (Math.abs(z - cur) < 0.0005) return
      // Fast-zoom mode (Phelipe 2026-08-01): while the wheel is actively
      // spinning, render at standard resolution instead of the screen's
      // pixel density — on Retina that's 4x fewer pixels per step. The idle
      // timer below restores full sharpness the moment the gesture stops.
      if (!this._fastZoomOn && (window.devicePixelRatio || 1) > 1.2) {
        try {
          const CoreNS = this._instance.Core
          if (typeof CoreNS.setCanvasMultiplier === "function" && typeof CoreNS.unsetCanvasMultiplier === "function") {
            CoreNS.setCanvasMultiplier(1)
            this._fastZoomOn = true
          }
        } catch (_) { /* full-res zooming still works */ }
      }
      dv.zoomTo(z) // zoom THIS pane only
      if (scrollViewElement && viewerElement) {
        const vr = viewerElement.getBoundingClientRect()
        scrollViewElement.scrollLeft += Math.round(vr.left + anchor.docX * z - anchor.mx)
        scrollViewElement.scrollTop += Math.round(vr.top + anchor.docY * z - anchor.my)
      }
      // Apryse auto-switches to viewport-only rendering from 2x up, which is
      // what flashed WHITE around the previously visible rectangle when
      // zooming back out. Keep the WHOLE page painted at moderate zoom (out
      // zooms scale the existing image, blurry→sharp like Bluebeam) and only
      // allow viewport mode when deep-zoomed, where full-page rasters of big
      // sheets would eat memory. Thresholds verified against the vendored
      // build (mode flips at 2x; enable/disable both stick).
      try {
        if (typeof dv.isInViewportRenderMode === "function") {
          // At-rest rule stays as validated: whole page ≤3x, viewport >3x.
          // DURING the gesture, also allow viewport mode whenever the full
          // page would dwarf the screen (zoom-OUT steps used to re-raster
          // the entire 24x36 sheet every notch — the "zoom out is slower"
          // feel). The idle settle below repaints the complete page at rest.
          let wantViewport = z > 3
          if (!wantViewport && scrollViewElement && viewerElement && viewerElement.clientWidth > 0) {
            const pw = (viewerElement.clientWidth / cur) * z
            const ph = (viewerElement.clientHeight / cur) * z
            wantViewport = (pw * ph) > (scrollViewElement.clientWidth * scrollViewElement.clientHeight * 3)
          }
          if (dv.isInViewportRenderMode() !== wantViewport) {
            if (wantViewport) dv.enableViewportRenderMode()
            else dv.disableViewportRenderMode()
          }
        }
      } catch (_) { /* engine build without the API */ }
      // Gesture over (~a quarter second without a notch) → back to full
      // resolution, settle the render mode, and repaint sharp. Blurry-while-
      // spinning, crisp-at-rest — the same feel Bluebeam gives.
      clearTimeout(this._fastZoomIdle)
      this._fastZoomIdle = setTimeout(() => {
        try {
          if (this._fastZoomOn) {
            this._fastZoomOn = false
            this._instance.Core.unsetCanvasMultiplier()
          }
          // Settle to the at-rest mode: complete page below 3x (no white
          // edges left behind), viewport only when deep-zoomed.
          if (typeof dv.isInViewportRenderMode === "function" &&
              dv.isInViewportRenderMode() && dv.getZoomLevel() <= 3) {
            dv.disableViewportRenderMode()
          }
          if (typeof dv.refreshAll === "function") dv.refreshAll()
          if (typeof dv.updateView === "function") dv.updateView()
        } catch (_) { /* stays as-is until the next render */ }
      }, 240)
    }
    const handler = (e) => {
      if (!this._scrollZoomEnabled) return
      e.preventDefault()
      e.stopPropagation()
      const now = performance.now()
      if (viewerElement && (!anchor || now - anchor.t > 300 ||
          Math.abs(e.clientX - anchor.mx) > 3 || Math.abs(e.clientY - anchor.my) > 3)) {
        const zz = dv.getZoomLevel()
        const vr = viewerElement.getBoundingClientRect()
        // Clamp the anchor to the page surface: zooming from the gutter
        // follows the nearest page edge instead of chasing empty space.
        anchor = {
          mx: e.clientX, my: e.clientY, t: now,
          docX: Math.min(Math.max((e.clientX - vr.left) / zz, 0), vr.width / zz),
          docY: Math.min(Math.max((e.clientY - vr.top) / zz, 0), vr.height / zz)
        }
      } else if (anchor) { anchor.t = now }
      pending += e.deltaY
      if (!scheduled) {
        scheduled = true
        if (typeof requestAnimationFrame === "function") requestAnimationFrame(flush)
        else setTimeout(flush, 16)
      }
    }

    target.addEventListener('wheel', handler, { passive: false })
    ;(this._wheelZoomRefs = this._wheelZoomRefs || []).push({ target, handler })
  }

  _addScrollZoomToggle() {
    const { UI } = this._instance
    const self = this

    try {
      const topHeader = UI.getModularHeader('default-top-header')
      if (!topHeader) return

      // Find the grouped left buttons item which contains zoom controls
      const leftGroup = topHeader.items.find(i => i.dataElement === 'groupedLeftHeaderButtons')
      if (!leftGroup || !leftGroup.items) return

      const toggleBtn = new UI.Components.CustomButton({
        dataElement: 'scrollZoomToggle',
        title: self._scrollZoomEnabled ? 'Scroll to Zoom: ON' : 'Scroll to Zoom: OFF',
        img: self._scrollZoomSvg(self._scrollZoomEnabled),
        onClick: () => self._toggleScrollZoom()
      })

      const items = [...leftGroup.items]
      // Insert after zoom-container, before the divider that follows it
      const zoomIdx = items.findIndex(i => i.dataElement === 'zoom-container')
      items.splice(zoomIdx !== -1 ? zoomIdx + 1 : items.length, 0, toggleBtn)
      leftGroup.setItems(items)
      setTimeout(() => this._applyToggleStyling(), 500)
    } catch (e) {
      console.warn('[scroll-zoom] Could not add toggle:', e)
    }
  }

  _toggleScrollZoom() {
    this._scrollZoomEnabled = !this._scrollZoomEnabled
    this.scrollZoomValue = this._scrollZoomEnabled

    // Re-build the button in the left group with updated icon
    try {
      const { UI } = this._instance
      const topHeader = UI.getModularHeader('default-top-header')
      const leftGroup = topHeader?.items.find(i => i.dataElement === 'groupedLeftHeaderButtons')
      if (leftGroup) {
        const items = [...leftGroup.items]
        const idx = items.findIndex(i => i.dataElement === 'scrollZoomToggle')
        if (idx !== -1) {
          const self = this
          items[idx] = new UI.Components.CustomButton({
            dataElement: 'scrollZoomToggle',
            title: self._scrollZoomEnabled ? 'Scroll to Zoom: ON' : 'Scroll to Zoom: OFF',
            img: self._scrollZoomSvg(self._scrollZoomEnabled),
            onClick: () => self._toggleScrollZoom()
          })
          leftGroup.setItems(items)
          setTimeout(() => this._applyToggleStyling(), 300)
        }
      }
    } catch (_) {}

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) {
      fetch('/save_pdf_scroll_zoom', {
        method: 'POST',
        headers: { 'X-CSRF-Token': token, 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `enabled=${this._scrollZoomEnabled}&context=${this.scrollZoomContextValue}`
      })
    }
  }

  _applyToggleStyling() {
    if (!this._instance) return
    try {
      const iframeDoc = this._instance.UI.iframeWindow.document
      const btn = iframeDoc.querySelector('[data-element="scrollZoomToggle"]')
      if (btn) {
        const on = this._scrollZoomEnabled
        btn.title = on ? 'Scroll to Zoom: ON' : 'Scroll to Zoom: OFF'
        btn.style.background = on ? 'rgba(56,132,244,0.15)' : ''
        btn.style.borderRadius = '4px'
      }
    } catch (_) {}
  }

  _scrollZoomSvg(active) {
    const color = active ? '%233884f4' : '%23868e96'
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="3" width="12" height="18" rx="6"/><line x1="6" y1="11" x2="18" y2="11"/><line x1="12" y1="3" x2="12" y2="11"/><circle cx="12" cy="7" r="1" fill="${color}" stroke="none"/></svg>`
    return `data:image/svg+xml,${svg}`
  }

  // ── Light/dark theme toggle (sun/moon, left of the search icon) ──────────

  _buildThemeButton() {
    const { UI } = this._instance
    const self = this
    return new UI.Components.CustomButton({
      dataElement: 'pdfThemeToggle',
      title: self._pdfTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
      img: self._themeSvg(self._pdfTheme === 'dark'),
      onClick: () => self._toggleTheme()
    })
  }

  // The default top header keeps search/notes toggles either directly in its
  // items list or inside a grouped container. Walk both so we can drop the
  // theme button immediately to the LEFT of the search icon.
  _headerContainers(topHeader) {
    const groups = (topHeader?.items || []).filter(i => Array.isArray(i?.items))
    return [topHeader, ...groups]
  }

  _addThemeToggle() {
    const { UI } = this._instance
    try {
      const topHeader = UI.getModularHeader('default-top-header')
      if (!topHeader) return

      for (const container of this._headerContainers(topHeader)) {
        const items = [...container.items]
        // Idempotent: drop any prior copy before re-inserting.
        const dupe = items.findIndex(i => i?.dataElement === 'pdfThemeToggle')
        if (dupe !== -1) items.splice(dupe, 1)

        const searchIdx = items.findIndex(i => i?.dataElement && /search/i.test(i.dataElement))
        if (searchIdx !== -1) {
          items.splice(searchIdx, 0, this._buildThemeButton())
          container.setItems(items)
          return
        }
      }
      // Fallback: append to the top header if no search button was found.
      topHeader.setItems([...topHeader.items.filter(i => i?.dataElement !== 'pdfThemeToggle'), this._buildThemeButton()])
    } catch (e) {
      console.warn('[pdf-theme] Could not add toggle:', e)
    }
  }

  _toggleTheme() {
    this._pdfTheme = this._pdfTheme === 'dark' ? 'light' : 'dark'
    try { this._instance.UI.setTheme(this._pdfTheme) } catch (_) {}
    this._applyOverlayTheme()

    // Rebuild the button in place so its icon/title reflect the new theme.
    try {
      const { UI } = this._instance
      const topHeader = UI.getModularHeader('default-top-header')
      for (const container of this._headerContainers(topHeader)) {
        const items = [...container.items]
        const idx = items.findIndex(i => i?.dataElement === 'pdfThemeToggle')
        if (idx !== -1) {
          items[idx] = this._buildThemeButton()
          container.setItems(items)
          break
        }
      }
    } catch (_) {}

    // Persist per-user (independent of the app-wide theme).
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) {
      fetch('/save_pdf_viewer_theme', {
        method: 'POST',
        headers: { 'X-CSRF-Token': token, 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `theme=${this._pdfTheme}`
      })
    }
  }

  _themeSvg(isDark) {
    const color = '%23868e96'
    // Show the action's icon: a moon while light (click → dark), a sun while dark (click → light).
    const moon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`
    const sun = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.93" y1="4.93" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.07" y2="19.07"/><line x1="4.93" y1="19.07" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.07" y2="4.93"/></svg>`
    return `data:image/svg+xml,${isDark ? sun : moon}`
  }

  async _loadDocument(data, preloadedBlob = null, preloadedDoc = null) {
    const { Core, UI } = this._instance

    // If a previous Split/Compare left the viewer in dual-pane mode, force
    // single-viewer first — loading into a stale multi-viewer never fires
    // documentLoaded on Core.documentViewer, which used to hang the spinner
    // forever.
    try {
      if (Core.getDocumentViewers && Core.getDocumentViewers().length > 1) {
        UI.exitMultiViewerMode()
        await new Promise(r => setTimeout(r, 300))
      }
    } catch (_) { /* best-effort */ }

    // Fetch PDF as blob to avoid CORS issues with S3 presigned URLs.
    // A prefetched neighbor skips the download; a parse-ahead'd one skips
    // the parse too (the ready Document goes straight to the viewer).
    let pdfBlob = preloadedBlob
    if (!pdfBlob && !preloadedDoc) {
      const pdfResponse = await fetch(data.pdf_url)
      if (!pdfResponse.ok) throw new Error(`PDF fetch HTTP ${pdfResponse.status}`)
      pdfBlob = await pdfResponse.blob()
    }

    // Attach BOTH listeners BEFORE loadDocument: a fast (cached) load can fire
    // the events before late listeners attach, leaving the await hanging — the
    // "loading forever" bug. A timeout turns any missed event into a visible
    // error instead of an endless spinner.
    const loadedPromise = new Promise((resolve, reject) => {
      const cleanup = () => {
        Core.documentViewer.removeEventListener("documentLoaded", onLoaded)
        Core.documentViewer.removeEventListener("loaderror", onError)
        clearTimeout(timer)
      }
      const onLoaded = () => { cleanup(); resolve() }
      const onError = (err) => {
        cleanup()
        console.error("[action-item-pdf-viewer] Document load error:", err?.message || err)
        reject(err)
      }
      const timer = setTimeout(() => { cleanup(); reject(new Error("Document load timed out")) }, 45000)
      Core.documentViewer.addEventListener("documentLoaded", onLoaded)
      Core.documentViewer.addEventListener("loaderror", onError)
    })

    // Wait for native PDF annotations (form fields, links, Bluebeam markups)
    // to be fully parsed before capturing IDs. Without this, annotations from
    // Bluebeam-synced PDFs aren't in the list yet and get double-counted.
    // Tolerate a missed event (8s fallback) rather than hang.
    const annotationsPromise = new Promise(resolve => {
      Core.documentViewer.addEventListener("annotationsLoaded", resolve, { once: true })
      setTimeout(resolve, 8000)
    })

    UI.loadDocument(preloadedDoc || pdfBlob, { filename: data.name || "document.pdf", extension: "pdf" })
    await loadedPromise
    await annotationsPromise

    const annotManager = Core.documentViewer.getAnnotationManager()

    // Capture native PDF annotation IDs BEFORE importing saved XFDF so we
    // can exclude them from exports and avoid double-counting.
    this._nativeAnnotIds = new Set(annotManager.getAnnotationsList().map(a => a.Id))

    // Import existing annotations
    if (data.xfdf) {
      await annotManager.importAnnotations(this._stripBrokenAppearanceRefs(data.xfdf))
    }

    // Tombstones for native (in-file) markups the user deleted: the PDF
    // bytes still contain them — a Bluebeam round-trip embeds markups in
    // the file — so every reload used to resurrect them ("it's back
    // there again", Larry 2026-08-25). Keyed by a page/type/position
    // FINGERPRINT, not by Id: embedded annotations without an NM entry get
    // a fresh random Id on every load, so Id tombstones never matched
    // (Phelipe repro 2026-08-26). Persisted as a marker comment in the
    // saved XFDF; matching natives are re-deleted after each import.
    this._hiddenNativeFps = new Set()
    const tomb = /<!--conslog-hidden:(\[.*?\])-->/.exec(data.xfdf || "")
    if (tomb) {
      try { JSON.parse(tomb[1]).forEach(fp => this._hiddenNativeFps.add(String(fp))) } catch (_) {}
      if (this._hiddenNativeFps.size) {
        const doomed = annotManager.getAnnotationsList().filter(a => this._hiddenNativeFps.has(this._annotFp(a)))
        if (doomed.length) {
          try { annotManager.deleteAnnotations(doomed, { imported: true, force: true }) } catch (_) {}
        }
      }
    }

    // Typed form field values, saved as their own trailing comment (see
    // _saveAnnotations). Restored BEFORE _documentReady flips so the
    // fieldChanged autosave ignores our own writes.
    const savedFields = /<!--conslog-fields:([A-Za-z0-9+/=]*)-->/.exec(data.xfdf || "")
    if (savedFields) {
      try {
        this._restoreFieldValues(JSON.parse(this._b64decode(savedFields[1])))
      } catch (err) {
        console.warn("[pdf-viewer] saved form values unreadable", err)
      }
    }

    // Capture ALL annotation IDs after full load (native + XFDF) so the
    // annotationChanged listener can ignore Apryse's internal processing
    // of pre-loaded annotations and only react to real user edits.
    this._loadedAnnotIds = new Set(annotManager.getAnnotationsList().map(a => a.Id))

    this._documentReady = true
    this._dirty = false
    this.loadingTarget.classList.add("hidden")
    this._hideSoftLoader()

    // Phase 2: lay clickable reference highlights over the text. Fire-and-
    // forget — never blocks load, never throws into the load path.
    this._buildReferenceHotspots().catch(() => {})

    // Default layout: fit-to-page, notes panel open (left panel closed).
    // When the user has a saved zoom preference (sane range only) it wins
    // over fit-to-page, so every PDF opens at the zoom they last used.
    // Each programmatic call below fires a zoomUpdated event — the suppress
    // counter keeps those from being mistaken for user zooms even when they
    // arrive late on big documents.
    this._applyingZoom = true
    this._suppressZoomEvents = (this._suppressZoomEvents || 0) + 1
    UI.setFitMode(UI.FitMode.FitPage)
    if (this._lastSavedZoom && this._lastSavedZoom >= 0.25 && this._lastSavedZoom <= 4) {
      this._suppressZoomEvents++
      try { UI.setZoomLevel(this._lastSavedZoom) } catch (_) {}
    }
    setTimeout(() => {
      this._applyingZoom = false
      this._suppressZoomEvents = 0
    }, 1200)
    // The comments panel used to force itself open on every sheet — "anytime
    // I go from sheet to sheet, it opens back up again" (Larry 2026-08-25).
    // It now honors the last open/closed choice, remembered per browser.
    if (this._notesPanelPref() !== "closed") {
      this._notesPanelSuppress = Date.now()
      UI.openElements(["notesPanel"])
      try { UI.setNotesPanelWidth?.(280) } catch (_) { /* not available in all versions */ }
    }
    this._watchNotesPanel()

    // Hide noisy "AutoCAD SHX Text" comments from the panel by default
    this._applyDefaultCommentFilter()

    if (this._readOnly && !this._commentOnly) {
      annotManager.enableReadOnlyMode()
      // Hide all toolbar groups except View so historical PDFs are truly read-only
      UI.disableElements([
        "toolbarGroup-Annotate",
        "toolbarGroup-Shapes",
        "toolbarGroup-Insert",
        "toolbarGroup-Edit",
        "toolbarGroup-FillAndSign"
      ])
      UI.setToolbarGroup("toolbarGroup-View")
    } else if (this._commentOnly) {
      annotManager.disableReadOnlyMode()

      // Capture all existing annotation IDs (from PDF + XFDF) so we only export new ones
      this._existingAnnotIds = new Set(annotManager.getAnnotationsList().map(a => a.Id))

      // Disable markup/shape/edit tools, keep view + sticky note/comment
      const { UI } = this._instance
      UI.disableElements([
        "toolbarGroup-Annotate",
        "toolbarGroup-Shapes",
        "toolbarGroup-Insert",
        "toolbarGroup-Edit",
        "toolbarGroup-FillAndSign"
      ])

      // Set tool to pan mode, then enable sticky note
      const dv = Core.documentViewer
      dv.setToolMode(dv.getTool("Pan"))
    } else {
      annotManager.disableReadOnlyMode()
      // Re-enable toolbar groups in case they were disabled by a previous readOnly/commentOnly session
      const { UI } = this._instance
      UI.enableElements([
        "toolbarGroup-Annotate",
        "toolbarGroup-Shapes",
        "toolbarGroup-Insert",
        "toolbarGroup-Edit",
        "toolbarGroup-FillAndSign"
      ])
      UI.setToolbarGroup("toolbarGroup-Annotate")
    }

    // Opened from a list search result: land on the matched page and run the
    // term through Apryse's full-text search so every hit is listed +
    // highlighted (same panel as the manual in-viewer search).
    if (this._initialPage) {
      try { Core.documentViewer.setCurrentPage(this._initialPage, false) } catch (_) { /* page out of range */ }
      this._initialPage = null
    }
    if (this._searchTerm) {
      const term = this._searchTerm
      this._searchTerm = null
      try {
        // Programmatic close for search focus — not a user preference.
        this._notesPanelSuppress = Date.now()
        UI.closeElements(["notesPanel"])
        UI.searchTextFull(term)
      } catch (e) {
        console.warn("[pdf-viewer] auto search failed:", e)
      }
    }
  }

  // AutoCAD-exported PDFs embed SHX text labels as annotations authored by
  // "AutoCAD SHX Text", which flood the comments panel. Hide that author from
  // the notes/comments panel by default. This only filters the comments list —
  // the drawing itself is untouched. (This WebViewer build has no public API to
  // pre-set the Filters modal author checkboxes, so we filter the panel here.)
  _applyDefaultCommentFilter() {
    const HIDDEN_AUTHOR = "AutoCAD SHX Text"
    try {
      this._instance.UI.setCustomNoteFilter((annot) => {
        // Reference hotspots are clickable links, not comments — keep them
        // out of the notes/comments panel.
        if (annot.getCustomData && annot.getCustomData("refTarget")) return false
        return (annot.Author || "") !== HIDDEN_AUTHOR
      })
    } catch (e) {
      console.warn("[pdf-viewer] default comment filter failed:", e)
    }
  }

  // Stable identity for a native (in-file) annotation across loads: page +
  // subject/type + rounded rect. Ids can't be used — embedded annotations
  // without an NM entry are assigned a fresh random Id every load. "--" is
  // squashed because the fingerprint travels inside an XML comment.
  _annotFp(a) {
    const n = v => Math.round((v || 0) * 10) / 10
    return `${a.PageNumber}|${a.Subject || a.elementName || ""}|${n(a.X)},${n(a.Y)},${n(a.Width)},${n(a.Height)}`
      .replace(/--+/g, "-")
  }

  // ── Form field values (Apryse FieldManager) ─────────────────────────
  // Fields are a separate data path from annotations in Apryse: a value
  // lives on the field, not on a markup, so nothing in the annotation
  // export carries it. These read/write them directly.

  // { "Project Name": "SLCC", ... } for every leaf field holding a value.
  _collectFieldValues() {
    const out = {}
    try {
      const fieldManager = this._instance.Core.documentViewer.getAnnotationManager().getFieldManager()
      const walk = (field) => {
        if (!field) return

        const kids = field.children || []
        if (kids.length) return kids.forEach(walk) // only leaves hold values

        const name = field.name
        const value = field.getValue()
        if (name && value !== null && value !== undefined && String(value) !== "" && String(value) !== "Off") {
          out[name] = value
        }
      }
      // getFields(), NOT forEachField — the latter doesn't exist on this
      // Apryse build and threw straight into the catch below, so this
      // silently collected nothing (verified against the real viewer,
      // 2026-08-27).
      ;(fieldManager.getFields() || []).forEach(walk)
    } catch (err) {
      console.warn("[pdf-viewer] could not read form fields", err)
    }
    return out
  }

  _restoreFieldValues(values) {
    try {
      const fieldManager = this._instance.Core.documentViewer.getAnnotationManager().getFieldManager()
      Object.entries(values || {}).forEach(([name, value]) => {
        const field = fieldManager.getField(name)
        if (field) field.setValue(value)
      })
    } catch (err) {
      console.warn("[pdf-viewer] could not restore form fields", err)
    }
  }

  // UTF-8 safe (Portuguese accents survive the round-trip).
  _b64encode(str) {
    let binary = ""
    new TextEncoder().encode(str).forEach(byte => { binary += String.fromCharCode(byte) })
    return btoa(binary)
  }

  _b64decode(b64) {
    const binary = atob(b64)
    return new TextDecoder().decode(Uint8Array.from(binary, ch => ch.charCodeAt(0)))
  }

  _setupAnnotationListeners() {
    const { Core } = this._instance
    const annotManager = Core.documentViewer.getAnnotationManager()

    // Filling a form field is an edit too (Phelipe 2026-08-27). Field typing
    // fires fieldChanged on the FIELD manager, never annotationChanged, so
    // the autosave below never heard it — the value was lost even before the
    // export dropped it. Same debounce as a markup edit.
    try {
      const fieldManager = annotManager.getFieldManager()
      fieldManager.addEventListener("fieldChanged", () => {
        if (!this._documentReady) return // import/load sets values too

        this._dirty = true
        this._setSaveStatus("Saving...")
        clearTimeout(this._saveTimeout)
        this._saveTimeout = setTimeout(() => this._saveAnnotations(), 1500)
      })
    } catch (err) {
      console.warn("[pdf-viewer] form field autosave unavailable", err)
    }

    annotManager.addEventListener("annotationChanged", (annotations, action, info) => {
      if (action === "import" || !this._documentReady) return
      // Ignore Apryse's internal processing of pre-loaded annotations —
      // but ONLY for its own bookkeeping. A user deleting or editing a
      // SAVED markup also produces a batch of nothing but pre-loaded IDs,
      // and this guard used to swallow it: nothing was ever marked dirty,
      // no save fired, and the shape came back on refresh (Larry
      // 2026-08-21). imported/internal changes carry info.imported.
      const userEdit = action === "delete" || (action === "modify" && !info?.imported)
      if (!userEdit && this._loadedAnnotIds &&
          annotations.every(a => this._loadedAnnotIds.has(a.Id))) return

      // A deleted annotation is no longer "pre-loaded" — drop it so the
      // draft-comment rebuild and later batches don't count it.
      if (action === "delete" && this._loadedAnnotIds) {
        annotations.forEach(a => this._loadedAnnotIds.delete(a.Id))
      }
      // Deleting a NATIVE annotation leaves no tombstone in the export (it
      // was never in the saved XFDF) — remember its fingerprint so the save
      // persists the removal and reloads don't resurrect it.
      if (action === "delete" && this._nativeAnnotIds) {
        annotations.forEach(a => {
          if (this._nativeAnnotIds.has(a.Id)) (this._hiddenNativeFps ||= new Set()).add(this._annotFp(a))
        })
      }

      this._dirty = true
      this._setSaveStatus("Saving...")

      clearTimeout(this._saveTimeout)
      if (action === "delete") {
        // A delete saves IMMEDIATELY — the debounce window meant "delete,
        // refresh to check" lost the deletion every time (Phelipe repro
        // 2026-08-26: the save with the marks still in it was the load-time
        // autosave; his own delete-save never got to run).
        if (this._splitActive || this._overlayMode) {
          this._setSaveStatus("Not saved — close the compare view first")
        }
        this._saveAnnotations()
      } else {
        this._saveTimeout = setTimeout(() => this._saveAnnotations(), 1500)
      }

      this._updateDraftComment()
    })
  }

  // Rebuild the live draft comment from the pages the user has annotated this
  // session (annotations whose IDs weren't present after the initial load), and
  // write it into the host comment box on top of whatever the user already typed.
  // Only marks THIS person made count: SLCC Submittal 003 said "Annotated pages
  // 1 and 11" when the one saved markup was on page 1 (Chris McCarty, Phelipe
  // 2026-10-28) - a note, link or form field of the file itself that showed up
  // after the load snapshot was taken for a new mark.
  _updateDraftComment() {
    if (!this._commentEl || !this._instance || !this._loadedAnnotIds) return

    const { Annotations } = this._instance.Core
    const annotManager = this._instance.Core.documentViewer.getAnnotationManager()
    const me = annotManager.getCurrentUser()
    const fileOwned = [ Annotations.WidgetAnnotation, Annotations.Link ].filter(c => typeof c === "function")
    const pages = [...new Set(
      annotManager.getAnnotationsList()
        .filter(a => !this._loadedAnnotIds.has(a.Id) && !this._nativeAnnotIds?.has(a.Id) && a.PageNumber)
        .filter(a => !fileOwned.some(c => a instanceof c))
        .filter(a => !me || a.Author === me)
        .map(a => a.PageNumber)
    )].sort((a, b) => a - b)

    const draft = this._buildAnnotationDraft(pages)
    const base = this._draftBaseText || ""
    this._commentEl.value = draft ? (base ? `${base}\n${draft}` : draft) : base
    this._lastAutoDraft = draft
    if (this._onDraftUpdate) this._onDraftUpdate()
  }

  _buildAnnotationDraft(pages) {
    if (!pages.length) return ""
    const tmpl = pages.length === 1 ? this.annotateSingularValue : this.annotatePluralValue
    if (!tmpl) return ""
    const file = this.filenameTarget.textContent || "document.pdf"
    // Function replacements so `$` sequences in a filename aren't treated as
    // String.replace special patterns ($&, $$, …).
    return tmpl.replace("%{pages}", () => this._joinPages(pages)).replace("%{file}", () => file)
  }

  _joinPages(pages) {
    const strs = pages.map(String)
    try {
      const locale = document.documentElement.lang || "en"
      return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(strs)
    } catch (_) {
      if (strs.length <= 1) return strs.join("")
      if (strs.length === 2) return strs.join(", ")
      return `${strs.slice(0, -1).join(", ")}, ${strs[strs.length - 1]}`
    }
  }

  async _saveAnnotations() {
    if (!this._documentReady) return
    if (this._splitActive) return // split panes are view-only — never save from them
    if (this._overlayMode) return // overlay compare is a synthetic read-only doc
    if (!this._instance || !this._currentAttachmentId || !this._punchlistId) return
    if (this._saving) {
      // Never swallow a change that lands mid-save (a delete right after the
      // load-time autosave kicked off) — run once more when this one ends.
      this._resaveWanted = true
      return
    }

    this._saving = true
    this._setSaveStatus("Saving...")

    try {
      const { Core } = this._instance
      const annotManager = Core.documentViewer.getAnnotationManager()

      let xfdf
      // `fields: true` writes the form's VALUES (the <fields> block), which
      // is not the same thing as exporting widget annotations: the widgets
      // stay excluded below, so the count still can't grow. Without this,
      // anything typed into a fillable field was thrown away on save while
      // free-text markups survived — SLCC filled the parking form in the
      // platform and it came back blank (Tallaya/Lori, Phelipe 2026-08-27).
      if (this._commentOnly && this._existingAnnotIds) {
        // Only export user-added annotations (not Bluebeam embedded ones)
        const newAnnots = annotManager.getAnnotationsList().filter(a => !this._existingAnnotIds.has(a.Id))
        xfdf = await annotManager.exportAnnotations({ annotList: newAnnots, fields: true })
      } else {
        // Exclude native PDF annotations (widgets, links, form fields) — they
        // are part of the PDF itself and re-exporting them causes the count to
        // grow by the number of native annotations on every open/close cycle.
        const userAnnots = annotManager.getAnnotationsList().filter(a => !this._nativeAnnotIds?.has(a.Id))
        xfdf = await annotManager.exportAnnotations({ annotList: userAnnots, fields: true })
      }
      // Deleted-native tombstones ride along as a trailing XML comment —
      // legal after the XFDF root, ignored by the importer, parsed on load.
      if (this._hiddenNativeFps?.size) {
        xfdf += `<!--conslog-hidden:${JSON.stringify([...this._hiddenNativeFps])}-->`
      }
      // Form field VALUES ride along the same way (Phelipe 2026-08-27, SLCC
      // parking form). `fields: true` above is not enough on its own: the
      // export is scoped to `annotList`, and the field widgets are
      // deliberately NOT in that list (re-exporting them multiplies the
      // annotation count), so Apryse writes no <fields> block and every
      // typed value was lost. Carrying them ourselves is independent of
      // how the exporter scopes things. Base64 so a value containing
      // "-->" can't terminate the comment early.
      const fieldValues = this._collectFieldValues()
      if (Object.keys(fieldValues).length) {
        xfdf += `<!--conslog-fields:${this._b64encode(JSON.stringify(fieldValues))}-->`
      }

      const saveUrl = this._customSaveUrl || this.baseUrlValue
        .replace(/\/0\//, `/${this._punchlistId}/`)
        .replace("pdf_viewer_data", "save_pdf_annotations")

      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const body = JSON.stringify({
        attachment_id: this._currentAttachmentId,
        xfdf
      })
      const response = await fetch(saveUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        credentials: "same-origin",
        // keepalive lets a just-fired save (deletes save immediately) finish
        // even when the user refreshes right away — browsers cap keepalive
        // bodies around 64KB, so big markup sets skip it.
        keepalive: body.length < 60_000,
        body
      })

      if (response.ok) {
        this._dirty = false
        this._setSaveStatus("Saved")
      } else {
        this._setSaveStatus("Save failed")
      }
    } catch (error) {
      console.error("[action-item-pdf-viewer] Save error:", error)
      this._setSaveStatus("Save failed")
    } finally {
      this._saving = false
      if (this._resaveWanted) {
        this._resaveWanted = false
        this._saveAnnotations()
      }
    }
  }

  // "Open" button next to the filename. Clicking opens a picker (modal) to
  // choose any project document — a standard drawing, a spec section, or a
  // drawing sheet. Picking one opens it as another TAB; the ⇆ beside it
  // opens it side-by-side instead (read-only right pane, thumbnails hidden).
  // While split the button reads "Unsplit" and restores the single view.
  _renderSplitButton(splitListUrl) {
    if (this._relatedEl) { this._relatedEl.remove(); this._relatedEl = null }
    if (this._splitModal) { this._splitModal.remove(); this._splitModal = null }
    if (!splitListUrl) return

    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 cursor-pointer shrink-0 text-[13px] font-medium"
    // Opening another document adds a TAB now (Larry 2026-08-04) — splitting
    // is a deliberate second step, from the ⇆ on a tab or in this picker.
    btn.title = this._splitActive ? "Back to a single pane" : "Open another document in a tab"
    btn.textContent = this._splitActive ? "Unsplit" : "Open"
    btn.addEventListener("click", (e) => {
      e.stopPropagation()
      if (this._splitActive) this._unsplit()
      else this._openSplitPicker()
    })
    this.filenameTarget.insertAdjacentElement("afterend", btn)
    this._relatedEl = btn
  }

  // Fetch the detected cross-references for this document (shared by the
  // Phase 1 strip and the Phase 2 in-PDF hotspots). Always resolves to an
  // array (never rejects) so callers can use it freely.
  async _fetchReferences(url) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      return (await resp.json()).references || []
    } catch (e) {
      return []
    }
  }

  // ── Phase 2: clickable references inside the PDF ─────────────────
  // After the document loads, search its text for each reference's terms
  // ("Section 401", "SD-301", a sheet number) and lay a translucent,
  // read-only highlight over every hit. Clicking a highlight opens that
  // referenced document in its own tab. The highlights are tagged as
  // non-user annotations so they're never saved into the PDF. Entirely
  // best-effort: any Apryse API hiccup is swallowed so it can't break the
  // viewer.
  async _buildReferenceHotspots() {
    if (!this._instance || !this._documentReady) return
    let refs
    try { refs = await this._referencesPromise } catch (_) { return }
    if (!Array.isArray(refs) || !refs.length) return

    const { Core } = this._instance
    const Search = Core.Search
    const Annotations = Core.Annotations
    const dv = Core.documentViewer
    const annotManager = dv.getAnnotationManager()
    if (!Search || !Annotations || !dv.textSearchInit) return

    // Build a term → reference lookup (lowercased).
    const termMap = new Map()
    refs.forEach(ref => (ref.terms || []).forEach(t => {
      if (t) termMap.set(String(t).toLowerCase(), ref)
    }))
    if (!termMap.size) return

    // Don't let a click-API mismatch abort the highlight placement.
    try { this._installReferenceClickHandler() } catch (_) { /* clicks may not bind */ }

    // HIGHLIGHT is required for `result.quads` to be populated; PAGE_STOP
    // lets a full search report every hit through onResult.
    const searchMode = (Search.Mode.PAGE_STOP || 0) | (Search.Mode.HIGHLIGHT || 0)
    let hits = 0, placed = 0
    const placeFor = (term, ref) => new Promise((resolve) => {
      try {
        dv.textSearchInit(term, searchMode, {
          fullSearch: true,
          onResult: (result) => {
            try {
              const found = !Search.ResultCode || result.resultCode === Search.ResultCode.FOUND
              if (!found) return
              hits++
              if (result.quads?.length) {
                this._addReferenceHotspot(result.pageNum, result.quads, ref, term)
                placed++
              }
            } catch (err) { console.warn("[pdf-refs] hit error", err) }
          },
          onDocumentEnd: () => resolve(),
          onError: () => resolve()
        })
      } catch (_) { resolve() }
    })

    // Sequential to avoid overlapping search sessions in the SDK.
    for (const ref of refs) {
      for (const term of (ref.terms || [])) {
        await placeFor(term, ref)
      }
    }

    // Remove Apryse's own (yellow) search-result overlay — we only want
    // our clickable highlights.
    try { dv.clearSearchResults?.() } catch (_) { /* noop */ }
    console.log(`[pdf-refs] refs=${refs.length} searchHits=${hits} placed=${placed}`)
  }

  _addReferenceHotspot(pageNum, quads, ref, term) {
    const { Core } = this._instance
    const { Annotations } = Core
    const annotManager = Core.documentViewer.getAnnotationManager()

    // Convert the raw search-result quads into real Annotations.Quad
    // objects — assigning the search quads directly leaves the
    // annotation's bounding box null, so it never paints or hit-tests.
    const quadObjs = (quads || []).map((q) => {
      const p = q.getPoints ? q.getPoints() : q
      return new Annotations.Quad(p.x1, p.y1, p.x2, p.y2, p.x3, p.y3, p.x4, p.y4)
    })

    const hl = new Annotations.TextHighlightAnnotation()
    hl.PageNumber = pageNum
    hl.Quads = quadObjs
    hl.StrokeColor = new Annotations.Color(255, 105, 180) // highlighter pink
    hl.Opacity = 0.4
    hl.ReadOnly = true
    hl.Locked = true
    hl.NoResize = true
    hl.NoMove = true
    hl.setCustomData("refTarget", ref.data_url)
    hl.setCustomData("refLabel", ref.label || "reference")
    // The exact token this hotspot sits on (e.g. "SD-301") so the split view
    // can jump the related packet to that sheet, not just page 1.
    hl.setCustomData("refTerm", term || "")

    annotManager.addAnnotation(hl, { imported: true, autoFocus: false })
    annotManager.redrawAnnotation(hl)

    // Tag as non-user so it's excluded from saves + the change listener.
    this._refHotspotIds?.add(hl.Id)
    this._nativeAnnotIds?.add(hl.Id)
    this._loadedAnnotIds?.add(hl.Id)
    this._existingAnnotIds?.add(hl.Id)
  }

  _installReferenceClickHandler() {
    if (this._refClickInstalled) return
    const { Core } = this._instance
    const dv = Core.documentViewer
    const annotManager = dv.getAnnotationManager()

    dv.addEventListener("click", (e) => {
      if (this._splitActive) return
      try {
        const native = e?.target ? e : (e?.detail || e)
        const annot = annotManager.getAnnotationByMouseEvent(native)
        const target = annot?.getCustomData && annot.getCustomData("refTarget")
        if (!target) return
        // Clicking a reference inside the drawing opens that document in a
        // tab rather than splitting the page (Larry 2026-08-04).
        this._openDocInTab(target, annot.getCustomData("refLabel"))
      } catch (_) { /* not a reference click */ }
    })
    this._refClickInstalled = true
  }

  // Fetch the project's documents and show the chooser modal grouped into
  // Standard Drawings, Specifications, and Drawings (each group only when
  // it has entries). Picking one splits the viewer against it.
  async _openSplitPicker() {
    if (!this._splitListUrl) return
    this._buildSplitModal()
    const listEl = this._splitModal.querySelector("[data-split-list]")
    const searchEl = this._splitModal.querySelector("[data-split-search]")
    listEl.innerHTML = `<div class="px-3 py-6 text-center text-[13px] text-gray-500">Loading documents…</div>`
    this._splitModal.classList.remove("hidden")
    searchEl.value = ""
    setTimeout(() => searchEl.focus(), 50)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(this._splitListUrl, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const data = await resp.json()
      // Generic form ({groups: [{title, items}]}) from newer hosts (Design);
      // the legacy three-bucket shape from Specs/Drawings still works.
      this._splitDocs = data.groups || [
        { title: "Standard Drawings", items: data.standard_drawings || [] },
        { title: "Specifications", items: data.specifications || [] },
        { title: "Drawings", items: data.drawings || [] }
      ]
      // Groups after the first start rolled up (Larry 2026-08-20) — the
      // server puts the section you're standing in on top, and that's the
      // one you almost always want. Headers click open the rest.
      this._splitCollapsed = new Set(
        this._splitDocs.filter(g => (g.items || []).length).slice(1).map(g => g.title)
      )
      this._renderSplitList("")
    } catch (e) {
      listEl.innerHTML = `<div class="px-3 py-6 text-center text-[13px] text-rose-600">Couldn't load documents — try again.</div>`
    }
  }

  _renderSplitList(query) {
    const listEl = this._splitModal?.querySelector("[data-split-list]")
    if (!listEl) return
    const q = (query || "").trim().toLowerCase()
    const groups = (this._splitDocs || [])
      .map(g => ({ title: g.title, items: g.items.filter(it => !q || it.label.toLowerCase().includes(q)) }))
      .filter(g => g.items.length)

    if (!groups.length) {
      listEl.innerHTML = `<div class="px-3 py-6 text-center text-[13px] text-gray-500">No matching documents.</div>`
      return
    }

    // While a search is typed, every group shows its matches — the roll-up
    // only applies to the browse view.
    listEl.innerHTML = groups.map(g => {
      const collapsed = !q && this._splitCollapsed?.has(g.title)
      return `
      <button type="button" data-split-group="${this._esc(g.title)}"
              class="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 sticky top-0 cursor-pointer hover:bg-gray-100">
        <span class="inline-flex items-center gap-1.5"><svg class="w-3 h-3 transition-transform${collapsed ? " -rotate-90" : ""}" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg>${this._esc(g.title)}</span>
        <span class="font-normal normal-case tracking-normal text-gray-400">${g.items.length}</span>
      </button>
      ${collapsed ? "" : g.items.map(it => {
        const versKey = `${g.title}:${it.id}`
        const versOpen = !!this._splitVersOpen?.has(versKey)
        const versions = it.versions || []
        return `
        <div class="flex items-center gap-1 hover:bg-sky-50">
          ${it.preview_url ? `<span data-split-thumb data-preview-url="${this._esc(it.preview_url)}" data-label="${this._esc(it.label)}"
                  class="shrink-0 pl-2 py-2 text-gray-400 hover:text-sky-600 cursor-zoom-in"><svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Z"/></svg></span>` : ""}
          <button type="button" data-split-pick data-url="${this._esc(it.data_url)}" data-label="${this._esc(it.label)}"
                  class="flex-1 min-w-0 text-left px-3 py-2 text-[13px] text-gray-700 cursor-pointer truncate">${this._esc(it.label)}</button>
          ${versions.length ? `<button type="button" data-split-vers="${this._esc(versKey)}"
                  title="Show older versions of this sheet"
                  class="shrink-0 px-2 py-2 text-[11px] whitespace-nowrap ${versOpen ? "text-sky-700" : "text-gray-400"} hover:text-sky-700 cursor-pointer">${versions.length} older</button>` : ""}
          <button type="button" data-split-side data-url="${this._esc(it.data_url)}" data-label="${this._esc(it.label)}"
                  title="Open side by side instead"
                  class="shrink-0 px-2 py-2 text-[13px] text-gray-400 hover:text-sky-700 cursor-pointer">&#8646;</button>
        </div>
        ${versOpen ? versions.map(v => `
        <div class="flex items-center gap-1 hover:bg-sky-50 bg-gray-50/70">
          <span class="shrink-0 w-5"></span>
          ${v.preview_url ? `<span data-split-thumb data-preview-url="${this._esc(v.preview_url)}" data-label="${this._esc(it.label)} — ${this._esc(v.label)}"
                  class="shrink-0 pl-2 py-1.5 text-gray-400 hover:text-sky-600 cursor-zoom-in"><svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Z"/></svg></span>` : ""}
          <button type="button" data-split-pick data-url="${this._esc(v.data_url)}" data-label="${this._esc(it.label)} — ${this._esc(v.label)}"
                  class="flex-1 min-w-0 text-left px-3 py-1.5 text-[12px] text-gray-500 cursor-pointer truncate">${this._esc(v.label)}</button>
          <button type="button" data-split-side data-url="${this._esc(v.data_url)}" data-label="${this._esc(it.label)} — ${this._esc(v.label)}"
                  title="Open side by side instead"
                  class="shrink-0 px-2 py-1.5 text-[13px] text-gray-400 hover:text-sky-700 cursor-pointer">&#8646;</button>
        </div>`).join("") : ""}
      `}).join("")}
    `}).join("")

    // Group headers roll their section up / open (Larry 2026-08-20).
    listEl.querySelectorAll("[data-split-group]").forEach(el => {
      el.addEventListener("click", () => {
        const title = el.dataset.splitGroup
        if (!this._splitCollapsed) this._splitCollapsed = new Set()
        if (this._splitCollapsed.has(title)) this._splitCollapsed.delete(title)
        else this._splitCollapsed.add(title)
        hideSheetPreview()
        this._renderSplitList(this._splitModal?.querySelector("[data-split-search]")?.value || "")
      })
    })

    // "N older" expands that sheet's prior versions in place (Larry
    // 2026-08-25: the picker showed no version history).
    listEl.querySelectorAll("[data-split-vers]").forEach(el => {
      el.addEventListener("click", (e) => {
        e.stopPropagation()
        if (!this._splitVersOpen) this._splitVersOpen = new Set()
        const k = el.dataset.splitVers
        this._splitVersOpen.has(k) ? this._splitVersOpen.delete(k) : this._splitVersOpen.add(k)
        hideSheetPreview()
        this._renderSplitList(this._splitModal?.querySelector("[data-split-search]")?.value || "")
      })
    })

    // Hover thumbnail per document (Larry 2026-08-20) — same floating page-1
    // preview the sheet lists use.
    listEl.querySelectorAll("[data-split-thumb]").forEach(el => {
      el.addEventListener("mouseenter", () => showSheetPreview(el, el.dataset.previewUrl, el.dataset.label))
      el.addEventListener("mouseleave", () => hideSheetPreview())
    })

    // Default click opens the document in its own tab — specs included, which
    // is what used to render as a blank right-hand pane (Larry 2026-08-04).
    listEl.querySelectorAll("[data-split-pick]").forEach(el => {
      el.addEventListener("click", () => {
        this._closeSplitPicker()
        this._openDocInTab(el.dataset.url, el.dataset.label)
      })
    })

    // The ⇆ is the explicit "I want them side by side" action.
    listEl.querySelectorAll("[data-split-side]").forEach(el => {
      el.addEventListener("click", () => {
        this._related = { label: el.dataset.label, dataUrl: el.dataset.url }
        this._closeSplitPicker()
        this._toggleSplit()
      })
    })
  }

  // Open any project document as another sheet tab in this viewer. Read-only
  // — these are documents you navigated TO, not the one you're marking up.
  _openDocInTab(dataUrl, label) {
    if (!dataUrl) return
    this.open({
      dataUrl,
      filename: label || "document.pdf",
      readOnly: true,
      splitListUrl: this._splitListUrl
    })
  }

  _buildSplitModal() {
    if (this._splitModal) return
    const wrap = document.createElement("div")
    wrap.className = "hidden fixed inset-0 flex items-center justify-center p-4"
    // Apryse's WebViewer overlay uses a near-max z-index (~2147483005), so a
    // normal Tailwind z-class renders the picker BEHIND the PDF. Sit above it.
    wrap.style.zIndex = "2147483646"
    wrap.innerHTML = `
      <div data-split-backdrop class="absolute inset-0 bg-black/40"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-4xl flex flex-col" style="max-height:80vh">
        <div class="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200">
          <h2 class="text-[15px] font-semibold text-gray-900">Open a document…</h2>
          <button type="button" data-split-close class="text-gray-400 hover:text-gray-600 cursor-pointer text-xl leading-none">&times;</button>
        </div>
        <div class="px-3 py-2 border-b border-gray-100">
          <input type="text" data-split-search placeholder="Search documents…"
                 class="w-full px-3 py-1.5 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500">
        </div>
        <div data-split-list class="overflow-y-auto flex-1"></div>
      </div>`
    document.body.appendChild(wrap)
    wrap.querySelector("[data-split-backdrop]").addEventListener("click", () => this._closeSplitPicker())
    wrap.querySelector("[data-split-close]").addEventListener("click", () => this._closeSplitPicker())
    wrap.querySelector("[data-split-search]").addEventListener("input", (e) => this._renderSplitList(e.target.value))
    this._splitModal = wrap
  }

  _closeSplitPicker() {
    if (this._splitModal) this._splitModal.classList.add("hidden")
    hideSheetPreview()
  }

  _esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, c => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }

  // Search a document viewer's text for `term` and jump to (and highlight) the
  // first hit's page. Used by the split view to land on the exact referenced
  // sheet inside a multi-page packet. Best-effort — any SDK hiccup is swallowed.
  _jumpToTermInViewer(dv, Core, term) {
    const Search = Core.Search
    if (!Search || !term || !dv?.textSearchInit) return
    const mode = (Search.Mode.PAGE_STOP || 0) | (Search.Mode.HIGHLIGHT || 0)
    let jumped = false
    dv.textSearchInit(term, mode, {
      fullSearch: true,
      onResult: (result) => {
        if (jumped) return
        const found = !Search.ResultCode || result.resultCode === Search.ResultCode.FOUND
        if (found && result.pageNum) {
          jumped = true
          try { dv.setCurrentPage(result.pageNum, false) } catch (_) { /* out of range */ }
          try { dv.displaySearchResult(result) } catch (_) { /* highlight is bonus */ }
        }
      },
      onError: () => { /* leave on page 1 */ }
    })
  }

  // ── Split view (document left, related drawings right) ───────────
  // Apryse-native split: enter MultiViewer mode with the current document on
  // the left (annotations imported, read-only while split) and the related
  // drawings on the right. Same dual-pane machinery the Compare feature uses.
  async _toggleSplit() {
    if (this._splitActive) return this._unsplit()
    const related = this._related
    if (!related || !related.dataUrl || !this._instance) return

    const { UI, Core } = this._instance
    try {
      if (this._relatedEl) this._relatedEl.textContent = "Splitting…"
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(related.dataUrl, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const rightData = await resp.json()
      if (!rightData.pdf_url) throw new Error("no pdf_url")
      // Remember the right pane's doc so "close the left pane" re-opens this
      // sheet full-page. fileId lets us re-open it through the host's list row
      // (full params: editable, hotspots, repeatable split).
      this._splitRightData = {
        pdf_url: rightData.pdf_url,
        label: related.label,
        term: related.term,
        dataUrl: related.dataUrl,
        fileId: ((related.dataUrl || "").match(/\/(\d+)\/pdf_viewer_data/) || [])[1] || null
      }
      const leftUrl = this._currentDocData && this._currentDocData.pdf_url
      if (!leftUrl) throw new Error("no current document url")

      // Bytes first (same as Compare) — sidesteps S3 CORS/range quirks.
      const [leftBlob, rightBlob] = await Promise.all([
        fetch(leftUrl).then(r => { if (!r.ok) throw new Error(`left HTTP ${r.status}`); return r.blob() }),
        fetch(rightData.pdf_url).then(r => { if (!r.ok) throw new Error(`right HTTP ${r.status}`); return r.blob() })
      ])

      await new Promise((resolve, reject) => {
        let settled = false
        const onReady = async () => {
          UI.removeEventListener(UI.Events.MULTI_VIEWER_READY, onReady)
          try {
            const [dv1, dv2] = Core.getDocumentViewers()
            // Resolve when a pane's document is present. On single-threaded WASM
            // (this dev server serves no WASM threads) the `documentLoaded` event
            // is frequently missed by a freshly-attached listener, which used to
            // hang the whole split to the 30s timeout even though both PDFs had
            // actually loaded. Belt-and-suspenders: listen for the event AND poll
            // getDocument() so the pane resolves as soon as the doc is really in.
            const loaded = (dv) => new Promise((res, rej) => {
              let done = false
              const finish = () => { if (done) return; done = true; cleanup(); res() }
              const fail = (e) => { if (done) return; done = true; cleanup(); rej(new Error(e?.message || "load failed")) }
              const cleanup = () => {
                dv.removeEventListener("documentLoaded", finish)
                dv.removeEventListener("loaderror", fail)
                clearInterval(poll); clearTimeout(to)
              }
              dv.addEventListener("documentLoaded", finish, { once: true })
              dv.addEventListener("loaderror", fail, { once: true })
              const poll = setInterval(() => { try { if (dv.getDocument && dv.getDocument()) finish() } catch (_) { /* not ready */ } }, 400)
              const to = setTimeout(() => fail(new Error("pane load timed out")), 28000)
            })
            const p1 = loaded(dv1)
            const p2 = loaded(dv2)
            dv1.loadDocument(leftBlob, { filename: `${this.filenameTarget.textContent || "document"}.pdf`, extension: "pdf" })
            dv2.loadDocument(rightBlob, { filename: `${related.label || "drawings"}.pdf`, extension: "pdf" })
            await Promise.all([p1, p2])
            // Show the document's saved markups on the left pane.
            const xfdf = this._currentDocData && this._currentDocData.xfdf
            if (xfdf) { try { await dv1.getAnnotationManager().importAnnotations(this._stripBrokenAppearanceRefs(xfdf)) } catch (_) { /* best-effort */ } }
            // Jump the related (right) pane to the exact referenced sheet — e.g.
            // the "SD-301" page inside the SD-300 packet — by searching its text
            // for the clicked token. Best-effort: stays on page 1 if not found.
            if (related.term) { try { this._jumpToTermInViewer(dv2, Core, related.term) } catch (_) { /* noop */ } }
            settled = true
            resolve()
          } catch (e) { reject(e) }
        }
        if (Core.getDocumentViewers().length > 1) onReady()
        else {
          UI.addEventListener(UI.Events.MULTI_VIEWER_READY, onReady)
          UI.enterMultiViewerMode()
        }
        setTimeout(() => { if (!settled) reject(new Error("split timed out")) }, 30000)
      })

      // Hide the thumbnails panel while split; pause autosave (the split panes
      // are a viewing surface — editing resumes on Unsplit).
      try {
        this._splitHadLeftPanel = UI.isElementOpen("leftPanel")
        UI.closeElements(["leftPanel"])
      } catch (_) { /* older SDKs */ }
      this._splitActive = true
      this._setSaveStatus("Split view — editing paused")
      if (this._relatedEl) { this._relatedEl.textContent = "Unsplit"; this._relatedEl.title = "Close split (keep the original sheet)" }
      // Per-pane close is handled by hooking Apryse's own ✕ (see _onNativePaneClose).
      // Bind wheel-to-zoom to BOTH panes so scroll-zoom works in split view.
      this._syncScrollZoomTargets()
    } catch (e) {
      console.error("[action-item-pdf-viewer] split failed:", e)
      try { UI.exitMultiViewerMode() } catch (_) { /* not in multiviewer */ }
      if (this._relatedEl) this._relatedEl.textContent = "Open"
    }
  }

  // Leave split mode. With reload (default) the original document is loaded
  // back through the normal path — annotations, saving and panels all resume.
  async _unsplit(reload = true) {
    if (!this._splitActive) return
    this._splitActive = false
    this._removeSplitPaneCloses()
    this._splitRightData = null
    const { UI } = this._instance || {}
    try { UI.exitMultiViewerMode() } catch (_) { /* noop */ }
    this._setSaveStatus("")
    if (this._relatedEl) {
      this._relatedEl.textContent = "Open"
      this._relatedEl.title = "Open another document in a tab"
    }
    if (reload && this._currentDocData) {
      try {
        await this._loadDocument(this._currentDocData)
        if (this._splitHadLeftPanel) { try { UI.openElements(["leftPanel"]) } catch (_) { /* noop */ } }
      } catch (e) { console.error("[action-item-pdf-viewer] unsplit reload failed:", e) }
    }
    // Back to a single pane — rebind wheel-zoom to it.
    this._syncScrollZoomTargets()
  }

  // Per-pane close (Larry K request). Clicking a pane's ✕ brings the OTHER pane
  // to the full page:
  //   • close the RIGHT pane → back to the original sheet (toolbar "Unsplit").
  //   • close the LEFT pane  → refresh into the sheet you navigated to on the
  //     right, opening it full-page exactly as if it were clicked in the list
  //     (editable, hotspots, its own Split button) so the user can repeat the
  //     hop from one reference to the next.
  async _collapseSplit(keep) {
    if (!this._splitActive) return
    this._removeSplitPaneCloses()

    if (keep !== "right" || !this._splitRightData) {
      return this._unsplit(true) // keep the original left sheet
    }

    const right = this._splitRightData
    this._splitActive = false
    this._splitRightData = null

    // Show the spinner right away so the refresh never looks frozen.
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")

    // Dispose the viewer so open() re-boots a FRESH Apryse instance (its
    // reliable initial-load path) rather than the exit-then-reload path, which
    // silently fails when loading a different doc straight out of split on this
    // WASM-threadless dev server.
    try { this._instance && this._instance.UI.dispose() } catch (_) { /* no-op */ }
    this._instance = null
    // The reference-click hotspot handler was bound to the now-disposed document
    // viewer; reset the flag so it re-installs on the fresh instance — that's
    // what lets the user click another link in the re-opened sheet and split
    // again, over and over.
    this._refClickInstalled = false
    // dispose() can leave the old <apryse-webviewer> node behind; clear any
    // stragglers so the fresh re-init doesn't stack viewer instances.
    try { this.viewerContainerTarget.querySelectorAll("apryse-webviewer").forEach(n => n.remove()) } catch (_) { /* no-op */ }

    // Preferred: re-open the sheet exactly as clicking it in the list would —
    // the host controller wires up full params (editable, hotspots, its own
    // Split list), which is what makes the flow repeatable (hop reference to
    // reference). A programmatic click fires the Stimulus handler even though
    // the viewer overlay is on top.
    const row = right.fileId
      ? document.querySelector(`[data-file-id="${right.fileId}"][data-action*="openFile"]`)
      : null
    if (row) {
      row.click() // open() will manage the spinner from here
      return
    }

    // Fallback: re-open by URL through our own open() (still splittable via the
    // Split button).
    if (right.dataUrl) {
      try {
        await this.open({ dataUrl: right.dataUrl, filename: right.label, splitListUrl: this._splitListUrl, readOnly: true })
      } catch (e) {
        console.error("[action-item-pdf-viewer] keep-right reopen failed:", e)
        if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
      }
    } else if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }

  // Small ✕ overlays, one per pane, shown only while split. They live in the
  // viewer body (a `relative` box), floating over Apryse's dual-pane iframe.
  _renderSplitPaneCloses() {
    this._removeSplitPaneCloses()
    const parent = this.hasViewerContainerTarget ? this.viewerContainerTarget.parentElement : null
    if (!parent) return

    const make = (posClass, keep, title) => {
      const b = document.createElement("button")
      b.type = "button"
      // Layout via classes; COLORS inline — the GC "Builder" persona theme
      // remaps bg-white/gray tokens to dark, which turned these chips into
      // near-invisible dark circles. Inline styles are theme-proof.
      b.className = `absolute top-2 ${posClass} z-20 inline-flex items-center justify-center w-8 h-8 rounded-full cursor-pointer`
      b.style.cssText = "background:#ffffff;border:1px solid #cbd5e1;color:#334155;box-shadow:0 1px 4px rgba(0,0,0,.25);"
      b.title = title
      b.innerHTML = '<svg viewBox="0 0 20 20" fill="none" style="width:18px;height:18px"><path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
      b.addEventListener("mouseenter", () => { b.style.background = "#f1f5f9" })
      b.addEventListener("mouseleave", () => { b.style.background = "#ffffff" })
      b.addEventListener("click", (e) => { e.stopPropagation(); this._collapseSplit(keep) })
      parent.appendChild(b)
      return b
    }

    this._splitCloseEls = [
      make("left-2", "right", "Close the left pane — keep the right sheet full-screen"),
      make("right-2", "left", "Close the right pane — keep the left sheet full-screen")
    ]
  }

  _removeSplitPaneCloses() {
    if (this._splitCloseEls) {
      this._splitCloseEls.forEach((el) => el.remove())
      this._splitCloseEls = null
    }
  }

  // "V3", with the set's tag before it when the version came from something
  // ("PR-5", "ADD-1" — K7-21); hover the tag for the set's full name.
  _setVersionBadge(version, isCurrent = true, tag = null, tagTitle = "") {
    if (!this.hasVersionBadgeTarget) return
    if (version) {
      const colors = isCurrent ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
      this.versionBadgeTarget.innerHTML = `${issueTagChip(tag, tagTitle)}<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium ${colors}">V${this._esc(version)}</span>`
      this.versionBadgeTarget.className = "inline-flex items-center gap-1.5"
    } else {
      this.versionBadgeTarget.textContent = ""
      this.versionBadgeTarget.className = "hidden"
    }
  }

  _setSaveStatus(text) {
    if (!this.hasSaveStatusTarget) return
    this.saveStatusTarget.textContent = text
    if (text === "Saved") {
      this.saveStatusTarget.className = "text-[13px] text-emerald-600"
    } else if (text === "Save failed") {
      this.saveStatusTarget.className = "text-[13px] text-red-500"
    } else {
      this.saveStatusTarget.className = "text-[13px] text-gray-400"
    }
  }

  disconnect() {
    window.removeEventListener("conslog:open-last-preview", this._openLastPreviewHandler)
    if (this._boundViewerPopState) window.removeEventListener("popstate", this._boundViewerPopState)
    clearTimeout(this._saveTimeout)
    if (this._drawKeys) { window.removeEventListener("keydown", this._drawKeys, true); document.removeEventListener("keydown", this._drawKeys, true) }
    if (this._trackMouse) window.removeEventListener("mousemove", this._trackMouse, true)
    this._measureTip?.remove()
    if (this._floatNavSync) clearInterval(this._floatNavSync)
    this._floatNav?.remove()
    this._floatNav = null
    this._removeSplitPaneCloses()
    if (this._onNativePaneClose) document.removeEventListener("click", this._onNativePaneClose, true)
    if (this._instance) {
      this._instance.UI.dispose()
      this._instance = null
    }
  }
}
